import './style.css';
import eulaContentHtml from './legal/eula-content.html?raw';
import {
  BleTransport,
  type DeviceInfo,
  type SetActiveTuningTargetResult,
} from './ble/transport';
import {
  COMMAND_UNLOCK_PASSPHRASE,
  parseFactoryInfoLines,
  type SettingsStatus,
  type SweepPoint,
} from './ble/toolbox-protocol';
import {
  DiagnosticConfigError,
  DiagnosticUnsupportedHardwareError,
  runDiagnosticReport,
  type DiagnosticReport,
} from './flows/diagnostics';
import {
  FREQUENCY_MAX_HZ,
  FREQUENCY_MIN_HZ,
  RETUNE_DEFAULT_CENTER_HZ,
  runRetune,
  type RetuneResult,
  type RetuneSweepPhase,
} from './flows/retune';
import {
  RetuneConfigError,
  RetuneUnsupportedHardwareError,
  type OperationalTuningTargetConfig,
  resolveRetuneRevision,
  resolveRetuneTargets,
} from './flows/retune-target-selection';
import {
  buildDiagnosticDownloadJson,
  buildDiagnosticReportPayload,
  DEFAULT_DIAGNOSTIC_OBFUSCATION_KEY_ID,
} from './report/diagnostic-json';
import { loadManifest, evaluateManifest, type UpdateEvaluation } from './manifest/load';
import type { Manifest, ManifestUpdate } from './manifest/schema';
import {
  HardwareConfigValidationError,
  loadHardwareConfig,
} from './hardware-config/load';
import type { HardwareConfig } from './hardware-config/schema';
import {
  hasAcceptedCurrentEula,
  persistEulaAcceptance,
} from './legal/eula';
import { bindDom } from './ui/dom';
import { setupEulaGate } from './ui/eula-gate';
import { createRetuneChart } from './ui/retune-chart';
import {
  appendLog,
  clearLog,
  renderDeviceInfo,
  renderFirmwareList,
  renderProgress,
  renderRetuneTargetSelector,
  renderStatus,
} from './ui/render';
import { OtaSession } from './ota/session';
import { OtaAbortError } from './ota/types';
import type { OtaProgress, OtaStatus } from './ota/types';
import {
  evaluateCompatibility,
  getCompatibilityMessage,
  getCompatibilityProbe,
  requiresBluefyOnIos,
  type CompatibilityResult,
} from './platform/compatibility';
import { trackError, trackEvent } from './analytics';
import { createIdleDisconnectController } from './idle-disconnect';
import type { KnifeSnapshot, RetuneRuntimeSummary, UserDecision } from './firebase/types';
import { resolveRetuneSelectionCommand } from './retune-selection';
import {
  parseAutotuneRuntimeLine,
  type AutotuneRunResultEvent,
  type AutotuneRuntimePhase,
} from './autotune-runtime';
import {
  resolveRetuneExperienceMode,
  type RetuneExperienceMode,
} from './firmware-version';
import {
  appendSupportLog,
  buildSupportLogFileName,
  buildSupportLogJson,
  flushSupportLog,
} from './support-log';

const OTA_PROTOCOL_ID = 'bleota-8018';
const MANIFEST_URL = import.meta.env.DEV ? '/__dev/manifest.json' : './manifest.json';
const HARDWARE_CONFIG_URL = import.meta.env.DEV ? '/__dev/hardware_config.json' : './hardware_config.json';
const OTA_RECONNECT_STAGES = [
  { delayMs: 500, timeoutMs: 2000 },
  { delayMs: 1000, timeoutMs: 2000 },
  { delayMs: 2000, timeoutMs: 5000 },
  { delayMs: 5000, timeoutMs: 8000 },
] as const;
const OTA_RECONNECT_DISCONNECT_FALLBACK_DELAY_MS = 30000;
const OTA_RECONNECT_PROGRESS_MESSAGE = 'Knife rebooting. Waiting to reconnect...';
const USER_CONNECT_TIMEOUT_MS = 12000;
const IDLE_DISCONNECT_TIMEOUT_MS = 5 * 60_000;
const IDLE_DISCONNECT_TIMEOUT_DEV_TEST_MS = 15_000;
const IDLE_DISCONNECT_HOME_MESSAGE = 'Knife disconnected after a period of inactivity.';
const IDLE_DISCONNECT_DEBUG_LOG_INTERVAL_MS = 15_000;
const POST_CONNECT_COMMAND_DELAY_MS = 250;
const RETUNE_OLDER_FIRMWARE_MESSAGE =
  'This knife is running older firmware. A firmware update is strongly recommended for the best retuning performance.';
const RETUNE_RUNTIME_SAMPLE_INTERVAL_MS = 120;
const RETUNE_TARGET_AUTOTUNE_RESULT_TIMEOUT_MS = 120_000;
const RETUNE_FORCE_SINGLE_FREQ_DISCOVER_COMMAND = 'ForceSingleFreqDiscoverOnce';
const RETUNE_RUNTIME_RAIL_MAX_PROBES = 28;
const RETUNE_RUNTIME_RAIL_MIN_REFINING_WIDTH_PERCENT = 6;
const RETUNE_RUNTIME_RANGE_TOLERANCE_HZ = 25;
const DEV_CONTROLS_ENABLED = import.meta.env.DEV && isLocalhost();
const DEV_DIAGNOSTIC_CONSOLE_LOGS = import.meta.env.DEV && isLocalhost();
const DEV_DIAGNOSTIC_OBFUSCATION_FALLBACK_SECRET = 'dev-local-obfuscation';
const DIAGNOSTIC_OBFUSCATION_SECRET =
  import.meta.env.VITE_DIAGNOSTIC_OBFUSCATION_SECRET ??
  (import.meta.env.DEV ? DEV_DIAGNOSTIC_OBFUSCATION_FALLBACK_SECRET : '');
const DIAGNOSTIC_OBFUSCATION_KEY_ID =
  import.meta.env.VITE_DIAGNOSTIC_OBFUSCATION_KEY_ID?.trim() || DEFAULT_DIAGNOSTIC_OBFUSCATION_KEY_ID;
const EULA_DECLINE_URL = 'https://www.seattleultrasonics.com';
const SUPPORT_EMAIL = 'hello@seattleultrasonics.com';
const UPDATE_IN_PROGRESS_MESSAGE =
  'Update in progress. Keep the knife nearby and battery inserted.';
const DIAGNOSTICS_COMPLETE_MESSAGE_HTML = `Diagnostics complete! Download the report and attach it to an email: <a class="support-link" href="mailto:${SUPPORT_EMAIL}">${SUPPORT_EMAIL}</a>`;

type FlowKind = 'firmware' | 'retune' | 'diagnostics';
type ActiveOperation = 'none' | FlowKind;
type FirmwareUiStage = 'selection' | 'updating' | 'complete';
type DevMode = 'live' | 'success' | 'fail';
type UpdateErrorStage = 'download' | 'checksum' | 'ota' | 'reconnect';
type PostConnectSetupContext = 'normal' | 'ota-auto' | 'ota-manual';
type RetuneStage = 'idle' | 'armed' | 'running' | 'paused' | 'compare' | 'saving' | 'complete';
type DiagnosticStage = 'idle' | 'armed' | 'running' | 'paused' | 'complete';
type RetuneSelection = 'original' | 'updated';
type RetuneActiveState = {
  targetId: string | null;
  centerHz: number;
};
type RetuneComparisonState = {
  originalTargetId: string | null;
  originalHz: number;
  updatedHz: number;
  selected: RetuneSelection;
  persisted: boolean;
  revisionId: string;
  targetId: string;
  previousActiveState: RetuneActiveState;
};
type RetuneUploadDraft = {
  result: RetuneResult;
  knifeSnapshot: KnifeSnapshot;
};

const dom = bindDom();
const transport = new BleTransport();
const retuneChart = createRetuneChart(dom.retuneChartCanvas);

let manifest: Manifest | null = null;
let hardwareConfig: HardwareConfig | null = null;
let evaluations: UpdateEvaluation[] = [];
let selectedTag: string | null = null;
let manifestEvaluationFingerprint: string | null = null;
let firmwareOptionsRenderFingerprint: string | null = null;
let selectedFlow: FlowKind | null = 'firmware';
let flowRetuneDefaultApplied = false;
let activeOperation: ActiveOperation = 'none';
let abortController: AbortController | null = null;

let otaReconnectPending = false;
let otaReconnectTimer: number | null = null;
let otaReconnectFallbackTimer: number | null = null;
let otaReconnectFallbackCountdownTimer: number | null = null;
let reconnectPromptActive = false;
let isAutoReconnectAttempt = false;

let updateOutcome: 'success' | 'failed' | null = null;
let pendingUpdateOutcome: { outcome: 'success' | 'failed'; message: string } | null = null;
let updateStartedAtMs: number | null = null;
let updateResultTracked = false;
let updateFailureStage: UpdateErrorStage | null = null;
let updateFirmwareVersion: string | null = null;
let updateBeforeFirmwareVersion: string | null = null;
let updateUsedAutoReconnect = false;
let updateHadReconnectIssue = false;
let otaReconnectReadyAtMs: number | null = null;
let otaReconnectAttemptStartedAtMs: number | null = null;
let otaReconnectFallbackScheduledAtMs: number | null = null;

let diagnosticReport: DiagnosticReport | null = null;
let diagnosticUploadSnapshot: KnifeSnapshot | null = null;
let retuneStage: RetuneStage = 'idle';
let retuneComparisonState: RetuneComparisonState | null = null;
let retuneUploadDraft: RetuneUploadDraft | null = null;
let retuneNoChangeResult = false;
let retuneAvailableTargets: OperationalTuningTargetConfig[] = [];
let retuneSelectedTargetId: string | null = null;
let retuneDefaultTargetId: string | null = null;
let retuneLockedTarget: OperationalTuningTargetConfig | null = null;
let retuneKnownActiveState: RetuneActiveState | null = null;
let retuneButtonPressed: boolean | null = null;
let retuneButtonTelemetrySeen = false;
let retuneAutoStartInFlight = false;
let retuneRequireButtonReleaseForAutoStart = false;
let retuneTargetApplyInFlight = false;
let retunePendingTargetApplyId: string | null = null;
let retunePreparedTargetId: string | null = null;
let retunePreparedAttemptBeforeState: RetuneActiveState | null = null;
let retuneRuntimePhase: AutotuneRuntimePhase | null = null;
let retuneRuntimeRunActive = false;
let retuneRuntimeCompleted = false;
let retuneRuntimeLastSampleAtMs = 0;
let retuneRuntimeCenterHz: number | null = null;
let retuneRuntimeResultResolver: ((result: AutotuneRunResultEvent) => void) | null = null;
let retuneRuntimeResultRejecter: ((error: Error) => void) | null = null;
let retuneRuntimePendingResult: AutotuneRunResultEvent | null = null;
let retuneRuntimeRailRange: { minHz: number; maxHz: number } | null = null;
let diagnosticsStage: DiagnosticStage = 'idle';
let supportLoggedRetuneStage: RetuneStage | null = null;
let supportLoggedDiagnosticsStage: DiagnosticStage | null = null;
let diagnosticsButtonPressed: boolean | null = null;
let diagnosticsButtonTelemetrySeen = false;
let diagnosticsAutoStartInFlight = false;
let diagnosticsRequireButtonReleaseForAutoStart = false;
let diagnosticsActiveTargetProgress: string | null = null;
let diagnosticsActiveTargetPercent: number | null = null;
let devFirmwareMode: DevMode = 'live';
let devRetuneMode: DevMode = 'live';
let devDiagnosticsMode: DevMode = 'live';
let devDiagnosticsObfuscationEnabled = false;
let taskWizardStep = 1;
let taskWizardTotal = 3;
let taskWizardTitle = 'Choose task';
let updaterStarted = false;
let pendingIdleDisconnect = false;
let idleDisconnectDebugLogIntervalId: number | null = null;
let supportLastKnownKnife: DeviceInfo | null = null;

const wizardSteps = [...dom.wizardSteps].sort(
  (left, right) => Number(left.dataset.step ?? 0) - Number(right.dataset.step ?? 0)
);
const totalSteps = wizardSteps.length;
let currentStepIndex = Math.max(
  0,
  wizardSteps.findIndex((step) => step.classList.contains('is-active'))
);

const idleDisconnectController = createIdleDisconnectController({
  timeoutMs: IDLE_DISCONNECT_TIMEOUT_MS,
  onTimeout: triggerIdleDisconnect,
});

appendSupportLog('app', 'session_started', {
  browserFamily: detectBrowserFamily(),
  platformFamily: detectPlatformFamily(),
});

window.addEventListener('pagehide', flushSupportLog);
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') {
    flushSupportLog();
  }
});

transport.onDiagnosticEvent((event) => {
  appendSupportLog('ble', event.event, event.details, event.level);
});

transport.onDisconnect(() => {
  const disconnectedDueToIdle = pendingIdleDisconnect;
  pendingIdleDisconnect = false;

  const shouldReconnect = otaReconnectPending && transport.canReconnect();
  if (otaReconnectPending || pendingUpdateOutcome !== null || reconnectPromptActive) {
    otaReconnectInfo('Disconnect event observed.', {
      disconnectedDueToIdle,
      shouldReconnect,
      pendingUpdateOutcome: pendingUpdateOutcome?.outcome ?? null,
    });
  }
  renderDeviceInfo(dom, null);
  dom.connectButton.textContent = 'Connect';

  if (shouldReconnect) {
    appendLog(dom, { level: 'info', message: 'Knife disconnected. Waiting to reconnect...' });
    renderStatus(dom, {
      level: 'info',
      message: OTA_RECONNECT_PROGRESS_MESSAGE,
    });
    dom.progressSpeed.textContent = '0 kB/s';
    dom.progressDetail.textContent = OTA_RECONNECT_PROGRESS_MESSAGE;
  } else {
    appendLog(dom, {
      level: disconnectedDueToIdle ? 'info' : 'error',
      message: disconnectedDueToIdle ? IDLE_DISCONNECT_HOME_MESSAGE : 'Knife disconnected.',
    });
    if (selectedFlow === 'firmware') {
      resetFirmwareProgress('Waiting for update start');
    } else if (selectedFlow === 'retune' && hasUnsavedRetuneSelection()) {
      dom.retuneStatusText.textContent =
        'Knife disconnected during frequency comparison. Unsaved selection could not be reverted.';
      appendLog(dom, {
        level: 'warning',
        message: 'Retune compare session disconnected before save. Unsaved selection could not be reverted.',
      });
    }
  }

  if (activeOperation !== 'none' && abortController && !otaReconnectPending) {
    abortController.abort();
  }

  retuneButtonPressed = null;
  retuneButtonTelemetrySeen = false;
  retuneAutoStartInFlight = false;
  retuneRequireButtonReleaseForAutoStart = false;
  flowRetuneDefaultApplied = false;
  dom.retuneHoldBanner.hidden = true;
  diagnosticsButtonPressed = null;
  diagnosticsButtonTelemetrySeen = false;
  diagnosticsAutoStartInFlight = false;
  diagnosticsRequireButtonReleaseForAutoStart = false;
  diagnosticsActiveTargetProgress = null;
  diagnosticsActiveTargetPercent = null;
  if (diagnosticsStage !== 'complete') {
    diagnosticsStage = 'idle';
  }
  dom.diagnosticsHoldBanner.hidden = true;
  dom.diagnosticsHoldBanner.textContent = '';

  updateManifestEvaluation();
  updateUiState();

  if (disconnectedDueToIdle) {
    setStep(0);
    showIdleDisconnectHomeMessage();
  }

  if (shouldReconnect && !otaReconnectTimer) {
    otaReconnectInfo('Starting auto-reconnect from disconnect event.', {
      stageIndex: 0,
    });
    clearOtaReconnectFallbackTimer();
    otaReconnectPending = false;
    scheduleOtaReconnect();
  }
});

transport.onDeviceInfoChanged((info) => {
  if (info) {
    supportLastKnownKnife = { ...info };
  }
  renderDeviceInfo(dom, info);
  updateManifestEvaluation();
  refreshFlowSectionContent();
  updateUiState();
});

transport.onSettingsStatus((settings: SettingsStatus) => {
  handleRetuneSettingsStatusUpdate(settings);
  handleDiagnosticsSettingsStatusUpdate(settings);
});

transport.onTextLine((line) => {
  handleRetuneRuntimeTextLine(line);
});

function isLocalhost(): boolean {
  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1';
}

function detectBrowserFamily(): string {
  const ua = navigator.userAgent;
  if (ua.includes('Edg/')) {
    return 'edge';
  }
  if (ua.includes('OPR/')) {
    return 'opera';
  }
  if (ua.includes('Chrome/')) {
    return 'chrome';
  }
  if (ua.includes('Firefox/')) {
    return 'firefox';
  }
  if (ua.includes('Safari/')) {
    return 'safari';
  }
  return 'other';
}

function detectPlatformFamily(): string {
  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes('android')) {
    return 'android';
  }
  if (ua.includes('iphone') || ua.includes('ipad') || ua.includes('ipod')) {
    return 'ios';
  }
  if (ua.includes('mac os x')) {
    return 'macos';
  }
  if (ua.includes('windows')) {
    return 'windows';
  }
  if (ua.includes('linux')) {
    return 'linux';
  }
  return 'other';
}

function elapsedSince(startedAtMs: number | null): number | null {
  if (startedAtMs === null) {
    return null;
  }
  return Math.max(0, Date.now() - startedAtMs);
}

function resetOtaReconnectDebugState(): void {
  otaReconnectReadyAtMs = null;
  otaReconnectAttemptStartedAtMs = null;
  otaReconnectFallbackScheduledAtMs = null;
}

function buildOtaReconnectLogDetails(
  details: Record<string, unknown> = {}
): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    ...details,
    activeOperation,
    selectedFlow,
    otaReconnectPending,
    autoReconnectTimerActive: otaReconnectTimer !== null,
    fallbackTimerActive: otaReconnectFallbackTimer !== null,
    isAutoReconnectAttempt,
    reconnectPromptActive,
    transportConnected: transport.isConnected(),
    canReconnect: transport.canReconnect(),
  };

  const sinceOtaComplete = elapsedSince(otaReconnectReadyAtMs);
  if (sinceOtaComplete !== null) {
    payload.elapsedSinceOtaCompleteMs = sinceOtaComplete;
  }

  const sinceAttemptStarted = elapsedSince(otaReconnectAttemptStartedAtMs);
  if (sinceAttemptStarted !== null) {
    payload.elapsedSinceAttemptStartedMs = sinceAttemptStarted;
  }

  const sinceFallbackScheduled = elapsedSince(otaReconnectFallbackScheduledAtMs);
  if (sinceFallbackScheduled !== null) {
    payload.elapsedSinceFallbackScheduledMs = sinceFallbackScheduled;
  }

  return payload;
}

function otaReconnectInfo(message: string, details?: Record<string, unknown>): void {
  const logDetails = buildOtaReconnectLogDetails(details);
  appendSupportLog('ota-reconnect', message, logDetails);
  console.info(`[OTAReconnect] ${message}`, logDetails);
}

function otaReconnectError(
  message: string,
  error: unknown,
  details?: Record<string, unknown>
): void {
  const logDetails = {
    ...buildOtaReconnectLogDetails(details),
    error,
  };
  appendSupportLog('ota-reconnect', message, logDetails, 'error');
  console.error(`[OTAReconnect] ${message}`, logDetails);
}

function inferConnectErrorCode(error: unknown): string {
  if (error instanceof DOMException && error.name === 'NotFoundError') {
    return 'request_canceled';
  }
  const message = error instanceof Error ? error.message.toLowerCase() : '';
  if (message.includes('timed out')) {
    return 'timeout';
  }
  if (message.includes('not supported')) {
    return 'unsupported';
  }
  if (message.includes('gatt')) {
    return 'gatt_unavailable';
  }
  if (message.includes('failed to connect') || message.includes('connection failed')) {
    return 'connect_failed';
  }
  return 'unknown';
}

function inferUpdateErrorStage(error: unknown): UpdateErrorStage {
  const message = error instanceof Error ? error.message.toLowerCase() : '';
  if (message.includes('sha-256') || message.includes('checksum')) {
    return 'checksum';
  }
  if (message.includes('download failed') || message.includes('failed to fetch')) {
    return 'download';
  }
  if (message.includes('reconnect')) {
    return 'reconnect';
  }
  return 'ota';
}

async function init(): Promise<void> {
  configureDevControls();

  const compatibility = evaluateCompatibility(getCompatibilityProbe());
  if (!compatibility.supported) {
    showCompatibilityNotice(compatibility);
    return;
  }
  startIdleDisconnectDebugLogging();

  syncFlowInputs();
  setStep(currentStepIndex);
  setRetuneStageUi('idle');
  setDiagnosticsStageUi('idle');
  resetFirmwareProgress('Waiting for update start');
  setRetuneProgress(0, 'Not started');
  setDiagnosticsProgress(0, 'Not started');

  await Promise.all([loadManifestAndRender(), loadHardwareConfigForRetune()]);
  bindEvents();
  updateUiState();
}

function startIdleDisconnectDebugLogging(): void {
  if (!DEV_DIAGNOSTIC_CONSOLE_LOGS || idleDisconnectDebugLogIntervalId !== null) {
    return;
  }

  const emitStatus = (): void => {
    const state = idleDisconnectController.getDebugState();
    const now = Date.now();
    console.debug('[IdleDisconnect] status', {
      connected: state.connected,
      paused: state.paused,
      active: state.active,
      timerRunning: state.timerRunning,
      timeoutMs: state.timeoutMs,
      msRemaining: state.msRemaining,
      lastActivityMsAgo:
        state.lastActivityAtMs === null ? null : Math.max(0, now - state.lastActivityAtMs),
      currentStepIndex,
      selectedFlow,
      activeOperation,
      otaReconnectPending,
      isAutoReconnectAttempt,
      pendingIdleDisconnect,
      homeMessageVisible: !dom.idleDisconnectHomeMessage.hidden,
    });
  };

  emitStatus();
  idleDisconnectDebugLogIntervalId = window.setInterval(
    emitStatus,
    IDLE_DISCONNECT_DEBUG_LOG_INTERVAL_MS
  );
}

function stopIdleDisconnectDebugLogging(): void {
  if (idleDisconnectDebugLogIntervalId === null) {
    return;
  }
  window.clearInterval(idleDisconnectDebugLogIntervalId);
  idleDisconnectDebugLogIntervalId = null;
}

function configureDevControls(): void {
  if (!DEV_CONTROLS_ENABLED) {
    dom.devControls.remove();
    return;
  }

  dom.devControls.hidden = false;
  dom.devModeLive.addEventListener('change', syncDevModesFromInputs);
  dom.devModeSuccess.addEventListener('change', syncDevModesFromInputs);
  dom.devModeFail.addEventListener('change', syncDevModesFromInputs);
  dom.devRetuneModeLive.addEventListener('change', syncDevModesFromInputs);
  dom.devRetuneModeSuccess.addEventListener('change', syncDevModesFromInputs);
  dom.devRetuneModeFail.addEventListener('change', syncDevModesFromInputs);
  dom.devDiagnosticsModeLive.addEventListener('change', syncDevModesFromInputs);
  dom.devDiagnosticsModeSuccess.addEventListener('change', syncDevModesFromInputs);
  dom.devDiagnosticsModeFail.addEventListener('change', syncDevModesFromInputs);
  dom.devDiagnosticsObfuscationOn.addEventListener('change', syncDevModesFromInputs);
  dom.devDiagnosticsObfuscationOff.addEventListener('change', syncDevModesFromInputs);
  dom.devIdleTimeout5m.addEventListener('change', syncDevModesFromInputs);
  dom.devIdleTimeout15s.addEventListener('change', syncDevModesFromInputs);
  syncDevModesFromInputs();
}

function bindEvents(): void {
  dom.connectButton.addEventListener('click', async () => {
    if (transport.isConnected()) {
      cancelOtaReconnect();
      transport.disconnect();
      return;
    }
    await connectDevice();
  });

  dom.reconnectButton.addEventListener('click', async () => {
    if (transport.isConnected()) {
      return;
    }
    await connectDevice({ fromReconnectPrompt: true });
  });

  dom.flowFirmware.addEventListener('change', () => {
    void selectFlowFromInputs();
  });
  dom.flowRetune.addEventListener('change', () => {
    void selectFlowFromInputs();
  });
  dom.flowDiagnostics.addEventListener('change', () => {
    void selectFlowFromInputs();
  });

  dom.firmwareList.addEventListener('change', (event) => {
    const target = event.target as HTMLInputElement | null;
    if (!target || target.type !== 'radio') {
      return;
    }
    selectedTag = target.value;
    refreshFirmwareFlowSummary();
    updateUiState();
  });

  dom.startUpdateButton.addEventListener('click', async () => {
    await startUpdate();
  });

  dom.cancelButton.addEventListener('click', () => {
    if (abortController) {
      abortController.abort();
    }
  });

  dom.retuneTargetOptions.addEventListener('change', (event) => {
    const target = event.target as HTMLInputElement | null;
    if (!target || target.type !== 'radio') {
      return;
    }
    handleRetuneTargetSelectionChange(target.value);
  });

  dom.retuneTargetOptions.addEventListener('click', (event) => {
    const target = event.target as HTMLElement | null;
    if (!target || (target instanceof HTMLInputElement && target.type === 'radio')) {
      return;
    }

    const option = target.closest<HTMLLabelElement>('.retune-target-option');
    if (!option || !dom.retuneTargetOptions.contains(option)) {
      return;
    }

    const radio = option.querySelector<HTMLInputElement>('input[type="radio"][name="retuneTarget"]');
    if (!radio || radio.disabled) {
      return;
    }

    event.preventDefault();
    if (radio.checked) {
      return;
    }

    radio.checked = true;
    handleRetuneTargetSelectionChange(radio.value);
  });

  dom.retuneChoiceOriginal.addEventListener('change', () => {
    if (dom.retuneChoiceOriginal.checked) {
      void handleRetuneSelectionChange('original');
    }
  });

  dom.retuneChoiceUpdated.addEventListener('change', () => {
    if (dom.retuneChoiceUpdated.checked) {
      void handleRetuneSelectionChange('updated');
    }
  });

  dom.saveRetuneChoiceButton.addEventListener('click', async () => {
    await saveRetuneSelection();
  });

  dom.startDiagnosticsButton.addEventListener('click', async () => {
    await startDiagnostics();
  });

  dom.cancelDiagnosticsButton.addEventListener('click', () => {
    if (abortController) {
      abortController.abort();
    }
  });

  dom.diagnosticsHomeButton.addEventListener('click', () => {
    if (activeOperation !== 'none' || isAutoReconnectAttempt) {
      return;
    }
    resetDiagnosticsUiState();
    setStep(2);
  });

  dom.downloadDiagnosticsButton.addEventListener('click', () => {
    downloadDiagnosticReport();
  });

  dom.downloadSupportLogButton.addEventListener('click', (event) => {
    event.preventDefault();
    downloadSupportLog();
  });

  dom.clearLogButton.addEventListener('click', () => {
    clearLog(dom);
  });

  document.addEventListener('click', (event) => {
    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }

    const nextButton = target.closest<HTMLButtonElement>('[data-wizard-next]');
    if (nextButton) {
      event.preventDefault();
      goNext();
      return;
    }

    const backButton = target.closest<HTMLButtonElement>('[data-wizard-back]');
    if (backButton) {
      event.preventDefault();
      void goBack();
    }
  });
}

async function connectDevice(options: { fromReconnectPrompt?: boolean } = {}): Promise<void> {
  const postConnectContext: PostConnectSetupContext =
    pendingUpdateOutcome !== null && options.fromReconnectPrompt ? 'ota-manual' : 'normal';
  const connectStartedAtMs = Date.now();
  if (postConnectContext === 'ota-manual') {
    otaReconnectInfo('Manual reconnect requested from reconnect prompt.');
  }

  cancelOtaReconnect();
  transport.setKeepDeviceOnDisconnect(false);
  dom.connectButton.disabled = true;
  dom.reconnectButton.disabled = true;

  try {
    const info = await transport.connect({
      namePrefix: extractNamePrefix(manifest),
      timeoutMs: USER_CONNECT_TIMEOUT_MS,
      maxRetries: 3,
    });

    clearIdleDisconnectHomeMessage({ reason: 'reconnect-success' });
    dom.connectButton.textContent = 'Disconnect';
    appendLog(dom, { level: 'success', message: `Connected to ${info.name}.` });
    appendSupportLog('connection', 'connect_completed', {
      durationMs: elapsedSince(connectStartedAtMs),
      deviceName: info.name,
      deviceId: info.id,
      firmwareRevision: info.firmwareRevision ?? null,
      hardwareRevision: info.hardwareRevision ?? null,
      capabilities: transport.getCapabilities(),
    });
    trackEvent('connect_result', {
      result: 'success',
      from_reconnect_prompt: Boolean(options.fromReconnectPrompt),
    });

    if (postConnectContext === 'ota-manual') {
      otaReconnectInfo('Manual reconnect transport.connect() succeeded.', {
        connectDurationMs: elapsedSince(connectStartedAtMs),
        deviceName: info.name,
      });
    }

    if (postConnectContext === 'normal' && currentStepIndex === 1) {
      setStep(2);
    }

    await runPostConnectCommandSetup(postConnectContext);

    const reconnectCompletedAfterUpdate = pendingUpdateOutcome !== null && !otaReconnectPending;
    if (reconnectCompletedAfterUpdate) {
      hideReconnectPrompt();
      dom.progressSpeed.textContent = '0 kB/s';
      dom.progressDetail.textContent = 'Reconnected after update.';
      otaReconnectInfo('Manual reconnect completed; finalizing update outcome.');
      finalizeUpdateOutcome();
    } else if (options.fromReconnectPrompt) {
      hideReconnectPrompt();
    }
    if (postConnectContext !== 'normal' && currentStepIndex === 1) {
      setStep(2);
    }
  } catch (error) {
    const message = getErrorMessage(error);
    appendSupportLog(
      'connection',
      'connect_failed',
      {
        durationMs: elapsedSince(connectStartedAtMs),
        fromReconnectPrompt: Boolean(options.fromReconnectPrompt),
        error,
      },
      'error'
    );
    renderStatus(dom, { level: 'error', message });
    appendLog(dom, { level: 'error', message });
    if (postConnectContext === 'ota-manual') {
      otaReconnectError('Manual reconnect failed.', error, {
        connectDurationMs: elapsedSince(connectStartedAtMs),
      });
    }

    const errorCode = inferConnectErrorCode(error);
    trackEvent('connect_result', {
      result: errorCode === 'timeout' ? 'timeout' : 'failed',
      from_reconnect_prompt: Boolean(options.fromReconnectPrompt),
      error_code: errorCode,
    });
    trackError('connect_error', error, {
      from_reconnect_prompt: Boolean(options.fromReconnectPrompt),
      error_code: errorCode,
    });
  } finally {
    dom.connectButton.disabled = false;
    dom.reconnectButton.disabled = false;
    updateManifestEvaluation();
    updateUiState();
  }
}

async function loadManifestAndRender(): Promise<void> {
  try {
    manifest = await loadManifest(MANIFEST_URL);
    updateManifestEvaluation();
  } catch (error) {
    const detail = getErrorMessage(error);
    manifest = null;
    evaluations = [];
    selectedTag = null;
    refreshFirmwareView();
    renderStatus(dom, { level: 'error', message: 'Unable to load firmware updates.' });
    trackError('manifest_load_error', error, {
      manifest_url: MANIFEST_URL,
    });
    console.error('Manifest load failed:', detail);
  }
}

async function loadHardwareConfigForRetune(): Promise<void> {
  try {
    hardwareConfig = await loadHardwareConfig(HARDWARE_CONFIG_URL);
  } catch (error) {
    hardwareConfig = null;
    const detail = getErrorMessage(error);
    trackError('hardware_config_load_error', error, {
      hardware_config_url: HARDWARE_CONFIG_URL,
    });
    console.error('Hardware config load failed:', detail);
  }
}

function getFirmwareUiStage(): FirmwareUiStage {
  if (updateOutcome === 'success') {
    return 'complete';
  }

  if (
    activeOperation === 'firmware' ||
    pendingUpdateOutcome !== null ||
    otaReconnectPending ||
    reconnectPromptActive ||
    isAutoReconnectAttempt
  ) {
    return 'updating';
  }

  return 'selection';
}

function shouldHideFirmwareSelectionList(): boolean {
  return getFirmwareUiStage() !== 'selection';
}

function buildUpdatingFirmwareStatusMessage(version: string | null): string {
  return version ? `Updating to firmware version ${version}...` : 'Updating firmware...';
}

function shouldForceFirmwareUpdateStatus(statusLevel: 'info' | 'success' | 'warning' | 'error'): boolean {
  return getFirmwareUiStage() === 'updating' && statusLevel === 'info';
}

function renderFirmwareUpdateStatus(version: string | null): void {
  renderStatus(dom, {
    level: 'info',
    message: buildUpdatingFirmwareStatusMessage(version),
  });
}

function renderFirmwareStatusFromSession(
  status: OtaStatus,
  selectedVersion: string
): void {
  if (shouldForceFirmwareUpdateStatus(status.level)) {
    renderFirmwareUpdateStatus(selectedVersion);
    return;
  }

  renderStatus(dom, status);
}

function shouldShowFirmwareActionButtons(stage: FirmwareUiStage): boolean {
  if (stage === 'complete') {
    return false;
  }
  if (stage === 'updating' && activeOperation !== 'firmware') {
    return false;
  }
  return true;
}

function shouldShowStartFirmwareButton(stage: FirmwareUiStage): boolean {
  return stage === 'selection';
}

function shouldShowCancelFirmwareButton(): boolean {
  return activeOperation === 'firmware';
}

function renderFirmwareOptions(): void {
  const stage = getFirmwareUiStage();
  if (stage === 'selection') {
    const fingerprint = buildFirmwareOptionsRenderFingerprint();
    dom.firmwareList.hidden = false;
    if (firmwareOptionsRenderFingerprint === fingerprint) {
      return;
    }
    firmwareOptionsRenderFingerprint = fingerprint;
    renderFirmwareList(dom, evaluations, selectedTag, transport.getDeviceInfo()?.firmwareRevision ?? null);
    return;
  }

  // During install/reconnect/complete, treat firmware UI as a separate stage.
  firmwareOptionsRenderFingerprint = `stage:${stage}`;
  dom.firmwareList.innerHTML = '';
  dom.firmwareList.hidden = true;
  dom.firmwareEmpty.hidden = true;
}

function refreshFirmwareSummaryVisibility(): void {
  const showSummary = getFirmwareUiStage() === 'selection';
  dom.firmwareSummary.hidden = !showSummary;
  if (!showSummary) {
    dom.firmwareSummary.textContent = '';
  }
}

function refreshFirmwareActionButtonsVisibility(): void {
  const stage = getFirmwareUiStage();
  const actionRow = dom.startUpdateButton.parentElement as HTMLElement | null;
  const showActions = shouldShowFirmwareActionButtons(stage);
  if (actionRow) {
    actionRow.hidden = !showActions;
  }
  dom.startUpdateButton.hidden = !shouldShowStartFirmwareButton(stage);
  dom.cancelButton.hidden = !shouldShowCancelFirmwareButton();
}

function refreshFirmwareProgressVisibility(): void {
  dom.progressSection.hidden = getFirmwareUiStage() === 'complete';
}

function refreshFirmwareView(): void {
  renderFirmwareOptions();
  refreshFirmwareSummaryVisibility();
  refreshFirmwareActionButtonsVisibility();
  refreshFirmwareProgressVisibility();
}

function updateManifestEvaluation(force = false): void {
  const info = transport.getDeviceInfo();
  const fingerprint = buildManifestEvaluationFingerprint(info);
  if (!force && manifestEvaluationFingerprint === fingerprint) {
    return;
  }
  manifestEvaluationFingerprint = fingerprint;

  if (!manifest) {
    evaluations = [];
    selectedTag = null;
    refreshFirmwareView();
    refreshFirmwareFlowSummary();
    return;
  }

  evaluations = evaluateManifest(
    manifest,
    info
      ? {
          name: info.name,
          hardwareRevision: info.hardwareRevision,
          otaProtocol: OTA_PROTOCOL_ID,
        }
      : null
  );

  const selected = getSelectedUpdate();
  if (
    !shouldHideFirmwareSelectionList() &&
    (!selected ||
      selected.compatibility === 'incompatible' ||
      selected.update.size <= 0)
  ) {
    selectedTag = null;
  }

  if (!selectedTag) {
    const recommended = getRecommendedEvaluation(evaluations, info?.firmwareRevision ?? null);
    if (recommended && recommended.compatibility !== 'incompatible' && recommended.update.size > 0) {
      selectedTag = recommended.update.tag;
    }
  }

  refreshFirmwareView();
  refreshFirmwareFlowSummary();
}

function buildManifestEvaluationFingerprint(info: {
  name: string;
  hardwareRevision?: string;
  firmwareRevision?: string;
} | null): string {
  const updates = manifest?.updates ?? [];
  const manifestSignature = updates
    .map((update) => `${update.tag}:${update.version}:${update.date}:${update.size}`)
    .join(',');
  return [
    manifestSignature,
    (info?.name ?? '').trim().toLowerCase(),
    (info?.hardwareRevision ?? '').trim().toLowerCase(),
    (info?.firmwareRevision ?? '').trim().toLowerCase(),
  ].join('|');
}

function buildFirmwareOptionsRenderFingerprint(): string {
  const currentFirmwareRevision = normalizeFirmwareValue(transport.getDeviceInfo()?.firmwareRevision);
  const evaluationSignature = evaluations
    .map(
      (evaluation) =>
        [
          evaluation.update.tag,
          evaluation.update.version,
          evaluation.update.date,
          evaluation.update.size,
          evaluation.update.url,
          evaluation.update.release_notes,
          evaluation.update.release_url ?? '',
          evaluation.compatibility,
          evaluation.reasons.join('\n'),
        ].join(':')
    )
    .join('|');
  return [
    getFirmwareUiStage(),
    selectedTag ?? '',
    currentFirmwareRevision,
    evaluationSignature,
  ].join('||');
}

function getSelectedUpdate(): UpdateEvaluation | undefined {
  return evaluations.find((item) => item.update.tag === selectedTag);
}

function getLatestCompatibleEvaluation(items: UpdateEvaluation[]): UpdateEvaluation | null {
  const compatible = items.filter(
    (item) => item.compatibility === 'compatible' && item.update.size > 0
  );
  if (compatible.length === 0) {
    return null;
  }

  let best = compatible[0];
  let bestTimestamp = parseUpdateDate(best.update.date);
  for (let i = 1; i < compatible.length; i += 1) {
    const candidate = compatible[i];
    const candidateTime = parseUpdateDate(candidate.update.date);
    if (candidateTime > bestTimestamp) {
      best = candidate;
      bestTimestamp = candidateTime;
    }
  }

  return best;
}

function getRecommendedEvaluation(
  items: UpdateEvaluation[],
  currentFirmwareRevision: string | null
): UpdateEvaluation | null {
  const latestEligible = getLatestEligibleEvaluation(items);
  if (!latestEligible) {
    return null;
  }
  if (isCurrentFirmware(latestEligible.update, currentFirmwareRevision)) {
    return null;
  }
  return latestEligible;
}

function getLatestEligibleEvaluation(items: UpdateEvaluation[]): UpdateEvaluation | null {
  let best: UpdateEvaluation | null = null;
  let bestTimestamp = -Infinity;
  for (let i = 0; i < items.length; i += 1) {
    const candidate = items[i];
    if (candidate.compatibility === 'incompatible' || candidate.update.size <= 0) {
      continue;
    }
    const timestamp = parseUpdateDate(candidate.update.date);
    if (timestamp > bestTimestamp) {
      best = candidate;
      bestTimestamp = timestamp;
    }
  }
  return best;
}

function isCurrentFirmware(update: ManifestUpdate, currentFirmwareRevision: string | null): boolean {
  const normalizedCurrent = normalizeFirmwareValue(currentFirmwareRevision);
  if (!normalizedCurrent) {
    return false;
  }

  const normalizedVersion = normalizeFirmwareValue(update.version);
  const normalizedTag = normalizeFirmwareValue(update.tag);
  return normalizedCurrent === normalizedVersion || normalizedCurrent === normalizedTag;
}

function isDeviceOnLatestCompatibleFirmware(): boolean {
  const latest = getLatestCompatibleEvaluation(evaluations);
  const firmware = transport.getDeviceInfo()?.firmwareRevision;

  if (!latest || !firmware) {
    return false;
  }

  const normalizedCurrent = normalizeFirmwareValue(firmware);
  return (
    normalizedCurrent === normalizeFirmwareValue(latest.update.version) ||
    normalizedCurrent === normalizeFirmwareValue(latest.update.tag)
  );
}

function normalizeFirmwareValue(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  const normalized = value.trim().toLowerCase();
  return normalized.startsWith('v') ? normalized.slice(1) : normalized;
}

function refreshFirmwareFlowSummary(): void {
  if (selectedFlow !== 'firmware') {
    return;
  }

  if (shouldHideFirmwareSelectionList()) {
    dom.firmwareSummary.hidden = true;
    dom.firmwareSummary.textContent = '';
    return;
  }

  dom.firmwareSummary.hidden = false;
  const latest = getLatestCompatibleEvaluation(evaluations);

  if (!transport.isConnected()) {
    dom.firmwareSummary.textContent = 'Connect a knife to check whether an update is available.';
    return;
  }

  if (!latest) {
    dom.firmwareSummary.textContent = 'No compatible firmware update is currently available for this knife.';
    return;
  }

  if (isDeviceOnLatestCompatibleFirmware()) {
    dom.firmwareSummary.textContent = `Firmware is up to date (${latest.update.version}).`;
    return;
  }

  dom.firmwareSummary.hidden = true;
  dom.firmwareSummary.textContent = '';
}

function setStep(index: number): void {
  const previousStepIndex = currentStepIndex;
  const nextIndex = Math.max(0, Math.min(index, totalSteps - 1));
  currentStepIndex = nextIndex;

  if (previousStepIndex === 0 && currentStepIndex !== 0) {
    clearIdleDisconnectHomeMessage({ reason: 'left-home-step' });
  }

  wizardSteps.forEach((step, stepIndex) => {
    const isActive = stepIndex === currentStepIndex;
    step.classList.toggle('is-active', isActive);
    step.setAttribute('aria-hidden', isActive ? 'false' : 'true');
  });

  if (currentStepIndex === 3) {
    refreshFlowSectionContent();
  }

  updateWizardProgress();
  updateUiState();
}

function goNext(): void {
  if (currentStepIndex >= totalSteps - 1) {
    return;
  }

  if (!canAdvanceFrom(currentStepIndex)) {
    if (currentStepIndex === 1) {
      renderStatus(dom, { level: 'warning', message: 'Connect a knife to continue.' });
    } else if (currentStepIndex === 2) {
      renderStatus(dom, { level: 'warning', message: 'Choose a task to continue.' });
    }
    return;
  }

  setStep(currentStepIndex + 1);
}

async function goBack(): Promise<void> {
  if (currentStepIndex <= 0 || activeOperation !== 'none' || isAutoReconnectAttempt) {
    return;
  }

  if (currentStepIndex === 3 && selectedFlow === 'retune') {
    await revertUnsavedRetuneSelection('back navigation');
    resetRetuneUiState();
    setStep(2);
    return;
  }

  if (currentStepIndex === 3 && selectedFlow === 'diagnostics' && diagnosticsStage === 'complete') {
    resetDiagnosticsUiState();
    setStep(2);
    return;
  }

  if (currentStepIndex === 3 && updateOutcome === 'success') {
    resetFirmwareUiState();
    setStep(2);
    return;
  }

  setStep(currentStepIndex - 1);
}

function canAdvanceFrom(stepIndex: number): boolean {
  if (stepIndex === 0) {
    return true;
  }
  if (stepIndex === 1) {
    return transport.isConnected();
  }
  if (stepIndex === 2) {
    return selectedFlow !== null;
  }
  return false;
}

function updateWizardProgress(): void {
  if (currentStepIndex !== 3 || selectedFlow !== 'firmware') {
    dom.wizardProgressFill.style.width = '0%';
    dom.wizardProgressLabel.textContent = 'Step 1 of 3';
    dom.wizardProgressTitle.textContent = 'Firmware update';
    dom.wizardProgressFill.parentElement?.setAttribute('aria-valuenow', '0');
    return;
  }

  const percent = taskWizardTotal > 0 ? (taskWizardStep / taskWizardTotal) * 100 : 0;
  dom.wizardProgressFill.style.width = `${percent}%`;
  dom.wizardProgressLabel.textContent = `Step ${taskWizardStep} of ${taskWizardTotal}`;
  dom.wizardProgressTitle.textContent = taskWizardTitle;
  dom.wizardProgressFill.parentElement?.setAttribute('aria-valuenow', String(Math.round(percent)));
}

function setTaskWizardState(step: number, total: number, title: string): void {
  taskWizardStep = Math.max(1, Math.min(step, total));
  taskWizardTotal = Math.max(1, total);
  taskWizardTitle = title;
  updateWizardProgress();
}

function getFirmwareTaskWizardState(): { step: number; title: string } {
  if (
    pendingUpdateOutcome !== null ||
    otaReconnectPending ||
    reconnectPromptActive ||
    isAutoReconnectAttempt
  ) {
    return { step: 3, title: 'Reconnect knife' };
  }

  if (updateOutcome === 'success') {
    return { step: 3, title: 'Update complete' };
  }

  if (activeOperation === 'firmware' || updateOutcome === 'failed') {
    return { step: 2, title: 'Install firmware' };
  }

  return { step: 1, title: 'Choose firmware' };
}

function updateWizardNav(): void {
  wizardSteps.forEach((step, stepIndex) => {
    const backButton = step.querySelector<HTMLButtonElement>('[data-wizard-back]');
    const nextButton = step.querySelector<HTMLButtonElement>('[data-wizard-next]');

    if (backButton) {
      const isRetuneSuccessStep =
        stepIndex === totalSteps - 1 && selectedFlow === 'retune' && retuneStage === 'complete';
      const isDiagnosticsSuccessStep =
        stepIndex === totalSteps - 1 && selectedFlow === 'diagnostics' && diagnosticsStage === 'complete';
      backButton.hidden = stepIndex === 0 || isDiagnosticsSuccessStep;
      backButton.textContent = isRetuneSuccessStep || isDiagnosticsSuccessStep ? 'Home' : 'Back';
      backButton.disabled = activeOperation !== 'none' || isAutoReconnectAttempt;
    }

    if (nextButton) {
      nextButton.hidden = stepIndex >= totalSteps - 1;
      nextButton.disabled = activeOperation !== 'none' || isAutoReconnectAttempt || !canAdvanceFrom(stepIndex);
    }
  });
}

async function selectFlowFromInputs(): Promise<void> {
  const previousFlow = selectedFlow;
  if (dom.flowFirmware.checked) {
    selectedFlow = 'firmware';
  } else if (dom.flowRetune.checked) {
    selectedFlow = 'retune';
  } else if (dom.flowDiagnostics.checked) {
    selectedFlow = 'diagnostics';
  } else {
    selectedFlow = null;
  }
  if (selectedFlow !== previousFlow) {
    appendSupportLog('app', 'flow_selected', {
      previousFlow,
      selectedFlow,
      connected: transport.isConnected(),
      serialNumber: transport.getDeviceInfo()?.serialNumber ?? null,
    });
  }

  if (previousFlow === 'retune' && selectedFlow !== 'retune') {
    await revertUnsavedRetuneSelection('flow change');
    resetRetuneUiState();
  }

  if (currentStepIndex === 3) {
    refreshFlowSectionContent();
  }

  updateUiState();
}

function syncFlowInputs(): void {
  dom.flowFirmware.checked = selectedFlow === 'firmware';
  dom.flowRetune.checked = selectedFlow === 'retune';
  dom.flowDiagnostics.checked = selectedFlow === 'diagnostics';
}

function updateFlowAvailability(): void {
  const connected = transport.isConnected();
  const supportsToolboxCommands = transport.getCapabilities().command;
  const supportsSettingsTelemetry = transport.getCapabilities().settingsStatus;
  const busy = activeOperation !== 'none' || isAutoReconnectAttempt;
  const firmwareIsUpToDate = connected && isDeviceOnLatestCompatibleFirmware();
  const disableRetune = connected && !supportsToolboxCommands;
  const disableDiagnostics =
    connected && (!supportsToolboxCommands || !supportsSettingsTelemetry);

  dom.flowFirmware.disabled = busy;
  dom.flowRetune.disabled = busy || disableRetune;
  dom.flowDiagnostics.disabled = busy || disableDiagnostics;

  if (!connected) {
    dom.flowFirmwareBody.textContent = 'New firmware available';
  } else if (firmwareIsUpToDate) {
    dom.flowFirmwareBody.textContent = 'Your firmware is up to date.';
  } else {
    dom.flowFirmwareBody.textContent = 'New firmware available';
  }

  // Only auto-select retune while the user is on the flow chooser.
  if (firmwareIsUpToDate && currentStepIndex === 2 && !flowRetuneDefaultApplied) {
    if ((selectedFlow === null || selectedFlow === 'firmware') && !dom.flowRetune.disabled) {
      selectedFlow = 'retune';
      flowRetuneDefaultApplied = true;
      syncFlowInputs();
    }
  }

  if (disableRetune && selectedFlow === 'retune') {
    selectedFlow = 'firmware';
    syncFlowInputs();
  }

  if (disableDiagnostics && selectedFlow === 'diagnostics') {
    selectedFlow = 'firmware';
    syncFlowInputs();
  }
}

function refreshFlowSectionContent(): void {
  dom.firmwareFlowSection.hidden = true;
  dom.retuneFlowSection.hidden = true;
  dom.diagnosticsFlowSection.hidden = true;
  dom.statusText.hidden = true;
  dom.taskWizardProgress.hidden = true;

  if (selectedFlow === 'firmware') {
    const firmwareWizardState = getFirmwareTaskWizardState();
    dom.taskWizardProgress.hidden = false;
    setTaskWizardState(firmwareWizardState.step, 3, firmwareWizardState.title);
    dom.taskPanelTitle.textContent = 'Firmware update';
    dom.taskPanelDescription.textContent = '';
    dom.taskPanelDescription.hidden = true;
    dom.statusText.hidden = false;
    dom.firmwareFlowSection.hidden = false;
    refreshFirmwareView();
    refreshFirmwareFlowSummary();
    updateFirmwareButtons();
    return;
  }

  if (selectedFlow === 'retune') {
    dom.taskPanelTitle.textContent = 'Retune';
    dom.taskPanelDescription.textContent = '';
    dom.taskPanelDescription.hidden = true;
    dom.retuneFlowSection.hidden = false;
    refreshRetuneFlowState();
    return;
  }

  if (selectedFlow === 'diagnostics') {
    dom.taskPanelTitle.textContent = 'Diagnostic report';
    dom.taskPanelDescription.textContent = '';
    dom.taskPanelDescription.hidden = true;
    dom.diagnosticsFlowSection.hidden = false;
    refreshDiagnosticsFlowState();
  }
}

function updateFirmwareButtons(): void {
  const selected = getSelectedUpdate();
  const isBusy = activeOperation !== 'none' || isAutoReconnectAttempt;
  const canUpdate =
    selected &&
    selected.compatibility === 'compatible' &&
    selected.update.size > 0 &&
    transport.isConnected();

  dom.startUpdateButton.disabled = isBusy || !Boolean(canUpdate);
  dom.cancelButton.disabled = activeOperation !== 'firmware';
}

function updateRetuneButtons(): void {
  const isBusy = activeOperation !== 'none' || isAutoReconnectAttempt;
  const commandAvailable = transport.getCapabilities().command;
  const settingsAvailable = transport.getCapabilities().settingsStatus;
  const connected = transport.isConnected();
  const canInteractComparison =
    !isBusy &&
    connected &&
    commandAvailable &&
    retuneComparisonState != null &&
    (retuneStage === 'compare' || retuneStage === 'saving');

  dom.retuneChoiceOriginal.disabled = !canInteractComparison || retuneStage !== 'compare';
  dom.retuneChoiceUpdated.disabled = !canInteractComparison || retuneStage !== 'compare';
  dom.saveRetuneChoiceButton.disabled =
    !canInteractComparison ||
    !settingsAvailable ||
    retuneStage !== 'compare' ||
    retuneComparisonState == null;
}

function updateDiagnosticsButtons(): void {
  const isBusy = activeOperation !== 'none' || isAutoReconnectAttempt;
  const runningOrPaused = diagnosticsStage === 'running' || diagnosticsStage === 'paused';
  const hasReport = diagnosticReport !== null;
  dom.startDiagnosticsButton.hidden = true;
  dom.startDiagnosticsButton.disabled = true;
  dom.cancelDiagnosticsButton.disabled = activeOperation !== 'diagnostics' || !runningOrPaused;
  dom.downloadDiagnosticsButton.hidden = !hasReport;
  dom.downloadDiagnosticsButton.disabled = !hasReport || isBusy || diagnosticsStage !== 'complete';
  dom.diagnosticsHomeButton.hidden = diagnosticsStage !== 'complete';
  dom.diagnosticsHomeButton.disabled = diagnosticsStage !== 'complete' || isBusy;
}

function updateUiState(): void {
  const busy = activeOperation !== 'none' || isAutoReconnectAttempt;

  updateFlowAvailability();
  updateWizardNav();

  dom.connectButton.disabled = busy;
  dom.reconnectButton.disabled = busy;

  dom.deviceStatsPanel.hidden = !(transport.isConnected() && currentStepIndex > 0);

  if (currentStepIndex === 3) {
    refreshFlowSectionContent();
  }

  updateFirmwareButtons();
  updateRetuneButtons();
  updateDiagnosticsButtons();
  syncIdleDisconnectState();
}

function isIdleDisconnectPaused(): boolean {
  return activeOperation !== 'none' || isAutoReconnectAttempt || otaReconnectPending;
}

function syncIdleDisconnectState(): void {
  idleDisconnectController.setConnected(transport.isConnected());
  idleDisconnectController.setPaused(isIdleDisconnectPaused());
}

function triggerIdleDisconnect(): void {
  if (!transport.isConnected() || isIdleDisconnectPaused()) {
    return;
  }
  if (DEV_DIAGNOSTIC_CONSOLE_LOGS) {
    console.debug('[IdleDisconnect] timeout reached, disconnecting now');
  }
  pendingIdleDisconnect = true;
  transport.disconnect();
}

function showIdleDisconnectHomeMessage(): void {
  dom.idleDisconnectHomeMessage.textContent = IDLE_DISCONNECT_HOME_MESSAGE;
  dom.idleDisconnectHomeMessage.hidden = false;
  if (DEV_DIAGNOSTIC_CONSOLE_LOGS) {
    console.debug('[IdleDisconnect] home message shown');
  }
}

function clearIdleDisconnectHomeMessage(options: { reason?: string } = {}): void {
  if (dom.idleDisconnectHomeMessage.hidden) {
    return;
  }
  dom.idleDisconnectHomeMessage.hidden = true;
  if (DEV_DIAGNOSTIC_CONSOLE_LOGS) {
    console.debug('[IdleDisconnect] home message cleared', {
      reason: options.reason ?? 'unknown',
    });
  }
}

function buildRetuneButtonTelemetryUnavailableMessage(): string {
  return `Retune requires live button telemetry on this firmware. Please contact Seattle Ultrasonics support at ${SUPPORT_EMAIL}.`;
}

function buildDiagnosticsButtonTelemetryUnavailableMessage(): string {
  return `Diagnostics requires live button telemetry on this firmware. Please contact Seattle Ultrasonics support at ${SUPPORT_EMAIL}.`;
}

function getRetuneExperienceMode(): RetuneExperienceMode {
  return resolveRetuneExperienceMode(transport.getDeviceInfo()?.firmwareRevision ?? null);
}

function isTargetAutotuneRetuneExperience(): boolean {
  return transport.isConnected() && getRetuneExperienceMode() === 'target-autotune';
}

function refreshRetuneFirmwareBanner(): void {
  const showBanner = transport.isConnected() && getRetuneExperienceMode() === 'legacy-sweep';
  dom.retuneFirmwareBanner.hidden = !showBanner;
  dom.retuneFirmwareBanner.textContent = RETUNE_OLDER_FIRMWARE_MESSAGE;
}

function isRetuneTargetSelectionLocked(stage = retuneStage): boolean {
  if (isTargetAutotuneRetuneExperience()) {
    return stage === 'running' || stage === 'paused' || stage === 'saving';
  }

  return (
    stage === 'running' ||
    stage === 'paused' ||
    stage === 'compare' ||
    stage === 'saving' ||
    stage === 'complete'
  );
}

function resetRetuneTargetSelectionState(): void {
  retuneAvailableTargets = [];
  retuneSelectedTargetId = null;
  retuneDefaultTargetId = null;
  retuneLockedTarget = null;
  retuneKnownActiveState = null;
  retunePendingTargetApplyId = null;
  retunePreparedTargetId = null;
  retunePreparedAttemptBeforeState = null;
}

function getRetuneSelectedTarget(): OperationalTuningTargetConfig | null {
  if (isRetuneTargetSelectionLocked() && retuneLockedTarget) {
    return retuneLockedTarget;
  }

  if (!retuneSelectedTargetId) {
    return null;
  }

  return (
    retuneAvailableTargets.find((target) => target.target_id === retuneSelectedTargetId) ?? null
  );
}

function syncRetuneTargetSelectionState(): void {
  if (isRetuneTargetSelectionLocked()) {
    return;
  }

  if (!hardwareConfig || !transport.isConnected()) {
    retuneAvailableTargets = [];
    retuneSelectedTargetId = null;
    retuneDefaultTargetId = null;
    retuneKnownActiveState = null;
    return;
  }

  try {
    const currentCenterHz = resolveRetunePreviewCenterHz();
    const resolved = resolveRetuneTargets({
      config: hardwareConfig,
      revision: transport.getDeviceInfo()?.hardwareRevision ?? null,
      currentCenterHz,
    });

    retuneAvailableTargets = resolved.targets;
    retuneDefaultTargetId = resolved.defaultTargetId;
    retuneKnownActiveState = {
      targetId: resolved.defaultTargetId,
      centerHz: currentCenterHz,
    };

    if (
      !retuneSelectedTargetId ||
      !resolved.targets.some((target) => target.target_id === retuneSelectedTargetId)
    ) {
      retuneSelectedTargetId = resolved.defaultTargetId;
    }
  } catch {
    retuneAvailableTargets = [];
    retuneSelectedTargetId = null;
    retuneDefaultTargetId = null;
    retuneKnownActiveState = null;
  }
}

function handleRetuneTargetSelectionChange(targetId: string): void {
  if (isRetuneTargetSelectionLocked()) {
    return;
  }

  if (!retuneAvailableTargets.some((target) => target.target_id === targetId)) {
    return;
  }

  if (isTargetAutotuneRetuneExperience()) {
    handleRetuneTargetAutotuneSelectionChange(targetId);
    return;
  }

  if (activeOperation !== 'none') {
    return;
  }

  retuneSelectedTargetId = targetId;
  refreshRetuneTargetPreview();
}

function handleRetuneTargetAutotuneSelectionChange(targetId: string): void {
  const target = retuneAvailableTargets.find((candidate) => candidate.target_id === targetId);
  if (!target) {
    return;
  }

  if (activeOperation !== 'none' && !retuneTargetApplyInFlight) {
    return;
  }

  retuneSelectedTargetId = targetId;
  retunePreparedTargetId = null;
  retunePreparedAttemptBeforeState = null;
  resetRetuneTargetAutotuneRuntime();
  configureRetuneTargetAutotuneChart(target);
  retuneStage = 'idle';
  setRetuneStageUi(retuneStage);
  setRetuneProgress(0, retuneTargetApplyInFlight ? 'Preparing target...' : 'Not started');
  resetRetuneStatusTextTreatment();
  dom.retuneStatusText.textContent = retuneTargetApplyInFlight
    ? `Preparing ${target.description} next...`
    : `Preparing ${target.description}...`;
  renderRetuneTargetSelection();

  if (retuneTargetApplyInFlight) {
    retunePendingTargetApplyId = targetId;
    return;
  }

  void applySelectedRetuneTargetForAutotune();
}

function refreshRetuneTargetPreview(): void {
  syncRetuneTargetSelectionState();
  renderRetuneTargetSelection();

  if (isRetuneTargetSelectionLocked()) {
    return;
  }

  const selectedTarget = getRetuneSelectedTarget();
  const previewRange = resolveRetuneChartSupportedRange();
  retuneChart.setRuntimeMode(isTargetAutotuneRetuneExperience());
  retuneChart.setTargetOverlays(buildRetuneChartTargetOverlays(retuneAvailableTargets));
  retuneChart.setOriginalTarget(retuneDefaultTargetId);
  retuneChart.setSelectedTarget(selectedTarget?.target_id ?? null);
  retuneChart.setSweepRange(previewRange.minHz, previewRange.maxHz);
  dom.retuneInstruction.textContent = buildRetuneInstructionText(selectedTarget);
}

function renderRetuneTargetSelection(): void {
  const selectedTarget = getRetuneSelectedTarget();

  renderRetuneTargetSelector(dom, {
    targets: retuneAvailableTargets,
    selectedTargetId: selectedTarget?.target_id ?? null,
    summary: '',
    disabled: isRetuneTargetSelectionDisabled(),
    hidden: retuneAvailableTargets.length === 0,
  });
}

function isRetuneTargetSelectionDisabled(): boolean {
  if (isRetuneTargetSelectionLocked()) {
    return true;
  }

  if (isTargetAutotuneRetuneExperience()) {
    return activeOperation !== 'none' && !retuneTargetApplyInFlight;
  }

  return activeOperation !== 'none';
}

function renderRetuneChartLegend(originalHz: number | null): void {
  dom.retuneChartFrame.hidden = false;
  dom.retuneRuntimeRail.hidden = true;
  dom.retuneRuntimeLegend.hidden = true;
  const showOriginal = originalHz != null && Number.isFinite(originalHz);
  dom.retuneChartLegend.hidden = !showOriginal;
  dom.retuneChartLegendOriginal.textContent = showOriginal
    ? `Original: ${formatFrequencyHz(originalHz)} Hz`
    : 'Original: -- Hz';
}

function renderRetuneRuntimeChartLegend(): void {
  dom.retuneChartFrame.hidden = true;
  dom.retuneRuntimeRail.hidden = false;
  dom.retuneChartLegend.hidden = true;
  dom.retuneRuntimeLegend.hidden = true;
  renderRetuneRuntimeCenterLegend();
}

function setRetuneRuntimeCenterFrequency(hz: number | null): void {
  const nextCenterHz = hz != null && Number.isFinite(hz) ? Math.round(hz) : null;
  if (retuneRuntimeCenterHz === nextCenterHz) {
    return;
  }
  retuneRuntimeCenterHz = nextCenterHz;
  retuneChart.setRuntimeCenterFrequency(retuneRuntimeCenterHz);
  renderRetuneRuntimeCenterLegend();
  renderRetuneRuntimeRailCenter();
}

function renderRetuneRuntimeCenterLegend(): void {
  dom.retuneRuntimeLegendCenter.textContent =
    retuneRuntimeCenterHz == null
      ? 'Current center: -- Hz'
      : `Current center: ${formatFrequencyHz(retuneRuntimeCenterHz)} Hz`;
}

function configureRetuneRuntimeRailRange(range: { minHz: number; maxHz: number }): void {
  retuneRuntimeRailRange = range;
  dom.retuneRuntimeRangeMin.textContent = formatFrequencyKhz(range.minHz);
  dom.retuneRuntimeRangeMax.textContent = formatFrequencyKhz(range.maxHz);
  renderRetuneRuntimeRailCenter();
}

function resetRetuneRuntimeRail(): void {
  retuneRuntimeRailRange = null;
  dom.retuneRuntimeRail.dataset.phase = 'idle';
  dom.retuneRuntimeProbeLayer.replaceChildren();
  dom.retuneRuntimeRefineWindow.removeAttribute('style');
  dom.retuneRuntimeCenterMarker.hidden = true;
  dom.retuneRuntimeCenterMarker.removeAttribute('style');
  dom.retuneRuntimeCenterValue.textContent = '-- Hz';
  dom.retuneRuntimeRangeMin.textContent = '-- kHz';
  dom.retuneRuntimeRangeMax.textContent = '-- kHz';
  setRetuneRuntimeRailPhase(null);
}

function setRetuneRuntimeRailPhase(phase: AutotuneRuntimePhase | null): void {
  dom.retuneRuntimeRail.dataset.phase = phase ?? 'idle';
  const phases = [
    { phase: 'discover' as const, element: dom.retuneRuntimePhaseScanning },
    { phase: 'local_search' as const, element: dom.retuneRuntimePhaseRefining },
    { phase: 'track' as const, element: dom.retuneRuntimePhaseTuned },
  ];
  const activeIndex = phase ? phases.findIndex((entry) => entry.phase === phase) : -1;
  phases.forEach((entry, index) => {
    entry.element.classList.toggle('is-active', index === activeIndex);
    entry.element.classList.toggle('is-complete', activeIndex >= 0 && index < activeIndex);
  });
  if (phase === 'discover') {
    dom.retuneRuntimePhaseStatus.textContent = 'Scanning';
  } else if (phase === 'local_search') {
    dom.retuneRuntimePhaseStatus.textContent = 'Refining';
  } else if (phase === 'track') {
    dom.retuneRuntimePhaseStatus.textContent = 'Tuned';
    dom.retuneRuntimePhaseTuned.classList.add('is-complete');
  } else {
    dom.retuneRuntimePhaseStatus.textContent = 'Ready';
  }
}

function renderRetuneRuntimeRailCenter(): void {
  if (
    retuneRuntimeCenterHz == null ||
    !retuneRuntimeRailRange ||
    (retuneRuntimePhase == null && !retuneRuntimeCompleted)
  ) {
    dom.retuneRuntimeCenterMarker.hidden = true;
    dom.retuneRuntimeCenterValue.textContent = '-- Hz';
    return;
  }

  const percent = getRetuneRuntimeRailPercent(retuneRuntimeCenterHz);
  dom.retuneRuntimeCenterMarker.hidden = false;
  dom.retuneRuntimeCenterMarker.style.left = `${percent}%`;
  dom.retuneRuntimeCenterValue.textContent = `${formatFrequencyHz(retuneRuntimeCenterHz)} Hz`;
}

function addRetuneRuntimeRailSample(phase: AutotuneRuntimePhase, frequencyHz: number): void {
  const normalizedFrequencyHz = normalizeRetuneRuntimeFrequency(frequencyHz);
  if (!retuneRuntimeRailRange || normalizedFrequencyHz == null) {
    return;
  }

  const marker = document.createElement('span');
  marker.className = `retune-runtime-rail__probe retune-runtime-rail__probe--${phase}`;
  marker.style.left = `${getRetuneRuntimeRailPercent(normalizedFrequencyHz)}%`;
  marker.title = `${getRetuneRuntimePhaseLabel(phase)}: ${formatFrequencyHz(normalizedFrequencyHz)} Hz`;
  dom.retuneRuntimeProbeLayer.append(marker);

  while (dom.retuneRuntimeProbeLayer.children.length > RETUNE_RUNTIME_RAIL_MAX_PROBES) {
    dom.retuneRuntimeProbeLayer.firstElementChild?.remove();
  }

  if (phase === 'local_search' || phase === 'track') {
    updateRetuneRuntimeRailRefineWindow(normalizedFrequencyHz, phase);
  }
}

function updateRetuneRuntimeRailRefineWindow(
  centerHz: number,
  phase: AutotuneRuntimePhase
): void {
  if (!retuneRuntimeRailRange || !Number.isFinite(centerHz)) {
    return;
  }

  const rangeWidthHz = retuneRuntimeRailRange.maxHz - retuneRuntimeRailRange.minHz;
  const windowWidthPercent =
    phase === 'track'
      ? RETUNE_RUNTIME_RAIL_MIN_REFINING_WIDTH_PERCENT
      : Math.max(RETUNE_RUNTIME_RAIL_MIN_REFINING_WIDTH_PERCENT, Math.min(16, 30000 / rangeWidthHz));
  dom.retuneRuntimeRefineWindow.style.left = `${getRetuneRuntimeRailPercent(centerHz)}%`;
  dom.retuneRuntimeRefineWindow.style.width = `${windowWidthPercent}%`;
}

function getRetuneRuntimeRailPercent(frequencyHz: number): number {
  if (!retuneRuntimeRailRange) {
    return 50;
  }

  const spanHz = retuneRuntimeRailRange.maxHz - retuneRuntimeRailRange.minHz;
  if (!Number.isFinite(spanHz) || spanHz <= 0) {
    return 50;
  }

  const ratio = (frequencyHz - retuneRuntimeRailRange.minHz) / spanHz;
  return Math.max(0, Math.min(100, ratio * 100));
}

function normalizeRetuneRuntimeFrequency(value: number | null | undefined): number | null {
  if (value == null || !Number.isFinite(value)) {
    return null;
  }

  const roundedHz = Math.round(value);
  if (!retuneRuntimeRailRange) {
    return roundedHz;
  }

  const minHz = retuneRuntimeRailRange.minHz - RETUNE_RUNTIME_RANGE_TOLERANCE_HZ;
  const maxHz = retuneRuntimeRailRange.maxHz + RETUNE_RUNTIME_RANGE_TOLERANCE_HZ;
  return roundedHz >= minHz && roundedHz <= maxHz ? roundedHz : null;
}

function getRetuneRuntimePhaseLabel(phase: AutotuneRuntimePhase): string {
  if (phase === 'discover') {
    return 'Scanning';
  }
  if (phase === 'local_search') {
    return 'Refining';
  }
  return 'Tuned';
}

function configureRetuneTargetAutotuneChart(target: OperationalTuningTargetConfig | null): void {
  retuneChart.setRuntimeMode(true);
  retuneChart.clearTargetOverlays();

  const runRange = resolveRetuneChartRunRange(target);
  const range = runRange ?? resolveRetuneChartSupportedRange();
  if (runRange) {
    retuneChart.setSweepRange(runRange.minHz, runRange.maxHz);
  } else {
    retuneChart.setSweepRange(range.minHz, range.maxHz);
  }
  configureRetuneRuntimeRailRange(range);

  setRetuneRuntimeCenterFrequency(
    retuneRuntimeCenterHz ??
      (target ? resolveRetuneTargetSeedHz(target) : transport.getLatestSettingsStatus()?.centerFrequency ?? null)
  );
  renderRetuneRuntimeChartLegend();
}

function resetRetuneTargetAutotuneRuntime(clearChart = true): void {
  retuneRuntimePhase = null;
  retuneRuntimeRunActive = false;
  retuneRuntimeCompleted = false;
  retuneRuntimeLastSampleAtMs = 0;
  retuneRuntimeCenterHz = null;
  retuneRuntimeResultResolver = null;
  retuneRuntimeResultRejecter = null;
  retuneRuntimePendingResult = null;
  renderRetuneRuntimeCenterLegend();
  resetRetuneRuntimeRail();
  if (clearChart) {
    retuneChart.resetRuntime();
  }
}

function resetRetuneStatusTextTreatment(): void {
  dom.retuneStatusText.classList.remove('panel-sub--success');
  dom.retuneStatusText.classList.add('panel-sub--muted');
}

function renderRetuneTargetAutotuneCompleteMessage(target: OperationalTuningTargetConfig): void {
  dom.retuneStatusText.classList.remove('panel-sub--muted');
  dom.retuneStatusText.classList.add('panel-sub--success');

  const title = document.createElement('span');
  title.textContent = `Tuning complete for ${target.description}`;

  const guidance = document.createElement('span');
  guidance.className = 'retune-target-autotune-complete__guidance';
  guidance.textContent = 'You can try another tuning target, or return home';

  dom.retuneStatusText.replaceChildren(title, guidance);
}

async function applySelectedRetuneTargetForAutotune(): Promise<void> {
  if (!isTargetAutotuneRetuneExperience() || activeOperation !== 'none' || retuneTargetApplyInFlight) {
    return;
  }

  syncRetuneTargetSelectionState();
  const selectedTarget = getRetuneSelectedTarget();
  if (!selectedTarget) {
    return;
  }
  const applyingTargetId = selectedTarget.target_id;
  const attemptBeforeState = captureRetunePreviousActiveState();

  resetRetuneTargetAutotuneRuntime();
  configureRetuneTargetAutotuneChart(selectedTarget);
  retuneStage = 'idle';
  retuneComparisonState = null;
  retuneNoChangeResult = false;
  retunePreparedTargetId = null;
  retunePreparedAttemptBeforeState = null;
  retuneRequireButtonReleaseForAutoStart = retuneButtonPressed === true;
  retuneTargetApplyInFlight = true;
  activeOperation = 'retune';
  setRetuneStageUi(retuneStage);
  setRetuneProgress(0, 'Preparing target...');
  resetRetuneStatusTextTreatment();
  dom.retuneStatusText.textContent = `Preparing ${selectedTarget.description}...`;
  dom.retuneHoldBanner.hidden = true;
  updateUiState();

  try {
    const seedHz = resolveRetuneTargetSeedHz(selectedTarget);
    const preparedSelection = await prepareRetuneTargetAutotune(selectedTarget);
    retuneKnownActiveState = {
      targetId: preparedSelection.targetId,
      centerHz: seedHz,
    };
    if (retuneSelectedTargetId !== applyingTargetId) {
      return;
    }
    retunePreparedTargetId = applyingTargetId;
    retunePreparedAttemptBeforeState = attemptBeforeState;
    retuneStage = 'armed';
    setRetuneStageUi(retuneStage);
    setRetuneProgress(0, 'Waiting for button hold');
    setRetuneRuntimeCenterFrequency(seedHz);
    resetRetuneStatusTextTreatment();
    dom.retuneStatusText.textContent =
      `${selectedTarget.description} is ready. Hold the knife button to tune this target.`;
    appendLog(dom, {
      level: 'info',
      message:
        `Prepared ${selectedTarget.description} at ${formatFrequencyHz(seedHz)} Hz ` +
        `for target autotune (${preparedSelection.mode}).`,
    });
    sweepDebug('Retune', 'Prepared target autotune selection', {
      selectedTargetId: selectedTarget.target_id,
      seedHz,
      command: preparedSelection.command,
      mode: preparedSelection.mode,
      fallbackReason: preparedSelection.fallbackReason,
    });
  } catch (error) {
    if (retuneSelectedTargetId !== applyingTargetId) {
      return;
    }
    retunePreparedTargetId = null;
    retunePreparedAttemptBeforeState = null;
    retuneStage = 'idle';
    setRetuneStageUi(retuneStage);
    setRetuneProgress(0, 'Not started');
    const message = `Failed to prepare ${selectedTarget.description}: ${getErrorMessage(error)}`;
    dom.retuneStatusText.textContent = message;
    appendLog(dom, { level: 'error', message });
  } finally {
    activeOperation = 'none';
    retuneTargetApplyInFlight = false;
    const pendingTargetId = retunePendingTargetApplyId;
    retunePendingTargetApplyId = null;
    updateUiState();
    if (
      pendingTargetId &&
      pendingTargetId === retuneSelectedTargetId &&
      pendingTargetId !== retunePreparedTargetId
    ) {
      void applySelectedRetuneTargetForAutotune();
    }
  }
}

async function prepareRetuneTargetAutotune(
  target: OperationalTuningTargetConfig
): Promise<AppliedRetuneSelection> {
  const seedHz = resolveRetuneTargetSeedHz(target);
  await transport.unlockCommandChannel(COMMAND_UNLOCK_PASSPHRASE);
  await setDriveMode('autotune');
  await transport.sendTextCommand('SetAutoTuneMode SINGLE_FREQ');
  try {
    await transport.sendTextCommand('SetMultiFreq false');
  } catch (error) {
    sweepDebug('Retune', 'SetMultiFreq false was not accepted during target autotune prep', {
      error: getErrorMessage(error),
    });
  }

  const commandResult = await transport.setActiveTuningTargetWithFallback(target.target_id, seedHz);
  if (commandResult.command !== 'SetActiveTuningTarget') {
    throw new Error(
      `The knife did not acknowledge ${target.description} as the active tuning target. ` +
        `It only accepted a center-frequency fallback (${commandResult.fallbackReason ?? 'no target acknowledgement'}).`
    );
  }
  if (commandResult.targetId !== target.target_id) {
    throw new Error(
      `The knife acknowledged ${commandResult.targetId ?? 'an unknown target'} instead of ${target.target_id}.`
    );
  }
  retuneKnownActiveState = {
    targetId: commandResult.targetId ?? target.target_id,
    centerHz: seedHz,
  };
  const forceDiscoverResponse = await transport.sendTextCommandWithResponse(
    RETUNE_FORCE_SINGLE_FREQ_DISCOVER_COMMAND,
    2500,
    120
  );
  sweepDebug('Retune', 'Armed single-frequency discover for target autotune', {
    targetId: target.target_id,
    seedHz,
    command: RETUNE_FORCE_SINGLE_FREQ_DISCOVER_COMMAND,
    response: forceDiscoverResponse,
  });

  return {
    ...commandResult,
    targetId: commandResult.targetId ?? target.target_id,
    frequencyHz: seedHz,
  };
}

function restoreRetunePreRunChartPreview(): void {
  retuneChart.reset();
  refreshRetuneTargetPreview();

  const previewCenterHz = resolveRetunePreviewCenterHz();
  retuneChart.setInitialFrequency(previewCenterHz);
  renderRetuneChartLegend(previewCenterHz);
}

function buildRetuneInstructionText(_target: OperationalTuningTargetConfig | null): string {
  return '2. Hold the knife button to retune';
}

function getRetuneTargetDisplayName(targetId: string): string {
  if (retuneLockedTarget?.target_id === targetId) {
    return retuneLockedTarget.description;
  }

  return (
    retuneAvailableTargets.find((target) => target.target_id === targetId)?.description ?? targetId
  );
}

function getAppliedRetuneTargetDisplayName(targetId: string | null): string {
  return targetId ? getRetuneTargetDisplayName(targetId) : 'center frequency';
}

function getRetunePhaseProgress(
  phase: RetuneSweepPhase,
  paused: boolean
): { percent: number; label: string } {
  if (phase === 'coarse') {
    return {
      percent: 25,
      label: paused ? 'Finding target frequency (paused)' : 'Finding target frequency',
    };
  }

  return {
    percent: 65,
    label: paused ? 'Finetuning (paused)' : 'Finetuning',
  };
}

function resolveRetuneChartSupportedRange(): { minHz: number; maxHz: number } {
  const fallbackRange = {
    minHz: FREQUENCY_MIN_HZ,
    maxHz: FREQUENCY_MAX_HZ,
  };

  if (retuneAvailableTargets.length === 0) {
    return fallbackRange;
  }

  const paddingHz = 500;
  const minScanHz = Math.min(
    ...retuneAvailableTargets.map((target) =>
      Math.min(target.initial_tuning.scan_start_freq_hz, target.initial_tuning.scan_end_freq_hz)
    )
  );
  const maxScanHz = Math.max(
    ...retuneAvailableTargets.map((target) =>
      Math.max(target.initial_tuning.scan_start_freq_hz, target.initial_tuning.scan_end_freq_hz)
    )
  );
  const minHz = Math.round(minScanHz - paddingHz);
  const maxHz = Math.round(maxScanHz + paddingHz);

  if (!Number.isFinite(minHz) || !Number.isFinite(maxHz) || maxHz <= minHz) {
    return fallbackRange;
  }

  return { minHz, maxHz };
}

function resolveRetuneChartRunRange(
  target: OperationalTuningTargetConfig | null
): { minHz: number; maxHz: number } | null {
  if (!target) {
    return null;
  }

  const minHz = Math.round(
    Math.min(target.initial_tuning.scan_start_freq_hz, target.initial_tuning.scan_end_freq_hz)
  );
  const maxHz = Math.round(
    Math.max(target.initial_tuning.scan_start_freq_hz, target.initial_tuning.scan_end_freq_hz)
  );

  if (!Number.isFinite(minHz) || !Number.isFinite(maxHz) || maxHz <= minHz) {
    return null;
  }

  return { minHz, maxHz };
}

function buildRetuneChartTargetOverlays(
  targets: OperationalTuningTargetConfig[]
): Array<{
  targetId: string;
  label: string;
  centerHz: number;
  windowMinHz: number;
  windowMaxHz: number;
  previewMinHz: number;
  previewMaxHz: number;
}> {
  return targets.map((target) => ({
    targetId: target.target_id,
    label: target.description,
    centerHz: target.center_frequency_hz,
    windowMinHz: target.center_frequency_hz - target.search_bandwidth_hz,
    windowMaxHz: target.center_frequency_hz + target.search_bandwidth_hz,
    previewMinHz: target.initial_tuning.scan_start_freq_hz,
    previewMaxHz: target.initial_tuning.scan_end_freq_hz,
  }));
}

function resolveRetunePreviewCenterHz(): number {
  const deviceInfo = transport.getDeviceInfo();
  if (typeof deviceInfo?.centerFrequency === 'number' && Number.isFinite(deviceInfo.centerFrequency)) {
    return clampRetunePreviewHz(deviceInfo.centerFrequency);
  }
  if (typeof deviceInfo?.currentFrequency === 'number' && Number.isFinite(deviceInfo.currentFrequency)) {
    return clampRetunePreviewHz(deviceInfo.currentFrequency);
  }
  return RETUNE_DEFAULT_CENTER_HZ;
}

function resolveRetuneTargetSeedHz(target: OperationalTuningTargetConfig): number {
  return clampRetunePreviewHz(target.center_frequency_hz);
}

function captureRetunePreviousActiveState(): RetuneActiveState {
  if (retuneKnownActiveState) {
    return retuneKnownActiveState;
  }

  return {
    targetId: retuneDefaultTargetId,
    centerHz: resolveRetunePreviewCenterHz(),
  };
}

function clampRetunePreviewHz(value: number): number {
  return Math.max(FREQUENCY_MIN_HZ, Math.min(FREQUENCY_MAX_HZ, Math.round(value)));
}

function refreshRetuneFlowState(): void {
  refreshRetuneFirmwareBanner();
  retuneChart.setRuntimeMode(isTargetAutotuneRetuneExperience());
  syncRetuneTargetSelectionState();
  renderRetuneTargetSelection();
  renderRetuneComparisonState();
  setRetuneStageUi(retuneStage);

  if (isRetuneTargetSelectionLocked()) {
    return;
  }

  const capabilities = transport.getCapabilities();
  if (!transport.isConnected()) {
    retuneStage = 'idle';
    resetRetuneTargetAutotuneRuntime();
    retuneChart.setRuntimeMode(false);
    setRetuneStageUi(retuneStage);
    setRetuneProgress(0, 'Not started');
    retuneChart.clearTargetOverlays();
    retuneChart.setSweepRange(FREQUENCY_MIN_HZ, FREQUENCY_MAX_HZ);
    renderRetuneChartLegend(null);
    dom.retuneInstruction.textContent = buildRetuneInstructionText(null);
    dom.retuneStatusText.textContent = 'Connect a knife to start retune.';
    dom.retuneHoldBanner.hidden = true;
    return;
  }

  if (!capabilities.command) {
    retuneStage = 'idle';
    resetRetuneTargetAutotuneRuntime();
    setRetuneStageUi(retuneStage);
    setRetuneProgress(0, 'Not started');
    retuneChart.clearTargetOverlays();
    retuneChart.setSweepRange(FREQUENCY_MIN_HZ, FREQUENCY_MAX_HZ);
    renderRetuneChartLegend(null);
    dom.retuneInstruction.textContent = buildRetuneInstructionText(null);
    dom.retuneStatusText.textContent = 'Retune is not supported on this connected firmware.';
    dom.retuneHoldBanner.hidden = true;
    return;
  }

  if (!capabilities.settingsStatus) {
    retuneStage = 'idle';
    resetRetuneTargetAutotuneRuntime();
    setRetuneStageUi(retuneStage);
    setRetuneProgress(0, 'Not started');
    retuneChart.clearTargetOverlays();
    retuneChart.setSweepRange(FREQUENCY_MIN_HZ, FREQUENCY_MAX_HZ);
    renderRetuneChartLegend(null);
    dom.retuneInstruction.textContent = buildRetuneInstructionText(null);
    dom.retuneStatusText.textContent = buildRetuneButtonTelemetryUnavailableMessage();
    dom.retuneHoldBanner.hidden = true;
    return;
  }

  if (!hardwareConfig) {
    retuneStage = 'idle';
    resetRetuneTargetAutotuneRuntime();
    setRetuneStageUi(retuneStage);
    setRetuneProgress(0, 'Not started');
    retuneChart.clearTargetOverlays();
    retuneChart.setSweepRange(FREQUENCY_MIN_HZ, FREQUENCY_MAX_HZ);
    renderRetuneChartLegend(null);
    dom.retuneInstruction.textContent = buildRetuneInstructionText(null);
    dom.retuneStatusText.textContent = buildRetuneConfigUnavailableMessage();
    dom.retuneHoldBanner.hidden = true;
    return;
  }

  const latestSettings = transport.getLatestSettingsStatus();
  if (latestSettings) {
    retuneButtonPressed = latestSettings.buttonPhysicallyPressed;
    retuneButtonTelemetrySeen = true;
  }

  refreshRetuneTargetPreview();

  if (retuneAvailableTargets.length === 0 || !getRetuneSelectedTarget()) {
    retuneStage = 'idle';
    resetRetuneTargetAutotuneRuntime();
    setRetuneStageUi(retuneStage);
    setRetuneProgress(0, 'Not started');
    renderRetuneChartLegend(null);
    dom.retuneInstruction.textContent = buildRetuneInstructionText(null);
    dom.retuneStatusText.textContent = buildRetuneConfigUnavailableMessage();
    dom.retuneHoldBanner.hidden = true;
    return;
  }

  if (isTargetAutotuneRetuneExperience()) {
    refreshRetuneTargetAutotuneFlowState();
    return;
  }

  // Keep the pre-retune knife state marker visible while waiting for button hold.
  const previewCenterHz = resolveRetunePreviewCenterHz();
  retuneChart.setInitialFrequency(previewCenterHz);
  renderRetuneChartLegend(previewCenterHz);

  if (retuneStage === 'idle') {
    retuneStage = 'armed';
    setRetuneStageUi(retuneStage);
    setRetuneProgress(0, 'Waiting for button hold');
    dom.retuneStatusText.textContent = '';
  }

  if (retuneStage === 'armed') {
    maybeAutoStartRetuneFromButtonHold();
  }
}

function refreshRetuneTargetAutotuneFlowState(): void {
  const selectedTarget = getRetuneSelectedTarget();
  configureRetuneTargetAutotuneChart(selectedTarget);
  renderRetuneComparisonState();
  setRetuneStageUi(retuneStage);

  if (!selectedTarget) {
    retuneStage = 'idle';
    setRetuneStageUi(retuneStage);
    setRetuneProgress(0, 'Not started');
    dom.retuneStatusText.textContent = buildRetuneConfigUnavailableMessage();
    return;
  }

  if (
    activeOperation === 'none' &&
    !retuneTargetApplyInFlight &&
    retuneStage !== 'running' &&
    retunePreparedTargetId !== selectedTarget.target_id
  ) {
    void applySelectedRetuneTargetForAutotune();
    return;
  }

  if (retuneStage === 'idle' && retunePreparedTargetId === selectedTarget.target_id) {
    retuneStage = 'armed';
    setRetuneStageUi(retuneStage);
    setRetuneProgress(0, 'Waiting for button hold');
  }

  if (retuneStage === 'armed') {
    if (!dom.retuneStatusText.textContent.trim()) {
      dom.retuneStatusText.textContent =
        `${selectedTarget.description} is ready. Hold the knife button to tune this target.`;
    }
    setRetuneProgress(0, 'Waiting for button hold');
    maybeAutoStartRetuneFromButtonHold();
  }
}

function handleRetuneSettingsStatusUpdate(settings: SettingsStatus): void {
  const previousPressed = retuneButtonPressed;
  retuneButtonPressed = settings.buttonPhysicallyPressed;
  retuneButtonTelemetrySeen = true;

  if (previousPressed !== retuneButtonPressed) {
    sweepDebug('Retune', 'Button pressed state changed', {
      pressed: retuneButtonPressed,
      stage: retuneStage,
      activeOperation,
    });
  }

  if (!retuneButtonPressed && retuneRequireButtonReleaseForAutoStart) {
    retuneRequireButtonReleaseForAutoStart = false;
    sweepDebug('Retune', 'Auto-start re-armed after button release');
  }

  if (selectedFlow === 'retune' && currentStepIndex === 3) {
    if (isTargetAutotuneRetuneExperience()) {
      if (retuneStage === 'running' || retuneRuntimeCenterHz == null) {
        const telemetryFrequencyHz =
          retuneStage === 'running'
            ? resolveRetuneRuntimeCurrentFrequency(settings)
            : resolveRetuneRuntimeTelemetryFrequency(settings);
        if (telemetryFrequencyHz != null) {
          setRetuneRuntimeCenterFrequency(telemetryFrequencyHz);
        }
      }
      maybeAddRetuneRuntimeStatusSample(settings);
      if (retuneStage === 'running') {
        dom.retuneHoldBanner.textContent =
          'Keep holding the knife button until tuning is complete.';
        dom.retuneHoldBanner.hidden = settings.buttonPhysicallyPressed;
      }
    }
    maybeAutoStartRetuneFromButtonHold();
  }
}

function resolveRetuneRuntimeTelemetryFrequency(settings: SettingsStatus | null | undefined): number | null {
  return (
    resolveRetuneRuntimeCurrentFrequency(settings) ??
    normalizeRetuneRuntimeFrequency(settings?.centerFrequency)
  );
}

function resolveRetuneRuntimeCurrentFrequency(settings: SettingsStatus | null | undefined): number | null {
  return normalizeRetuneRuntimeFrequency(settings?.currentFrequency);
}

function maybeAddRetuneRuntimeStatusSample(settings: SettingsStatus, force = false): void {
  if (!isTargetAutotuneRetuneExperience() || retuneStage !== 'running' || retuneRuntimeCompleted) {
    return;
  }

  const phase = retuneRuntimePhase ?? (settings.autotuneActive || retuneRuntimeRunActive ? 'discover' : null);
  if (!phase) {
    return;
  }

  const now = Date.now();
  if (!force && now - retuneRuntimeLastSampleAtMs < RETUNE_RUNTIME_SAMPLE_INTERVAL_MS) {
    return;
  }

  const currentFrequencyHz = resolveRetuneRuntimeCurrentFrequency(settings);
  if (currentFrequencyHz == null) {
    return;
  }

  retuneRuntimeLastSampleAtMs = now;
  addRetuneRuntimeRailSample(phase, currentFrequencyHz);
}

function handleRetuneRuntimeTextLine(line: string): void {
  if (!isTargetAutotuneRetuneExperience() || selectedFlow !== 'retune' || currentStepIndex !== 3) {
    return;
  }

  const events = parseAutotuneRuntimeLine(line);
  events.forEach((event) => {
    if (event.type === 'run_start') {
      const expectedTarget = retuneLockedTarget ?? getRetuneSelectedTarget();
      if (event.targetId && expectedTarget && event.targetId !== expectedTarget.target_id) {
        rejectRetuneRuntimeResult(
          new Error(
            `The knife started autotune for ${event.targetId} instead of ${expectedTarget.target_id}.`
          )
        );
        return;
      }
      retuneRuntimeRunActive = true;
      retuneRuntimeCompleted = false;
      updateRetuneRuntimePhase('discover');
      setRetuneProgress(20, 'Scanning');
      sweepDebug('Retune', 'Autotune run started', {
        runId: event.runId,
        targetId: event.targetId,
        range: event.range,
        minStepHz: event.minStepHz,
      });
      return;
    }

    if (event.type === 'phase') {
      updateRetuneRuntimePhase(event.phase);
      return;
    }

    if (event.type === 'probe') {
      updateRetuneRuntimePhase(event.phase);
      addRetuneRuntimeRailSample(event.phase, event.frequencyHz);
      return;
    }

    const wasCompleted = retuneRuntimeCompleted;
    retuneRuntimeRunActive = false;
    const trackFreqHz = normalizeRetuneRuntimeFrequency(event.trackFreqHz);
    const sessionPeakFreqHz = normalizeRetuneRuntimeFrequency(event.sessionPeakFreqHz);
    if (!event.reachedTrack) {
      retuneRuntimeCompleted = false;
      rejectRetuneRuntimeResult(
        new Error(`Autotune finished before reaching track (${formatRetuneRuntimeRawResultFrequencies(event)}).`)
      );
      return;
    }
    if (event.reachedTrack && trackFreqHz == null && sessionPeakFreqHz == null) {
      retuneRuntimeCompleted = false;
      rejectRetuneRuntimeResult(
        new Error(
          `Autotune result was outside the selected target range ` +
            `(${formatRetuneRuntimeRawResultFrequencies(event)}).`
        )
      );
      return;
    }

    retuneRuntimeCompleted = true;
    const normalizedEvent: AutotuneRunResultEvent = {
      ...event,
      trackFreqHz,
      sessionPeakFreqHz,
    };

    if (trackFreqHz != null) {
      setRetuneRuntimeCenterFrequency(trackFreqHz);
      addRetuneRuntimeRailSample('track', trackFreqHz);
    } else if (sessionPeakFreqHz != null) {
      setRetuneRuntimeCenterFrequency(sessionPeakFreqHz);
      if (event.reachedTrack) {
        addRetuneRuntimeRailSample('track', sessionPeakFreqHz);
      }
    }
    if (event.reachedTrack) {
      setRetuneRuntimeRailPhase('track');
    }
    if (retuneStage === 'running' && !wasCompleted) {
      resolveRetuneRuntimeResult(normalizedEvent);
    }
  });
}

function updateRetuneRuntimePhase(phase: AutotuneRuntimePhase): void {
  retuneRuntimePhase = phase;
  if (retuneRuntimeCompleted) {
    retuneRuntimeRunActive = false;
    return;
  }

  retuneRuntimeRunActive = true;
  if (retuneStage !== 'running') {
    return;
  }

  setRetuneRuntimeRailPhase(phase);
  renderRetuneRuntimeRailCenter();
  const progress = getRetuneRuntimePhaseProgress(phase);
  setRetuneProgress(progress.percent, progress.label);
  if (phase === 'track') {
    const settings = transport.getLatestSettingsStatus();
    if (settings) {
      maybeAddRetuneRuntimeStatusSample(settings, true);
    }
    const result = buildRetuneRuntimeTrackReachedResult();
    if (result.trackFreqHz == null) {
      sweepDebug('Retune', 'Track phase reported before an in-range target frequency was observed', {
        targetRange: retuneRuntimeRailRange,
        latestSettings: settings,
      });
      return;
    }
    retuneRuntimeRunActive = false;
    retuneRuntimeCompleted = true;
    setRetuneRuntimeCenterFrequency(result.trackFreqHz);
    resolveRetuneRuntimeResult(result);
  }
}

function getRetuneRuntimePhaseProgress(
  phase: AutotuneRuntimePhase
): { percent: number; label: string } {
  if (phase === 'discover') {
    return { percent: 28, label: 'Scanning' };
  }
  if (phase === 'local_search') {
    return { percent: 62, label: 'Refining' };
  }
  return { percent: 100, label: 'Tuned' };
}

function buildRetuneRuntimeTrackReachedResult(): AutotuneRunResultEvent {
  const settings = transport.getLatestSettingsStatus();
  const powerStatus = transport.getLatestPowerStatus();
  const deviceInfo = transport.getDeviceInfo();
  const centerHz = resolveRetuneRuntimeCurrentFrequency(settings);
  const powerW = powerStatus?.powerWatts ?? deviceInfo?.powerWatts ?? null;
  return {
    type: 'run_result',
    runId: null,
    reachedTrack: true,
    timeToTrackMs: null,
    trackFreqHz: normalizeRetuneRuntimeFrequency(centerHz),
    trackPowerW: powerW != null && Number.isFinite(powerW) ? powerW : null,
    sessionPeakFreqHz: null,
    sessionPeakPowerW: null,
    raw: 'PHASE: TRACK',
  };
}

function resolveRetuneRuntimeResult(result: AutotuneRunResultEvent): void {
  if (retuneRuntimeResultResolver) {
    retuneRuntimeResultResolver(result);
    return;
  }

  retuneRuntimePendingResult = result;
}

function rejectRetuneRuntimeResult(error: Error): void {
  if (retuneRuntimeResultRejecter) {
    retuneRuntimeResultRejecter(error);
    return;
  }

  sweepDebug('Retune', 'Autotune runtime result rejected before waiter attached', {
    error: error.message,
  });
}

function formatRetuneRuntimeRawResultFrequencies(event: AutotuneRunResultEvent): string {
  const reported = [
    event.trackFreqHz == null ? null : `track ${formatFrequencyHz(event.trackFreqHz)} Hz`,
    event.sessionPeakFreqHz == null
      ? null
      : `peak ${formatFrequencyHz(event.sessionPeakFreqHz)} Hz`,
  ]
    .filter((value): value is string => value != null)
    .join(', ');
  const expected = retuneRuntimeRailRange
    ? `expected ${formatFrequencyHz(retuneRuntimeRailRange.minHz)}-${formatFrequencyHz(
        retuneRuntimeRailRange.maxHz
      )} Hz`
    : 'expected selected target range';
  return `${reported || 'no frequency reported'}; ${expected}`;
}

function waitForRetuneRuntimeResult(
  signal: AbortSignal,
  timeoutMs = RETUNE_TARGET_AUTOTUNE_RESULT_TIMEOUT_MS
): Promise<AutotuneRunResultEvent> {
  return new Promise((resolve, reject) => {
    let settled = false;
    const timeoutId = window.setTimeout(() => {
      rejectOnce(new Error('Timed out waiting for autotune result from the knife.'));
    }, timeoutMs);

    const resolveOnce = (result: AutotuneRunResultEvent): void => {
      if (settled) {
        return;
      }
      settled = true;
      cleanup();
      resolve(result);
    };

    const rejectOnce = (error: Error): void => {
      if (settled) {
        return;
      }
      settled = true;
      cleanup();
      reject(error);
    };

    const cleanup = (): void => {
      window.clearTimeout(timeoutId);
      signal.removeEventListener('abort', handleAbort);
      retuneRuntimeResultResolver = null;
      retuneRuntimeResultRejecter = null;
    };

    const handleAbort = (): void => {
      rejectOnce(new OtaAbortError('Retune canceled.'));
    };

    retuneRuntimeResultResolver = resolveOnce;
    retuneRuntimeResultRejecter = rejectOnce;
    signal.addEventListener('abort', handleAbort, { once: true });

    if (signal.aborted) {
      handleAbort();
      return;
    }

    if (retuneRuntimePendingResult) {
      const result = retuneRuntimePendingResult;
      retuneRuntimePendingResult = null;
      resolveOnce(result);
    }
  });
}

function maybeAutoStartRetuneFromButtonHold(): void {
  if (selectedFlow !== 'retune' || currentStepIndex !== 3) {
    return;
  }
  if (retuneStage !== 'armed') {
    return;
  }
  if (activeOperation !== 'none' || isAutoReconnectAttempt) {
    return;
  }
  if (retuneRequireButtonReleaseForAutoStart) {
    return;
  }
  if (retuneButtonPressed !== true) {
    return;
  }
  if (retuneAutoStartInFlight) {
    return;
  }

  retuneAutoStartInFlight = true;
  sweepDebug('Retune', 'Auto-start triggered by button hold', {
    buttonPressed: retuneButtonPressed,
    telemetrySeen: retuneButtonTelemetrySeen,
  });
  void startRetune().finally(() => {
    retuneAutoStartInFlight = false;
  });
}

function handleRetuneSweepPauseChange(phase: RetuneSweepPhase, paused: boolean): void {
  const progress = getRetunePhaseProgress(phase, paused);

  if (paused) {
    retuneStage = 'paused';
    setRetuneStageUi(retuneStage);
    dom.retuneHoldBanner.textContent =
      'Retune paused. Continue holding the button for the duration of retuning.';
    dom.retuneHoldBanner.hidden = false;
    setRetuneProgress(progress.percent, progress.label);
    sweepDebug('Retune', 'Sweep paused due to button release', { phase });
  } else {
    retuneStage = 'running';
    setRetuneStageUi(retuneStage);
    dom.retuneHoldBanner.hidden = true;
    setRetuneProgress(progress.percent, progress.label);
    sweepDebug('Retune', 'Sweep resumed due to button press', { phase });
  }
}

function refreshDiagnosticsFlowState(): void {
  setDiagnosticsStageUi(diagnosticsStage);

  if (diagnosticsStage === 'running' || diagnosticsStage === 'paused' || diagnosticsStage === 'complete') {
    return;
  }

  const capabilities = transport.getCapabilities();
  if (!transport.isConnected()) {
    diagnosticsStage = 'idle';
    setDiagnosticsStageUi(diagnosticsStage);
    setDiagnosticsProgress(0, 'Not started');
    dom.diagnosticsStatusText.textContent = 'Connect a knife to start diagnostics.';
    dom.diagnosticsHoldBanner.hidden = true;
    return;
  }

  if (!capabilities.command) {
    diagnosticsStage = 'idle';
    setDiagnosticsStageUi(diagnosticsStage);
    setDiagnosticsProgress(0, 'Not started');
    dom.diagnosticsStatusText.textContent = 'Diagnostics are not supported on this connected firmware.';
    dom.diagnosticsHoldBanner.hidden = true;
    return;
  }

  if (!capabilities.settingsStatus) {
    diagnosticsStage = 'idle';
    setDiagnosticsStageUi(diagnosticsStage);
    setDiagnosticsProgress(0, 'Not started');
    dom.diagnosticsStatusText.textContent = buildDiagnosticsButtonTelemetryUnavailableMessage();
    dom.diagnosticsHoldBanner.hidden = true;
    return;
  }

  if (!hardwareConfig) {
    diagnosticsStage = 'idle';
    setDiagnosticsStageUi(diagnosticsStage);
    setDiagnosticsProgress(0, 'Not started');
    dom.diagnosticsStatusText.textContent = buildDiagnosticsConfigUnavailableMessage();
    dom.diagnosticsHoldBanner.hidden = true;
    return;
  }

  const latestSettings = transport.getLatestSettingsStatus();
  if (latestSettings) {
    diagnosticsButtonPressed = latestSettings.buttonPhysicallyPressed;
    diagnosticsButtonTelemetrySeen = true;
  }

  if (diagnosticsStage === 'idle') {
    diagnosticsStage = 'armed';
    setDiagnosticsStageUi(diagnosticsStage);
    setDiagnosticsProgress(0, 'Waiting for button hold');
    dom.diagnosticsStatusText.textContent = '';
  }

  if (diagnosticsStage === 'armed') {
    maybeAutoStartDiagnosticsFromButtonHold();
  }
}

function handleDiagnosticsSettingsStatusUpdate(settings: SettingsStatus): void {
  const previousPressed = diagnosticsButtonPressed;
  diagnosticsButtonPressed = settings.buttonPhysicallyPressed;
  diagnosticsButtonTelemetrySeen = true;

  if (previousPressed !== diagnosticsButtonPressed) {
    sweepDebug('Diagnostics', 'Button pressed state changed', {
      pressed: diagnosticsButtonPressed,
      stage: diagnosticsStage,
      activeOperation,
    });
  }

  if (!diagnosticsButtonPressed && diagnosticsRequireButtonReleaseForAutoStart) {
    diagnosticsRequireButtonReleaseForAutoStart = false;
    sweepDebug('Diagnostics', 'Auto-start re-armed after button release');
  }

  if (selectedFlow === 'diagnostics' && currentStepIndex === 3) {
    maybeAutoStartDiagnosticsFromButtonHold();
  }
}

function maybeAutoStartDiagnosticsFromButtonHold(): void {
  if (selectedFlow !== 'diagnostics' || currentStepIndex !== 3) {
    return;
  }
  if (diagnosticsStage !== 'armed') {
    return;
  }
  if (activeOperation !== 'none' || isAutoReconnectAttempt) {
    return;
  }
  if (diagnosticsRequireButtonReleaseForAutoStart) {
    return;
  }
  if (diagnosticsButtonPressed !== true) {
    return;
  }
  if (diagnosticsAutoStartInFlight) {
    return;
  }

  diagnosticsAutoStartInFlight = true;
  sweepDebug('Diagnostics', 'Auto-start triggered by button hold', {
    buttonPressed: diagnosticsButtonPressed,
    telemetrySeen: diagnosticsButtonTelemetrySeen,
  });
  void startDiagnostics().finally(() => {
    diagnosticsAutoStartInFlight = false;
  });
}

function buildDiagnosticsRunningProgressLabel(paused: boolean): string {
  const base = paused ? 'Collecting diagnostic data (paused)' : 'Collecting diagnostic data';
  if (!diagnosticsActiveTargetProgress) {
    return base;
  }
  if (diagnosticsActiveTargetPercent == null) {
    return `${base} (${diagnosticsActiveTargetProgress})`;
  }
  return `${base} (${diagnosticsActiveTargetProgress}) ${diagnosticsActiveTargetPercent}%`;
}

function calculateDiagnosticsTargetSweepProgressPercent(
  targetIndex: number,
  totalTargets: number,
  targetPercent: number | null
): number {
  if (totalTargets <= 0) {
    return 90;
  }
  const clampedIndex = Math.max(1, Math.min(targetIndex, totalTargets));
  const clampedTargetPercent = Math.max(0, Math.min(100, targetPercent ?? 0));
  const completedTargetPortion = (clampedIndex - 1) + clampedTargetPercent / 100;
  const ratio = Math.min(1, completedTargetPortion / totalTargets);
  return 25 + Math.round(ratio * 65);
}

function handleDiagnosticsSweepPauseChange(
  paused: boolean,
  details: { targetId: string; pointCount: number; lastFrequencyHz: number | null }
): void {
  if (paused) {
    diagnosticsStage = 'paused';
    setDiagnosticsStageUi(diagnosticsStage);
    dom.diagnosticsHoldBanner.textContent = 'Diagnostics paused. Continue holding the button to resume.';
    dom.diagnosticsHoldBanner.hidden = false;
    dom.diagnosticsProgressLabel.textContent = buildDiagnosticsRunningProgressLabel(true);
    sweepDebug('Diagnostics', 'Sweep paused due to button release', details);
    return;
  }

  diagnosticsStage = 'running';
  setDiagnosticsStageUi(diagnosticsStage);
  dom.diagnosticsHoldBanner.hidden = true;
  dom.diagnosticsProgressLabel.textContent = buildDiagnosticsRunningProgressLabel(false);
  sweepDebug('Diagnostics', 'Sweep resumed due to button press', details);
}

function showCompatibilityNotice(result: CompatibilityResult): void {
  wizardSteps.forEach((step) => {
    step.classList.remove('is-active');
    step.setAttribute('aria-hidden', 'true');
    step.hidden = true;
  });

  dom.deviceStatsPanel.hidden = true;
  dom.connectButton.disabled = true;
  dom.reconnectButton.disabled = true;
  dom.taskWizardProgress.hidden = true;
  dom.compatibilityPanel.hidden = false;

  if (result.reason) {
    dom.compatibilityMessage.textContent = getCompatibilityMessage(result.reason);
  }

  trackEvent('compatibility_blocked', {
    reason: result.reason ?? 'unknown',
    browser_family: detectBrowserFamily(),
    platform: detectPlatformFamily(),
  });
}

function trackUpdateResult(outcome: 'success' | 'failed' | 'canceled', stage?: UpdateErrorStage): void {
  if (updateResultTracked || updateStartedAtMs === null) {
    return;
  }

  const durationMs = Math.max(0, Date.now() - updateStartedAtMs);
  trackEvent('update_result', {
    outcome,
    firmware_version: updateFirmwareVersion,
    target_firmware_version: updateFirmwareVersion,
    before_firmware_version: updateBeforeFirmwareVersion,
    error_stage: outcome === 'success' ? undefined : stage ?? updateFailureStage ?? 'ota',
    duration_ms: durationMs,
    auto_reconnect_used: updateUsedAutoReconnect,
    reconnect_issue: updateHadReconnectIssue,
  });
  updateResultTracked = true;
}

async function startUpdate(): Promise<void> {
  if (activeOperation !== 'none') {
    return;
  }

  if (DEV_CONTROLS_ENABLED && devFirmwareMode !== 'live') {
    triggerDevUpdateOutcome(devFirmwareMode === 'success' ? 'success' : 'failed');
    return;
  }

  const selected = getSelectedUpdate();
  if (!selected || selected.compatibility !== 'compatible') {
    renderStatus(dom, { level: 'warning', message: 'Select a compatible firmware first.' });
    return;
  }
  if (selected.update.size <= 0) {
    renderStatus(dom, { level: 'warning', message: 'Firmware asset is not available yet.' });
    return;
  }

  const beforeFirmwareVersion = transport.getDeviceInfo()?.firmwareRevision ?? null;

  cancelOtaReconnect();
  resetOtaReconnectDebugState();
  hideReconnectPrompt();

  activeOperation = 'firmware';
  abortController = new AbortController();
  updateOutcome = null;
  pendingUpdateOutcome = null;

  resetFirmwareProgress('Preparing firmware download...');
  setTaskWizardState(2, 3, 'Install firmware');
  dom.progressDetail.textContent = UPDATE_IN_PROGRESS_MESSAGE;
  renderFirmwareUpdateStatus(selected.update.version);
  updateUiState();

  updateStartedAtMs = Date.now();
  updateResultTracked = false;
  updateFailureStage = null;
  updateFirmwareVersion = selected.update.version;
  updateBeforeFirmwareVersion = beforeFirmwareVersion;
  updateUsedAutoReconnect = false;
  updateHadReconnectIssue = false;

  trackEvent('update_started', {
    firmware_tag: selected.update.tag,
    firmware_version: selected.update.version,
    target_firmware_version: selected.update.version,
    before_firmware_version: beforeFirmwareVersion,
    firmware_size: selected.update.size,
  });

  try {
    const firmware = await fetchFirmware(selected.update, abortController.signal);
    const session = new OtaSession(transport, {
      onStatus: (status) => renderFirmwareStatusFromSession(status, selected.update.version),
      onProgress: (progress: OtaProgress) => renderProgress(dom, progress),
      onLog: (entry) => appendLog(dom, entry),
    });

    appendLog(dom, { level: 'info', message: 'Starting OTA transfer.' });
    await session.run(firmware, abortController.signal);

    pendingUpdateOutcome = {
      outcome: 'success',
      message: 'Your knife is now ready to use. You can close this window, or go back and choose another task.',
    };

    otaReconnectPending = true;
    otaReconnectReadyAtMs = Date.now();
    transport.setKeepDeviceOnDisconnect(true);
    setTaskWizardState(3, 3, 'Reconnect knife');
    dom.progressSpeed.hidden = true;
    dom.progressDetail.textContent = OTA_RECONNECT_PROGRESS_MESSAGE;
    renderStatus(dom, {
      level: 'info',
      message: 'Update complete. Rebooting and reconnecting...',
    });

    updateUsedAutoReconnect = transport.canReconnect();
    otaReconnectInfo('OTA transfer complete. Entering reconnect phase.', {
      browserFamily: detectBrowserFamily(),
      platformFamily: detectPlatformFamily(),
      userAgent: navigator.userAgent,
      firmwareVersion: selected.update.version,
      firmwareTag: selected.update.tag,
    });

    if (!transport.canReconnect()) {
      otaReconnectInfo('Auto-reconnect unavailable after OTA complete.');
      finalizeUpdateOutcome();
    } else {
      otaReconnectInfo('Reconnect fallback armed; waiting up to timeout for reboot disconnect event.');
      scheduleOtaReconnectDisconnectFallback();
    }
  } catch (error) {
    const aborted =
      abortController?.signal.aborted ||
      error instanceof OtaAbortError ||
      (error instanceof DOMException && error.name === 'AbortError');

    if (aborted) {
      cancelOtaReconnect();
      resetOtaReconnectDebugState();
      renderStatus(dom, { level: 'warning', message: 'Update canceled.' });
      appendLog(dom, { level: 'error', message: 'Update canceled.' });
      updateFailureStage = updateFailureStage ?? 'ota';
      trackUpdateResult('canceled', updateFailureStage);
    } else if (error instanceof Error) {
      cancelOtaReconnect();
      updateFailureStage = inferUpdateErrorStage(error);
      appendLog(dom, { level: 'error', message: error.message });
      trackError('update_error', error, {
        firmware_version: updateFirmwareVersion,
        target_firmware_version: updateFirmwareVersion,
        before_firmware_version: updateBeforeFirmwareVersion,
        error_stage: updateFailureStage,
      });
      showUpdateOutcome('failed', 'Firmware update failed. Reconnect and try again.');
    } else {
      cancelOtaReconnect();
      updateFailureStage = 'ota';
      trackEvent('update_error', {
        firmware_version: updateFirmwareVersion,
        target_firmware_version: updateFirmwareVersion,
        before_firmware_version: updateBeforeFirmwareVersion,
        error_stage: updateFailureStage,
      });
      showUpdateOutcome('failed', 'Firmware update failed. Reconnect and try again.');
    }
  } finally {
    activeOperation = 'none';
    abortController = null;
    updateUiState();
  }
}

async function startRetuneTargetAutotune(): Promise<void> {
  resetRetuneStatusTextTreatment();
  const capabilities = transport.getCapabilities();
  if (!capabilities.command) {
    retuneStage = 'idle';
    setRetuneStageUi(retuneStage);
    setRetuneProgress(0, 'Not started');
    dom.retuneStatusText.textContent = 'Retune is not supported on this connected firmware.';
    dom.retuneHoldBanner.hidden = true;
    updateUiState();
    return;
  }

  if (!capabilities.settingsStatus) {
    retuneStage = 'idle';
    setRetuneStageUi(retuneStage);
    setRetuneProgress(0, 'Not started');
    dom.retuneStatusText.textContent = buildRetuneButtonTelemetryUnavailableMessage();
    dom.retuneHoldBanner.hidden = true;
    updateUiState();
    return;
  }

  if (retuneButtonPressed !== true) {
    if (retuneStage !== 'armed') {
      retuneStage = 'armed';
      setRetuneStageUi(retuneStage);
      setRetuneProgress(0, 'Waiting for button hold');
    }
    dom.retuneStatusText.textContent = '';
    dom.retuneHoldBanner.hidden = true;
    updateUiState();
    return;
  }

  if (!hardwareConfig) {
    const message = buildRetuneConfigUnavailableMessage();
    retuneStage = 'idle';
    dom.retuneStatusText.textContent = message;
    setRetuneProgress(0, 'Not started');
    setRetuneStageUi(retuneStage);
    appendLog(dom, { level: 'error', message });
    updateUiState();
    return;
  }

  syncRetuneTargetSelectionState();
  const selectedTarget = getRetuneSelectedTarget();
  if (!selectedTarget) {
    const message = buildRetuneConfigUnavailableMessage();
    retuneStage = 'idle';
    dom.retuneStatusText.textContent = message;
    setRetuneProgress(0, 'Not started');
    setRetuneStageUi(retuneStage);
    appendLog(dom, { level: 'error', message });
    updateUiState();
    return;
  }
  const attemptBeforeState = resolvePreparedRetuneAttemptBeforeState(selectedTarget.target_id);

  retuneRequireButtonReleaseForAutoStart = true;
  activeOperation = 'retune';
  abortController = new AbortController();
  clearRetuneUploadDraft();
  retuneComparisonState = null;
  retuneNoChangeResult = false;
  retuneLockedTarget = selectedTarget;
  retuneStage = 'running';
  resetRetuneTargetAutotuneRuntime();
  configureRetuneTargetAutotuneChart(selectedTarget);
  renderRetuneComparisonState();
  setRetuneStageUi(retuneStage);
  setRetuneRuntimeRailPhase('discover');
  dom.retuneStatusText.textContent = `Tuning ${selectedTarget.description}. Keep holding the knife button.`;
  dom.retuneHoldBanner.hidden = true;
  setRetuneProgress(12, 'Waiting for autotune start');
  updateUiState();

  let targetPreparedForAttempt = retunePreparedTargetId === selectedTarget.target_id;
  try {
    if (retunePreparedTargetId !== selectedTarget.target_id) {
      await prepareRetuneTargetAutotune(selectedTarget);
      retunePreparedTargetId = selectedTarget.target_id;
      retunePreparedAttemptBeforeState = attemptBeforeState;
      retuneKnownActiveState = {
        targetId: selectedTarget.target_id,
        centerHz: resolveRetuneTargetSeedHz(selectedTarget),
      };
    }
    targetPreparedForAttempt = true;

    const result = await waitForRetuneRuntimeResult(abortController.signal);
    const finalHz =
      normalizeRetuneRuntimeFrequency(result.trackFreqHz) ??
      normalizeRetuneRuntimeFrequency(result.sessionPeakFreqHz) ??
      resolveRetuneRuntimeCurrentFrequency(transport.getLatestSettingsStatus()) ??
      resolveRetuneTargetSeedHz(selectedTarget);
    setRetuneRuntimeCenterFrequency(finalHz);
    retuneStage = 'complete';
    retuneLockedTarget = null;
    retuneRuntimeRunActive = false;
    retuneRuntimeCompleted = true;
    setRetuneStageUi(retuneStage);
    setRetuneProgress(100, 'Tuned');
    renderRetuneTargetAutotuneCompleteMessage(selectedTarget);
    dom.retuneHoldBanner.hidden = true;
    appendLog(dom, {
      level: 'success',
      message:
        `Target autotune complete for ${selectedTarget.description}. ` +
        `Center ${formatFrequencyHz(finalHz)} Hz.`,
    });
    const savedAtIso = new Date().toISOString();
    trackRetuneTargetAttemptSaved({
      beforeTargetId: attemptBeforeState.targetId,
      selectedTargetId: selectedTarget.target_id,
      appliedTargetId: selectedTarget.target_id,
      attemptKind: 'target_autotune',
    });
    void queueRetuneTargetAttemptUpload({
      attemptKind: 'target_autotune',
      beforeState: attemptBeforeState,
      selectedTargetId: selectedTarget.target_id,
      appliedTargetId: selectedTarget.target_id,
      appliedCenterHz: finalHz,
      savedAtIso,
      userDecision: null,
      runtime: buildRetuneRuntimeSummary(result),
    });
    retunePreparedAttemptBeforeState = {
      targetId: selectedTarget.target_id,
      centerHz: finalHz,
    };
    retuneKnownActiveState = retunePreparedAttemptBeforeState;
    sweepDebug('Retune', 'Target autotune result', {
      targetId: selectedTarget.target_id,
      runId: result.runId,
      reachedTrack: result.reachedTrack,
      timeToTrackMs: result.timeToTrackMs,
      finalHz,
      trackPowerW: result.trackPowerW,
      sessionPeakPowerW: result.sessionPeakPowerW,
    });
  } catch (error) {
    const aborted = abortController?.signal.aborted;
    retuneRuntimeRunActive = false;
    retuneRuntimeCompleted = false;
    retuneLockedTarget = null;
    retuneStage = 'armed';
    setRetuneStageUi(retuneStage);
    setRetuneRuntimeRailPhase(null);
    setRetuneProgress(0, 'Waiting for button hold');
    dom.retuneHoldBanner.hidden = true;
    if (aborted) {
      dom.retuneStatusText.textContent =
        'Retune canceled. Hold the knife button to tune this target again.';
      appendLog(dom, { level: 'error', message: 'Retune canceled.' });
    } else {
      const message = `Target autotune failed: ${getErrorMessage(error)}`;
      dom.retuneStatusText.textContent = message;
      appendLog(dom, { level: 'error', message });
      sweepDebug('Retune', 'Target autotune failed', {
        targetId: selectedTarget.target_id,
        error: getErrorMessage(error),
      });
    }
    if (targetPreparedForAttempt) {
      retunePreparedAttemptBeforeState = {
        targetId: selectedTarget.target_id,
        centerHz: resolveRetuneTargetSeedHz(selectedTarget),
      };
      retuneKnownActiveState = retunePreparedAttemptBeforeState;
    }
  } finally {
    activeOperation = 'none';
    abortController = null;
    retuneRuntimeResultResolver = null;
    retuneRuntimeResultRejecter = null;
    updateUiState();
  }
}

async function startRetune(): Promise<void> {
  if (activeOperation !== 'none' || selectedFlow !== 'retune') {
    return;
  }

  if (DEV_CONTROLS_ENABLED && devRetuneMode !== 'live') {
    triggerDevRetuneOutcome(devRetuneMode === 'success' ? 'success' : 'failed');
    return;
  }

  if (isTargetAutotuneRetuneExperience()) {
    await startRetuneTargetAutotune();
    return;
  }

  const capabilities = transport.getCapabilities();
  if (!capabilities.command) {
    retuneStage = 'idle';
    setRetuneStageUi(retuneStage);
    setRetuneProgress(0, 'Not started');
    dom.retuneStatusText.textContent = 'Retune is not supported on this connected firmware.';
    dom.retuneHoldBanner.hidden = true;
    updateUiState();
    return;
  }

  if (!capabilities.settingsStatus) {
    retuneStage = 'idle';
    setRetuneStageUi(retuneStage);
    setRetuneProgress(0, 'Not started');
    dom.retuneStatusText.textContent = buildRetuneButtonTelemetryUnavailableMessage();
    dom.retuneHoldBanner.hidden = true;
    updateUiState();
    return;
  }

  if (retuneButtonPressed !== true) {
    if (retuneStage !== 'armed') {
      retuneStage = 'armed';
      setRetuneStageUi(retuneStage);
      setRetuneProgress(0, 'Waiting for button hold');
    }
    dom.retuneStatusText.textContent = '';
    dom.retuneHoldBanner.hidden = true;
    updateUiState();
    return;
  }

  const hardwareRevision = transport.getDeviceInfo()?.hardwareRevision ?? null;
  sweepDebug('Retune', 'Retune request received', {
    detectedHardwareRevision: hardwareRevision,
    hasHardwareConfig: Boolean(hardwareConfig),
    liveCenterFrequencyHz: transport.getDeviceInfo()?.centerFrequency ?? null,
  });

  if (!hardwareConfig) {
    const message = buildRetuneConfigUnavailableMessage();
    retuneStage = 'idle';
    dom.retuneStatusText.textContent = message;
    setRetuneProgress(0, 'Not started');
    setRetuneStageUi(retuneStage);
    appendLog(dom, { level: 'error', message });
    updateUiState();
    return;
  }

  syncRetuneTargetSelectionState();
  const selectedTarget = getRetuneSelectedTarget();
  if (!selectedTarget) {
    const message = buildRetuneConfigUnavailableMessage();
    retuneStage = 'idle';
    dom.retuneStatusText.textContent = message;
    setRetuneProgress(0, 'Not started');
    setRetuneStageUi(retuneStage);
    appendLog(dom, { level: 'error', message });
    updateUiState();
    return;
  }
  const previousActiveState = captureRetunePreviousActiveState();
  const selectedTargetSeedHz = resolveRetuneTargetSeedHz(selectedTarget);

  retuneRequireButtonReleaseForAutoStart = true;
  activeOperation = 'retune';
  abortController = new AbortController();
  clearRetuneUploadDraft();
  let sweepControlModeEnabled = false;
  let retuneFactoryInfo: Record<string, string> = {};
  let retuneKnifeSnapshotBefore: KnifeSnapshot | null = null;
  retuneLockedTarget = selectedTarget;
  retuneStage = 'running';
  retuneComparisonState = null;
  retuneChart.reset();
  renderRetuneChartLegend(null);
  const runRange = resolveRetuneChartRunRange(selectedTarget);
  if (runRange) {
    retuneChart.setSweepRange(
      Math.min(runRange.minHz, previousActiveState.centerHz - 500),
      Math.max(runRange.maxHz, previousActiveState.centerHz + 500)
    );
  }
  renderRetuneComparisonState();
  setRetuneStageUi(retuneStage);
  dom.retuneStatusText.textContent = `Preparing retune for ${selectedTarget.description}...`;
  dom.retuneHoldBanner.hidden = true;
  setRetuneProgress(10, 'Preparing...');
  retuneChart.setInitialFrequency(previousActiveState.centerHz);
  renderRetuneChartLegend(previousActiveState.centerHz);
  updateUiState();

  try {
    await transport.unlockCommandChannel(COMMAND_UNLOCK_PASSPHRASE);
    try {
      const { captureKnifeSnapshot, collectFactoryInfo } = await import('./firebase/capture');
      retuneFactoryInfo = await collectFactoryInfo(transport, {
        onDebug: (message, details) => {
          sweepDebug('Retune', message, details);
        },
      });
      retuneKnifeSnapshotBefore = await captureKnifeSnapshot(transport, {
        factoryInfo: retuneFactoryInfo,
        refreshMissingStatus: true,
        onDebug: (message, details) => {
          sweepDebug('Retune', message, details);
        },
      });
    } catch (error) {
      sweepDebug('Retune', 'Could not capture pre-retune upload snapshot', {
        error: getErrorMessage(error),
      });
    }
    const preparedSelection = await applyRetuneTargetFrequency(
      selectedTarget.target_id,
      selectedTargetSeedHz
    );
    appendLog(dom, {
      level: 'info',
      message:
        `Prepared ${selectedTarget.description} at ${formatFrequencyHz(selectedTargetSeedHz)} Hz ` +
        `before retune (${preparedSelection.mode}).`,
    });
    sweepDebug('Retune', 'Prepared selected target before retune sweep', {
      selectedTargetId: selectedTarget.target_id,
      selectedTargetSeedHz,
      previousTargetId: previousActiveState.targetId,
      previousCenterHz: previousActiveState.centerHz,
      command: preparedSelection.command,
      mode: preparedSelection.mode,
      fallbackReason: preparedSelection.fallbackReason,
    });
    await enterSweepControlMode('retune');
    sweepControlModeEnabled = true;

    const result = await runRetune(
      transport,
      {
        hardwareConfig,
        hardwareRevision,
        selectedTargetId: selectedTarget.target_id,
        startingCenterHz: selectedTargetSeedHz,
      },
      {
        signal: abortController.signal,
        onStatus: (message) => {
          dom.retuneStatusText.textContent = message;
        },
        onPhaseChange: (phase) => {
          if (retuneStage !== 'paused') {
            const progress = getRetunePhaseProgress(phase, false);
            setRetuneProgress(progress.percent, progress.label);
          }
          sweepDebug('Retune', 'Retune phase changed', { phase });
        },
        onPauseChange: (phase, paused) => {
          handleRetuneSweepPauseChange(phase, paused);
        },
        shouldPause: () => retuneButtonPressed !== true,
        onInitialFrequencyResolved: (hz) => {
          sweepDebug('Retune', 'Resolved retune sweep seed frequency', { seedCenterHz: hz });
        },
        onSweepPoint: (phase, point) => {
          retuneChart.addPoint(phase, point);
        },
        onSuggestedFrequencyResolved: (hz) => {
          retuneChart.setFinalFrequency(hz);
        },
        onDebug: (message, details) => {
          sweepDebug('Retune', message, details);
        },
      }
    );

    await restoreAutotuneButtonMode('retune');
    sweepControlModeEnabled = false;
    dom.retuneHoldBanner.hidden = true;

    renderRetuneChartFromResult(result, previousActiveState.centerHz);
    showRetuneResult(result, previousActiveState.centerHz);
    if (retuneKnifeSnapshotBefore) {
      retuneUploadDraft = {
        result,
        knifeSnapshot: retuneKnifeSnapshotBefore,
      };
    }
    await enterRetuneCompareStage(result, previousActiveState);
    sweepDebug('Retune', 'Retune result', {
      revisionId: result.revisionId,
      targetId: result.targetId,
      originalTargetId: previousActiveState.targetId,
      previousTargetId: previousActiveState.targetId,
      previousCenterHz: previousActiveState.centerHz,
      selectedTargetSeedHz,
      originalCenterHz: result.originalCenterHz,
      coarsePeakHz: result.coarsePeakHz,
      finePeakHz: result.finePeakHz,
    });
  } catch (error) {
    const aborted = abortController?.signal.aborted;
    dom.retuneHoldBanner.hidden = true;

    if (aborted) {
      retuneStage = 'armed';
      retuneComparisonState = null;
      clearRetuneUploadDraft();
      retuneLockedTarget = null;
      renderRetuneComparisonState();
      restoreRetunePreRunChartPreview();
      setRetuneStageUi(retuneStage);
      dom.retuneStatusText.textContent = 'Retune canceled. Press and hold the knife button to retune again.';
      appendLog(dom, { level: 'error', message: 'Retune canceled.' });
      setRetuneProgress(0, 'Waiting for button hold');
      sweepDebug('Retune', 'Retune canceled', {
        detectedHardwareRevision: hardwareRevision,
      });
    } else {
      retuneStage = 'armed';
      retuneComparisonState = null;
      clearRetuneUploadDraft();
      retuneLockedTarget = null;
      renderRetuneComparisonState();
      setRetuneStageUi(retuneStage);
      const message = mapRetuneErrorMessage(error, hardwareRevision);
      dom.retuneStatusText.textContent = message;
      appendLog(dom, { level: 'error', message });
      setRetuneProgress(0, 'Waiting for button hold');
      sweepDebug('Retune', 'Retune failed', {
        detectedHardwareRevision: hardwareRevision,
        error: getErrorMessage(error),
      });
    }
  } finally {
    if (sweepControlModeEnabled) {
      await restoreAutotuneButtonMode('retune');
    }
    activeOperation = 'none';
    abortController = null;
    updateUiState();
  }
}

async function enterRetuneCompareStage(
  result: RetuneResult,
  previousActiveState: RetuneActiveState
): Promise<void> {
  const originalTargetId = previousActiveState.targetId;
  const originalHz = Math.round(previousActiveState.centerHz);
  const updatedHz = Math.round(result.finePeakHz);
  const targetName = getRetuneTargetDisplayName(result.targetId);
  const targetChanged = originalTargetId != null && originalTargetId !== result.targetId;

  if (!targetChanged && originalHz === updatedHz) {
    const appliedSelection = await applyResolvedRetuneSelection(
      {
        originalTargetId,
        targetId: result.targetId,
        originalHz,
        updatedHz,
      },
      'original'
    );
    const savedAtIso = new Date().toISOString();
    retuneNoChangeResult = true;
    retuneComparisonState = null;
    renderRetuneComparisonState();
    retuneStage = 'complete';
    setRetuneStageUi(retuneStage);
    setRetuneProgress(100, 'Retune complete');
    dom.retuneCompareMessage.classList.remove('panel-sub--muted');
    dom.retuneCompareMessage.classList.add('panel-sub--success');
    const appliedTargetName = getAppliedRetuneTargetDisplayName(appliedSelection.targetId);
    dom.retuneCompareMessage.textContent =
      appliedSelection.targetId === result.targetId
        ? `Tuning complete for ${targetName}! Your knife is already tuned to its best available frequency.`
        : `Tuning complete. Your knife remains on ${appliedTargetName} at its best available frequency.`;
    dom.retuneStatusText.classList.remove('panel-sub--success');
    dom.retuneStatusText.classList.add('panel-sub--muted');
    dom.retuneStatusText.textContent = '';
    dom.retuneHoldBanner.hidden = true;
    appendLog(dom, {
      level: 'success',
      message:
        `Retune complete (${result.revisionId}/${appliedSelection.targetId} - ` +
        `${appliedTargetName}). Frequency remains at ${formatFrequencyHz(originalHz)} Hz.`,
    });
    sweepDebug('Retune', 'Applied no-change retune result', {
      originalTargetId,
      previousTargetId: previousActiveState.targetId,
      previousCenterHz: previousActiveState.centerHz,
      updatedTargetId: result.targetId,
      retuneSeedHz: result.originalCenterHz,
      frequencyHz: appliedSelection.frequencyHz,
      command: appliedSelection.command,
      mode: appliedSelection.mode,
      fallbackReason: appliedSelection.fallbackReason,
    });
    retuneKnownActiveState = {
      targetId: appliedSelection.targetId,
      centerHz: appliedSelection.frequencyHz,
    };
    trackRetuneTargetAttemptSaved({
      beforeTargetId: originalTargetId,
      selectedTargetId: result.targetId,
      appliedTargetId: appliedSelection.targetId,
      attemptKind: 'legacy_sweep',
    });
    void queueRetuneUpload({
      beforeTargetId: originalTargetId,
      selectedTargetId: result.targetId,
      appliedTargetId: appliedSelection.targetId,
      beforeCenterHz: originalHz,
      appliedHz: originalHz,
      appliedCenterHz: appliedSelection.frequencyHz,
      userDecision: 'no_change',
      persistedToKnife: true,
      savedAtIso,
    });
    return;
  }

  retuneNoChangeResult = false;
  retuneComparisonState = {
    originalTargetId,
    originalHz,
    updatedHz,
    selected: 'updated',
    persisted: false,
    revisionId: result.revisionId,
    targetId: result.targetId,
    previousActiveState,
  };

  renderRetuneComparisonState();
  retuneStage = 'compare';
  setRetuneStageUi(retuneStage);
  setRetuneProgress(100, 'Compare frequencies');
  await applyRetuneSelection('updated');
  retuneChart.setSelectedMarker('updated');
  dom.retuneCompareMessage.classList.remove('panel-sub--success');
  dom.retuneCompareMessage.classList.add('panel-sub--muted');
  dom.retuneCompareMessage.textContent = buildRetuneCompareMessageText('updated');
  dom.retuneStatusText.textContent = '';
  dom.retuneHoldBanner.hidden = true;
  appendLog(dom, {
    level: 'info',
    message:
      `Retune sweep finished (${result.revisionId}/${result.targetId} - ${targetName}). ` +
      `Compare original ${formatFrequencyHz(originalHz)} Hz and updated ${formatFrequencyHz(updatedHz)} Hz, then save your choice.`,
  });
}

function buildRetuneCompareMessageText(selected: RetuneSelection): string {
  if (!retuneComparisonState) {
    return 'Compare frequencies, then save your choice.';
  }

  const selectedHz =
    selected === 'original' ? retuneComparisonState.originalHz : retuneComparisonState.updatedHz;
  const selectedLabel = selected === 'original' ? 'Original' : 'Updated';
  return `${selectedLabel} frequency ${formatFrequencyHz(selectedHz)} Hz is active. Press the knife button to try it.`;
}

async function handleRetuneSelectionChange(selection: RetuneSelection): Promise<void> {
  if (retuneStage !== 'compare' || !retuneComparisonState) {
    return;
  }

  const previousSelection = retuneComparisonState.selected;
  try {
    await applyRetuneSelection(selection);
    retuneChart.setSelectedMarker(selection);
    dom.retuneCompareMessage.textContent = buildRetuneCompareMessageText(selection);
    dom.retuneStatusText.textContent = '';
    appendLog(dom, {
      level: 'info',
      message: `Applied ${selection} frequency ${formatFrequencyHz(getRetuneSelectionFrequency(selection))} Hz for comparison.`,
    });
  } catch (error) {
    retuneComparisonState.selected = previousSelection;
    renderRetuneComparisonState();
    retuneChart.setSelectedMarker(previousSelection);
    const message = `Failed to apply ${selection} frequency: ${getErrorMessage(error)}`;
    dom.retuneCompareMessage.textContent = message;
    dom.retuneStatusText.textContent = '';
    appendLog(dom, { level: 'error', message });
  } finally {
    updateUiState();
  }
}

async function saveRetuneSelection(): Promise<void> {
  if (retuneStage !== 'compare' || !retuneComparisonState || activeOperation !== 'none') {
    return;
  }

  activeOperation = 'retune';
  retuneStage = 'saving';
  setRetuneStageUi(retuneStage);
  setRetuneProgress(100, 'Saving selected frequency...');
  updateUiState();

  try {
    const appliedSelection = await applyRetuneSelection(retuneComparisonState.selected);
    retuneComparisonState.persisted = true;
    retuneStage = 'complete';
    setRetuneStageUi(retuneStage);
    setRetuneProgress(100, 'Retune complete');
    retuneChart.setSelectedMarker(retuneComparisonState.selected);

    const savedHz = appliedSelection.frequencyHz;
    if (retuneComparisonState.selected === 'updated') {
      dom.retuneCompareMessage.textContent = `Saved: Updated frequency ${formatFrequencyHz(savedHz)} Hz.`;
    } else {
      dom.retuneCompareMessage.textContent = `Saved: Original frequency ${formatFrequencyHz(savedHz)} Hz kept.`;
    }
    dom.retuneStatusText.textContent =
      'Retune complete. You can return home to choose another task, or close this window.';

    appendLog(dom, {
      level: 'success',
      message:
        `Retune choice saved (${retuneComparisonState.revisionId}/${appliedSelection.targetId} - ` +
        `${getAppliedRetuneTargetDisplayName(appliedSelection.targetId)}): ` +
        `${retuneComparisonState.selected} ${formatFrequencyHz(savedHz)} Hz.`,
    });

    const savedAtIso = new Date().toISOString();
    const userDecision = resolveRetuneUserDecision(
      retuneComparisonState.originalHz,
      retuneComparisonState.updatedHz,
      savedHz
    );
    retuneKnownActiveState = {
      targetId: appliedSelection.targetId,
      centerHz: savedHz,
    };
    trackRetuneTargetAttemptSaved({
      beforeTargetId: retuneComparisonState.originalTargetId,
      selectedTargetId: retuneComparisonState.targetId,
      appliedTargetId: appliedSelection.targetId,
      attemptKind: 'legacy_sweep',
    });
    void queueRetuneUpload({
      beforeTargetId: retuneComparisonState.originalTargetId,
      selectedTargetId: retuneComparisonState.targetId,
      appliedTargetId: appliedSelection.targetId,
      beforeCenterHz: retuneComparisonState.originalHz,
      appliedHz: savedHz,
      appliedCenterHz: savedHz,
      userDecision,
      persistedToKnife: true,
      savedAtIso,
    });
  } catch (error) {
    retuneStage = 'compare';
    setRetuneStageUi(retuneStage);
    setRetuneProgress(100, 'Compare frequencies');
    const message = `Failed to save selected frequency: ${getErrorMessage(error)}`;
    dom.retuneCompareMessage.textContent = message;
    dom.retuneStatusText.textContent = '';
    appendLog(dom, { level: 'error', message });
  } finally {
    activeOperation = 'none';
    updateUiState();
  }
}

type AppliedRetuneSelection = SetActiveTuningTargetResult & {
  targetId: string | null;
  frequencyHz: number;
};

async function applyRetuneSelection(selection: RetuneSelection): Promise<AppliedRetuneSelection> {
  if (!retuneComparisonState) {
    throw new Error('Retune comparison state is unavailable.');
  }

  const commandResult = await applyResolvedRetuneSelection(retuneComparisonState, selection);
  retuneComparisonState.selected = selection;
  renderRetuneComparisonState();
  sweepDebug('Retune', 'Applied comparison frequency', {
    selection,
    targetId: commandResult.targetId,
    frequencyHz: commandResult.frequencyHz,
    command: commandResult.command,
    mode: commandResult.mode,
    fallbackReason: commandResult.fallbackReason,
  });
  return commandResult;
}

function getRetuneSelectionFrequency(selection: RetuneSelection): number {
  if (!retuneComparisonState) {
    return 0;
  }
  return selection === 'original' ? retuneComparisonState.originalHz : retuneComparisonState.updatedHz;
}

function clearRetuneUploadDraft(): void {
  retuneUploadDraft = null;
}

function resolveRetuneUserDecision(
  originalHz: number,
  suggestedHz: number,
  appliedHz: number
): UserDecision {
  if (originalHz === suggestedHz) {
    return 'no_change';
  }

  return appliedHz === suggestedHz ? 'accept_suggested' : 'keep_original';
}

type RetuneTargetAttemptKind = 'legacy_sweep' | 'target_autotune';

type RetuneTargetAttemptAnalyticsInput = {
  attemptKind: RetuneTargetAttemptKind;
  beforeTargetId: string | null;
  selectedTargetId: string;
  appliedTargetId: string | null;
};

type RetuneTargetAttemptUploadQueueInput = {
  attemptKind: RetuneTargetAttemptKind;
  beforeState: RetuneActiveState;
  selectedTargetId: string;
  appliedTargetId: string | null;
  appliedCenterHz: number | null;
  savedAtIso: string;
  userDecision: UserDecision | null;
  runtime: RetuneRuntimeSummary | null;
};

function resolvePreparedRetuneAttemptBeforeState(targetId: string): RetuneActiveState {
  if (retunePreparedTargetId === targetId && retunePreparedAttemptBeforeState) {
    return retunePreparedAttemptBeforeState;
  }

  return captureRetunePreviousActiveState();
}

function trackRetuneTargetAttemptSaved(input: RetuneTargetAttemptAnalyticsInput): void {
  trackEvent('retune_target_attempt_saved', {
    attempt_kind: input.attemptKind,
    before_target_id: input.beforeTargetId ?? 'unknown',
    selected_target_id: input.selectedTargetId,
    applied_target_id: input.appliedTargetId ?? 'unknown',
    target_changed: input.beforeTargetId !== input.appliedTargetId,
  });
}

function buildRetuneRuntimeSummary(event: AutotuneRunResultEvent): RetuneRuntimeSummary {
  return {
    firmwareRunId: event.runId,
    reachedTrack: event.reachedTrack,
    timeToTrackMs: event.timeToTrackMs,
    trackFreqHz: event.trackFreqHz,
    trackPowerW: event.trackPowerW,
    sessionPeakFreqHz: event.sessionPeakFreqHz,
    sessionPeakPowerW: event.sessionPeakPowerW,
    raw: event.raw,
  };
}

function resolveCurrentRetuneMatchedRevisionId(): string | null {
  if (!hardwareConfig) {
    return null;
  }

  try {
    return resolveRetuneRevision({
      config: hardwareConfig,
      revision: transport.getDeviceInfo()?.hardwareRevision ?? null,
    }).revision_id;
  } catch {
    return null;
  }
}

async function captureRetuneAttemptKnifeSnapshot(): Promise<KnifeSnapshot> {
  const { captureKnifeSnapshot } = await import('./firebase/capture');
  return captureKnifeSnapshot(transport, {
    factoryInfo: {},
    refreshMissingStatus: false,
    onDebug: (message, details) => {
      sweepDebug('Retune', message, details);
    },
  });
}

async function queueRetuneTargetAttemptUpload(
  input: RetuneTargetAttemptUploadQueueInput
): Promise<void> {
  const uploadParams = {
    run_type: 'retune_target_attempt',
    attempt_kind: input.attemptKind,
    target_count: 1,
    before_target_id: input.beforeState.targetId ?? 'unknown',
    selected_target_id: input.selectedTargetId,
    applied_target_id: input.appliedTargetId ?? 'unknown',
    target_changed: input.beforeState.targetId !== input.appliedTargetId,
  };
  let stage: 'capture' | 'write' = 'capture';

  trackEvent('firebase_upload_started', uploadParams);

  try {
    const startedAtMs = Date.now();
    const knifeSnapshot = await captureRetuneAttemptKnifeSnapshot();
    const { uploadRetuneTargetAttempt } = await import('./firebase/uploads');
    stage = 'write';
    const result = await uploadRetuneTargetAttempt({
      knifeSnapshot,
      matchedRevisionId: resolveCurrentRetuneMatchedRevisionId(),
      attemptKind: input.attemptKind,
      sourceRunId: null,
      beforeTargetId: input.beforeState.targetId,
      selectedTargetId: input.selectedTargetId,
      appliedTargetId: input.appliedTargetId,
      beforeCenterHz: input.beforeState.centerHz,
      appliedCenterHz: input.appliedCenterHz,
      savedAtIso: input.savedAtIso,
      userDecision: input.userDecision,
      runtime: input.runtime,
    });

    if (result.status === 'skipped') {
      trackEvent('firebase_upload_skipped', {
        ...uploadParams,
        reason: result.reason,
      });
      sweepDebug('Retune', 'Retune target attempt Firebase upload skipped', {
        reason: result.reason,
      });
      return;
    }

    trackEvent('firebase_upload_result', {
      ...uploadParams,
      outcome: 'success',
      latency_ms: Date.now() - startedAtMs,
    });
    sweepDebug('Retune', 'Retune target attempt Firebase upload succeeded', {
      attemptId: result.attemptId,
      selectedTargetId: input.selectedTargetId,
      appliedTargetId: input.appliedTargetId,
    });
  } catch (error) {
    trackError('firebase_upload_error', error, {
      ...uploadParams,
      stage,
    });
    sweepDebug('Retune', 'Retune target attempt Firebase upload failed', {
      stage,
      selectedTargetId: input.selectedTargetId,
      appliedTargetId: input.appliedTargetId,
      error: getErrorMessage(error),
    });
  }
}

async function queueDiagnosticUpload(report: DiagnosticReport): Promise<void> {
  const diagnosticKnifeSnapshot = diagnosticUploadSnapshot;
  diagnosticUploadSnapshot = null;

  if (!diagnosticKnifeSnapshot) {
    return;
  }

  const uploadParams = {
    run_type: 'diagnostic',
    sweep_count: report.summary.completedTargets,
    point_count: report.summary.totalPointCount,
    target_count: report.summary.completedTargets,
  };
  let stage: 'prepare' | 'write' = 'prepare';

  trackEvent('firebase_upload_started', uploadParams);

  try {
    const startedAtMs = Date.now();
    const { uploadDiagnosticRun } = await import('./firebase/uploads');
    stage = 'write';
    const result = await uploadDiagnosticRun({
      report,
      knifeSnapshot: diagnosticKnifeSnapshot,
    });

    if (result.status === 'skipped') {
      trackEvent('firebase_upload_skipped', {
        ...uploadParams,
        reason: result.reason,
      });
      diagnosticsDebug('Diagnostic Firebase upload skipped', {
        reason: result.reason,
      });
      return;
    }

    trackEvent('firebase_upload_result', {
      ...uploadParams,
      outcome: 'success',
      latency_ms: Date.now() - startedAtMs,
    });
    diagnosticsDebug('Diagnostic Firebase upload succeeded', {
      runId: result.runId,
      sweepCount: result.sweepCount,
      totalPointCount: result.totalPointCount,
    });
  } catch (error) {
    trackError('firebase_upload_error', error, {
      ...uploadParams,
      stage,
    });
    diagnosticsDebug('Diagnostic Firebase upload failed', {
      stage,
      error: getErrorMessage(error),
    });
  }
}

async function queueRetuneUpload(input: {
  beforeTargetId: string | null;
  selectedTargetId: string;
  appliedTargetId: string | null;
  beforeCenterHz: number | null;
  appliedHz: number;
  appliedCenterHz: number | null;
  userDecision: UserDecision;
  persistedToKnife: boolean;
  savedAtIso: string;
}): Promise<void> {
  const draft = retuneUploadDraft;
  clearRetuneUploadDraft();

  if (!draft) {
    return;
  }

  const uploadParams = {
    run_type: 'retune',
    sweep_count: 2,
    point_count: draft.result.coarseSweep.points.length + draft.result.fineSweep.points.length,
    target_count: 1,
    user_decision: input.userDecision,
    before_target_id: input.beforeTargetId ?? 'unknown',
    selected_target_id: input.selectedTargetId,
    applied_target_id: input.appliedTargetId ?? 'unknown',
    target_changed: input.beforeTargetId !== input.appliedTargetId,
  };
  let stage: 'prepare' | 'write' = 'prepare';

  trackEvent('firebase_upload_started', uploadParams);

  try {
    const startedAtMs = Date.now();
    const { uploadRetuneRun } = await import('./firebase/uploads');
    stage = 'write';
    const result = await uploadRetuneRun({
      result: draft.result,
      knifeSnapshot: draft.knifeSnapshot,
      beforeTargetId: input.beforeTargetId,
      selectedTargetId: input.selectedTargetId,
      appliedTargetId: input.appliedTargetId,
      beforeCenterHz: input.beforeCenterHz,
      appliedHz: input.appliedHz,
      appliedCenterHz: input.appliedCenterHz,
      userDecision: input.userDecision,
      persistedToKnife: input.persistedToKnife,
      savedAtIso: input.savedAtIso,
    });

    if (result.status === 'skipped') {
      trackEvent('firebase_upload_skipped', {
        ...uploadParams,
        reason: result.reason,
      });
      sweepDebug('Retune', 'Retune Firebase upload skipped', {
        reason: result.reason,
      });
      return;
    }

    trackEvent('firebase_upload_result', {
      ...uploadParams,
      outcome: 'success',
      latency_ms: Date.now() - startedAtMs,
    });
    sweepDebug('Retune', 'Retune Firebase upload succeeded', {
      runId: result.runId,
      sweepCount: result.sweepCount,
      totalPointCount: result.totalPointCount,
      userDecision: input.userDecision,
    });
  } catch (error) {
    trackError('firebase_upload_error', error, {
      ...uploadParams,
      stage,
    });
    sweepDebug('Retune', 'Retune Firebase upload failed', {
      stage,
      userDecision: input.userDecision,
      error: getErrorMessage(error),
    });
  }
}

function hasUnsavedRetuneSelection(): boolean {
  return (
    retuneComparisonState != null &&
    retuneStage !== 'complete' &&
    retuneComparisonState.persisted === false
  );
}

async function revertUnsavedRetuneSelection(reason: string): Promise<void> {
  if (!hasUnsavedRetuneSelection() || !retuneComparisonState) {
    return;
  }

  const previousActiveState = retuneComparisonState.previousActiveState;
  if (!transport.isConnected() || !transport.getCapabilities().command) {
    appendLog(dom, {
      level: 'warning',
      message: `Leaving retune compare (${reason}) without saving. Could not restore the previous knife state because the knife is disconnected.`,
    });
    return;
  }

  try {
    const commandResult = await applyRetuneTargetFrequency(
      previousActiveState.targetId,
      previousActiveState.centerHz
    );
    sweepDebug('Retune', 'Reverted unsaved retune selection', {
      reason,
      targetId: commandResult.targetId,
      previousCenterHz: commandResult.frequencyHz,
      command: commandResult.command,
      mode: commandResult.mode,
      fallbackReason: commandResult.fallbackReason,
    });
    retuneKnownActiveState = {
      targetId: commandResult.targetId,
      centerHz: commandResult.frequencyHz,
    };
    appendLog(dom, {
      level: 'info',
      message: `Restored previous knife state at ${formatFrequencyHz(previousActiveState.centerHz)} Hz before leaving compare (${reason}).`,
    });
  } catch (error) {
    appendLog(dom, {
      level: 'warning',
      message: `Could not revert unsaved retune selection before leaving compare: ${getErrorMessage(error)}`,
    });
  }
}

function showRetuneResult(result: RetuneResult, originalHz = result.originalCenterHz): void {
  const delta = result.finePeakHz - originalHz;
  dom.devRetuneOriginalCenter.textContent = `${formatFrequencyHz(originalHz)} Hz`;
  dom.devRetuneCoarsePeak.textContent = `${formatFrequencyHz(result.coarsePeakHz)} Hz`;
  dom.devRetuneFinePeak.textContent = `${formatFrequencyHz(result.finePeakHz)} Hz`;
  dom.devRetuneDelta.textContent = `${delta >= 0 ? '+' : ''}${formatFrequencyHz(Math.abs(delta))} Hz`;
}

async function applyResolvedRetuneSelection(
  state: Pick<RetuneComparisonState, 'originalTargetId' | 'targetId' | 'originalHz' | 'updatedHz'>,
  selection: RetuneSelection
): Promise<AppliedRetuneSelection> {
  const resolved = resolveRetuneSelectionCommand(
    {
      originalTargetId: state.originalTargetId,
      updatedTargetId: state.targetId,
      originalHz: state.originalHz,
      updatedHz: state.updatedHz,
    },
    selection
  );

  return applyRetuneTargetFrequency(resolved.targetId, resolved.frequencyHz);
}

async function applyRetuneTargetFrequency(
  targetId: string | null,
  frequencyHz: number
): Promise<AppliedRetuneSelection> {
  const roundedFrequencyHz = Math.round(frequencyHz);

  await transport.unlockCommandChannel(COMMAND_UNLOCK_PASSPHRASE);
  await setDriveMode('autotune');

  if (!targetId) {
    await transport.sendTextCommand(`SetCenterFrequency ${roundedFrequencyHz}`);
    return {
      command: 'SetCenterFrequency',
      mode: 'legacy-center-frequency',
      targetId: null,
      centerHz: roundedFrequencyHz,
      frequencyHz: roundedFrequencyHz,
      fallbackReason: 'No tuning target id was available.',
      response: null,
    };
  }

  const commandResult = await transport.setActiveTuningTargetWithFallback(
    targetId,
    roundedFrequencyHz
  );

  return {
    ...commandResult,
    targetId: commandResult.targetId ?? targetId,
    frequencyHz: roundedFrequencyHz,
  };
}

async function startDiagnostics(): Promise<void> {
  if (activeOperation !== 'none' || selectedFlow !== 'diagnostics') {
    return;
  }

  if (DEV_CONTROLS_ENABLED && devDiagnosticsMode !== 'live') {
    await triggerDevDiagnosticsOutcome(devDiagnosticsMode === 'success' ? 'success' : 'failed');
    return;
  }

  const capabilities = transport.getCapabilities();
  if (!capabilities.command) {
    diagnosticsStage = 'idle';
    setDiagnosticsStageUi(diagnosticsStage);
    setDiagnosticsProgress(0, 'Not started');
    dom.diagnosticsStatusText.textContent = 'Diagnostics are not supported on this connected firmware.';
    updateUiState();
    return;
  }

  if (!capabilities.settingsStatus) {
    diagnosticsStage = 'idle';
    setDiagnosticsStageUi(diagnosticsStage);
    setDiagnosticsProgress(0, 'Not started');
    dom.diagnosticsStatusText.textContent = buildDiagnosticsButtonTelemetryUnavailableMessage();
    updateUiState();
    return;
  }

  if (diagnosticsButtonPressed !== true || diagnosticsStage !== 'armed') {
    diagnosticsStage = 'armed';
    setDiagnosticsStageUi(diagnosticsStage);
    setDiagnosticsProgress(0, 'Waiting for button hold');
    dom.diagnosticsStatusText.textContent = '';
    dom.diagnosticsHoldBanner.hidden = true;
    updateUiState();
    return;
  }

  if (!hardwareConfig) {
    diagnosticsStage = 'idle';
    setDiagnosticsStageUi(diagnosticsStage);
    setDiagnosticsProgress(0, 'Not started');
    dom.diagnosticsStatusText.textContent = buildDiagnosticsConfigUnavailableMessage();
    updateUiState();
    return;
  }

  diagnosticsRequireButtonReleaseForAutoStart = true;
  activeOperation = 'diagnostics';
  abortController = new AbortController();
  let sweepControlModeEnabled = false;
  diagnosticsStage = 'running';
  setDiagnosticsStageUi(diagnosticsStage);
  diagnosticReport = null;
  diagnosticUploadSnapshot = null;
  dom.diagnosticsSuccessMessage.textContent = '';
  dom.diagnosticsSuccessMessage.hidden = true;
  dom.downloadDiagnosticsButton.disabled = true;
  dom.diagnosticsSummary.textContent = '';
  dom.diagnosticsSummary.hidden = true;
  dom.diagnosticsHoldBanner.hidden = true;
  dom.diagnosticsHoldBanner.textContent = '';
  diagnosticsActiveTargetProgress = null;
  diagnosticsActiveTargetPercent = null;
  dom.diagnosticsStatusText.textContent = 'Preparing diagnostics...';
  setDiagnosticsProgress(10, 'Preparing...');
  updateUiState();

  try {
    diagnosticsDebug('Start diagnostics clicked', {
      hasCommandCapability: transport.getCapabilities().command,
      hasSettingsStatusCapability: transport.getCapabilities().settingsStatus,
      isConnected: transport.isConnected(),
      selectedFlow,
      stage: diagnosticsStage,
    });

    await transport.unlockCommandChannel(COMMAND_UNLOCK_PASSPHRASE);
    try {
      const { captureKnifeSnapshot, collectFactoryInfo } = await import('./firebase/capture');
      const diagnosticFactoryInfo = await collectFactoryInfo(transport, {
        onDebug: diagnosticsDebug,
      });
      diagnosticUploadSnapshot = await captureKnifeSnapshot(transport, {
        factoryInfo: diagnosticFactoryInfo,
        refreshMissingStatus: true,
        onDebug: diagnosticsDebug,
      });
    } catch (error) {
      diagnosticsDebug('Could not capture pre-diagnostics upload snapshot', {
        error: getErrorMessage(error),
      });
    }
    await enterSweepControlMode('diagnostics');
    sweepControlModeEnabled = true;
    diagnosticsDebug('Command channel unlocked');

    const report = await runDiagnosticReport(
      transport,
      {
        hardwareConfig,
        hardwareRevision: transport.getDeviceInfo()?.hardwareRevision ?? null,
      },
      {
        signal: abortController.signal,
        obfuscationSecret: getDiagnosticsObfuscationSecret(),
        obfuscationKeyId: DIAGNOSTIC_OBFUSCATION_KEY_ID,
        onStatus: (message) => {
          diagnosticsDebug('Diagnostic status update', { message });
          if (diagnosticsStage !== 'paused') {
            dom.diagnosticsStatusText.textContent = message;
            const targetMatch = message.match(/^Target\s+(\d+)\/(\d+)/i);
            if (targetMatch) {
              const targetProgress = `${targetMatch[1]}/${targetMatch[2]}`;
              if (diagnosticsActiveTargetProgress !== targetProgress) {
                diagnosticsActiveTargetPercent = null;
              }
              diagnosticsActiveTargetProgress = targetProgress;
              dom.diagnosticsProgressLabel.textContent = buildDiagnosticsRunningProgressLabel(false);
            }
          }
          if (message.toLowerCase().includes('collecting knife details')) {
            setDiagnosticsProgress(20, 'Collecting knife details...');
          }
        },
        onTargetSweepPointProgress: (progress) => {
          diagnosticsActiveTargetProgress = `${progress.targetIndex}/${progress.totalTargets}`;
          diagnosticsActiveTargetPercent = progress.progressPercent;
          const percent = calculateDiagnosticsTargetSweepProgressPercent(
            progress.targetIndex,
            progress.totalTargets,
            progress.progressPercent
          );
          setDiagnosticsProgress(percent, buildDiagnosticsRunningProgressLabel(diagnosticsStage === 'paused'));
        },
        onTargetSweepProgress: (progress) => {
          diagnosticsActiveTargetPercent = null;
          const processedTargets =
            progress.completedTargets + progress.failedTargets + progress.skippedTargets;
          const ratio =
            progress.totalTargets > 0
              ? Math.min(1, processedTargets / progress.totalTargets)
              : 1;
          const percent = 25 + Math.round(ratio * 65);
          setDiagnosticsProgress(percent, buildDiagnosticsRunningProgressLabel(diagnosticsStage === 'paused'));
        },
        shouldPause: () => diagnosticsButtonPressed !== true,
        onPauseChange: (paused, details) => {
          handleDiagnosticsSweepPauseChange(paused, details);
        },
        onDebug: diagnosticsDebug,
      }
    );

    diagnosticReport = report;
    diagnosticsDebug('Diagnostic report ready', {
      fileName: report.fileName,
      totalTargets: report.summary.totalTargets,
      completedTargets: report.summary.completedTargets,
      failedTargets: report.summary.failedTargets,
      skippedTargets: report.summary.skippedTargets,
      totalPointCount: report.summary.totalPointCount,
    });
    void queueDiagnosticUpload(report);

    diagnosticsStage = 'complete';
    diagnosticsActiveTargetPercent = null;
    setDiagnosticsStageUi(diagnosticsStage);
    dom.diagnosticsStatusText.textContent = '';
    dom.downloadDiagnosticsButton.disabled = false;
    dom.diagnosticsSummary.textContent =
      `Completed ${report.summary.completedTargets}/${report.summary.totalTargets} target sweeps, ` +
      `${report.summary.totalPointCount} points`;
    dom.diagnosticsSummary.hidden = false;
    renderDiagnosticsCompleteMessage();
    setDiagnosticsProgress(100, 'Diagnostics complete');
    dom.downloadDiagnosticsButton.focus();

    appendLog(dom, {
      level: 'success',
      message:
        `Diagnostics complete. Targets: ${report.summary.completedTargets}/${report.summary.totalTargets} completed, ` +
        `${report.summary.failedTargets} failed, ${report.summary.skippedTargets} skipped. ` +
        `Points: ${report.summary.totalPointCount}.`,
    });
  } catch (error) {
    diagnosticsError('Diagnostics flow failed', error, {
      activeOperation,
      hasAbortController: Boolean(abortController),
      aborted: Boolean(abortController?.signal.aborted),
      selectedFlow,
    });

    const aborted = abortController?.signal.aborted;
    if (aborted) {
      diagnosticUploadSnapshot = null;
      diagnosticsStage = 'armed';
      diagnosticsActiveTargetProgress = null;
      diagnosticsActiveTargetPercent = null;
      setDiagnosticsStageUi(diagnosticsStage);
      dom.diagnosticsStatusText.textContent =
        'Diagnostics canceled. Press and hold the knife button to run diagnostics again.';
      dom.diagnosticsSummary.textContent = 'No report generated.';
      dom.diagnosticsSummary.hidden = false;
      setDiagnosticsProgress(0, 'Waiting for button hold');
      dom.diagnosticsHoldBanner.hidden = true;
      appendLog(dom, { level: 'error', message: 'Diagnostics canceled.' });
    } else {
      diagnosticUploadSnapshot = null;
      diagnosticsStage = 'armed';
      diagnosticsActiveTargetProgress = null;
      diagnosticsActiveTargetPercent = null;
      setDiagnosticsStageUi(diagnosticsStage);
      dom.diagnosticsStatusText.textContent = mapDiagnosticsErrorMessage(
        error,
        transport.getDeviceInfo()?.hardwareRevision ?? null
      );
      dom.diagnosticsSummary.textContent = 'No report generated.';
      dom.diagnosticsSummary.hidden = false;
      setDiagnosticsProgress(0, 'Waiting for button hold');
      dom.diagnosticsHoldBanner.hidden = true;
      appendLog(dom, {
        level: 'error',
        message: mapDiagnosticsErrorMessage(error, transport.getDeviceInfo()?.hardwareRevision ?? null),
      });
    }
  } finally {
    if (sweepControlModeEnabled) {
      await restoreAutotuneButtonMode('diagnostics');
    }
    activeOperation = 'none';
    abortController = null;
    updateUiState();
  }
}

function downloadDiagnosticReport(): void {
  if (!diagnosticReport) {
    return;
  }

  const blob = new Blob([diagnosticReport.json], { type: 'application/json;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = diagnosticReport.fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}

function downloadSupportLog(): void {
  const deviceInfo = transport.getDeviceInfo() ?? supportLastKnownKnife;
  appendSupportLog('support', 'support_log_downloaded', {
    connected: transport.isConnected(),
    serialNumber: deviceInfo?.serialNumber ?? null,
    selectedFlow,
    activeOperation,
    retuneStage,
    diagnosticsStage,
  });

  const json = buildSupportLogJson(buildSupportLogCurrentState());
  const blob = new Blob([json], { type: 'application/json;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = buildSupportLogFileName(deviceInfo?.serialNumber);
  anchor.click();
  URL.revokeObjectURL(url);
}

function buildSupportLogCurrentState(): Record<string, unknown> {
  const connectedDeviceInfo = transport.getDeviceInfo();
  const deviceInfo = connectedDeviceInfo ?? supportLastKnownKnife;
  return {
    connected: transport.isConnected(),
    canReconnect: transport.canReconnect(),
    connectedKnifeSnapshotSource: connectedDeviceInfo ? 'connected' : deviceInfo ? 'last-known' : 'none',
    connectedKnife: deviceInfo
      ? {
          name: deviceInfo.name,
          id: deviceInfo.id,
          serialNumber: deviceInfo.serialNumber ?? null,
          model: deviceInfo.model ?? null,
          manufacturer: deviceInfo.manufacturer ?? null,
          firmwareRevision: deviceInfo.firmwareRevision ?? null,
          hardwareRevision: deviceInfo.hardwareRevision ?? null,
        }
      : null,
    capabilities: transport.getCapabilities(),
    transportHealth: transport.getDiagnosticSnapshot(),
    latestSettingsStatus: transport.getLatestSettingsStatus(),
    latestPowerStatus: transport.getLatestPowerStatus(),
    latestTemperatureStatus: transport.getLatestTemperatureStatus(),
    selectedFlow,
    activeOperation,
    retuneStage,
    diagnosticsStage,
    currentStepIndex,
    otaReconnectPending,
    isAutoReconnectAttempt,
    reconnectPromptActive,
  };
}

async function fetchFirmware(update: ManifestUpdate, signal: AbortSignal): Promise<ArrayBuffer> {
  renderFirmwareUpdateStatus(update.version);
  appendLog(dom, { level: 'info', message: `Fetching ${update.url}` });

  const response = await fetch(update.url, { cache: 'no-store', signal });
  if (!response.ok) {
    throw new Error(`Firmware download failed (${response.status}).`);
  }

  const buffer = await response.arrayBuffer();

  if (update.size > 0 && buffer.byteLength !== update.size) {
    appendLog(dom, {
      level: 'error',
      message: `Size mismatch (expected ${update.size}, got ${buffer.byteLength}).`,
    });
  }

  if (update.sha256 && !/^[0]+$/.test(update.sha256)) {
    const hash = await sha256Hex(buffer);
    if (hash !== update.sha256.toLowerCase()) {
      throw new Error('SHA-256 checksum mismatch.');
    }
  }

  return buffer;
}

async function sha256Hex(buffer: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', buffer);
  const bytes = new Uint8Array(digest);
  return Array.from(bytes)
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

function setRetuneProgress(percent: number, label: string): void {
  const clamped = Math.max(0, Math.min(100, percent));
  dom.retuneProgressBar.style.width = `${clamped}%`;
  dom.retuneProgressLabel.textContent = label;
}

function setRetuneStageUi(stage: RetuneStage): void {
  if (stage !== supportLoggedRetuneStage) {
    supportLoggedRetuneStage = stage;
    appendSupportLog('retune', 'stage_changed', {
      stage,
      selectedTargetId: retuneSelectedTargetId,
      buttonPressed: retuneButtonPressed,
      buttonTelemetrySeen: retuneButtonTelemetrySeen,
      activeOperation,
      settingsStatus: transport.getLatestSettingsStatus(),
      serialNumber: transport.getDeviceInfo()?.serialNumber ?? null,
    });
  }
  const showInstruction = stage === 'idle' || stage === 'armed' || stage === 'running' || stage === 'paused';
  const showPrestartWarning = stage === 'armed';
  const hideProgress = isTargetAutotuneRetuneExperience() || stage === 'complete';
  const showNoChangeCompletePanel = retuneNoChangeResult && stage === 'complete';
  const showComparePanel =
    showNoChangeCompletePanel ||
    (retuneComparisonState != null && (stage === 'compare' || stage === 'saving' || stage === 'complete'));
  const showCompareChoices = retuneComparisonState != null;

  dom.retuneInstruction.hidden = !showInstruction;
  dom.retunePrestartWarning.hidden = !showPrestartWarning;
  dom.retuneProgress.hidden = hideProgress;
  dom.retuneComparePanel.hidden = !showComparePanel;
  dom.retuneCompareInstruction.hidden = !showCompareChoices;
  dom.retuneCompareOptions.hidden = !showCompareChoices;
  dom.saveRetuneChoiceButton.hidden = stage === 'complete' || !showCompareChoices;
  if (stage !== 'paused') {
    dom.retuneHoldBanner.hidden = true;
  }
}

function resetRetuneUiState(): void {
  retuneStage = 'idle';
  retuneComparisonState = null;
  clearRetuneUploadDraft();
  retuneNoChangeResult = false;
  retuneAutoStartInFlight = false;
  retuneRequireButtonReleaseForAutoStart = false;
  retuneTargetApplyInFlight = false;
  retunePendingTargetApplyId = null;
  retunePreparedTargetId = null;
  resetRetuneTargetAutotuneRuntime(false);
  resetRetuneTargetSelectionState();
  retuneChart.reset();
  retuneChart.setRuntimeMode(false);
  renderRetuneChartLegend(null);
  renderRetuneComparisonState();
  renderRetuneTargetSelection();
  dom.retuneCompareMessage.classList.remove('panel-sub--success');
  dom.retuneCompareMessage.classList.add('panel-sub--muted');
  dom.retuneCompareMessage.textContent = '';
  dom.retuneHoldBanner.hidden = true;
  dom.retuneHoldBanner.textContent = '';
  setRetuneStageUi(retuneStage);
  setRetuneProgress(0, 'Not started');
  resetRetuneStatusTextTreatment();
  dom.retuneStatusText.textContent = '';
  dom.retuneFirmwareBanner.hidden = true;
  dom.retuneRuntimeLegend.hidden = true;
  dom.retuneInstruction.textContent = buildRetuneInstructionText(null);
}

function renderRetuneComparisonState(): void {
  if (!retuneComparisonState) {
    dom.retuneChoiceOriginal.checked = false;
    dom.retuneChoiceUpdated.checked = false;
    dom.retuneChoiceOriginalLabel.textContent = 'Original: -- Hz';
    dom.retuneChoiceUpdatedLabel.textContent = 'Updated: -- Hz';
    retuneChart.setSelectedMarker(null);
    return;
  }

  dom.retuneChoiceOriginal.checked = retuneComparisonState.selected === 'original';
  dom.retuneChoiceUpdated.checked = retuneComparisonState.selected === 'updated';
  dom.retuneChoiceOriginalLabel.textContent = `Original: ${formatFrequencyHz(retuneComparisonState.originalHz)} Hz`;
  dom.retuneChoiceUpdatedLabel.textContent = `Updated: ${formatFrequencyHz(retuneComparisonState.updatedHz)} Hz`;
}

function setDiagnosticsProgress(percent: number, label: string): void {
  const clamped = Math.max(0, Math.min(100, percent));
  dom.diagnosticsProgressBar.style.width = `${clamped}%`;
  dom.diagnosticsProgressLabel.textContent = label;
}

function setDiagnosticsStageUi(stage: DiagnosticStage): void {
  if (stage !== supportLoggedDiagnosticsStage) {
    supportLoggedDiagnosticsStage = stage;
    appendSupportLog('diagnostics', 'stage_changed', {
      stage,
      buttonPressed: diagnosticsButtonPressed,
      buttonTelemetrySeen: diagnosticsButtonTelemetrySeen,
      activeOperation,
      settingsStatus: transport.getLatestSettingsStatus(),
      serialNumber: transport.getDeviceInfo()?.serialNumber ?? null,
    });
  }
  const showInstruction = stage === 'armed';
  const showPrestartWarning = stage === 'armed';
  const showCancelButton = stage === 'running' || stage === 'paused';
  const showProgress = stage === 'running' || stage === 'paused';
  const showSuccessMessage = stage === 'complete';
  const showHomeButton = stage === 'complete';
  const hasSummary = (dom.diagnosticsSummary.textContent?.trim().length ?? 0) > 0;

  dom.startDiagnosticsButton.hidden = true;
  dom.diagnosticsInstruction.hidden = !showInstruction;
  dom.diagnosticsPrestartWarning.hidden = !showPrestartWarning;
  dom.cancelDiagnosticsButton.hidden = !showCancelButton;
  dom.diagnosticsProgress.hidden = !showProgress;
  dom.diagnosticsSuccessMessage.hidden = !showSuccessMessage;
  dom.diagnosticsHomeButton.hidden = !showHomeButton;
  dom.diagnosticsSummary.hidden = !hasSummary || !(stage === 'complete' || stage === 'armed');

  if (stage !== 'paused') {
    dom.diagnosticsHoldBanner.hidden = true;
  }

  dom.downloadDiagnosticsButton.classList.toggle('button--primary', stage === 'complete');
  dom.downloadDiagnosticsButton.classList.toggle('button--ghost', stage !== 'complete');
}

function resetDiagnosticsUiState(): void {
  diagnosticsStage = 'idle';
  diagnosticsAutoStartInFlight = false;
  diagnosticsRequireButtonReleaseForAutoStart = false;
  diagnosticsActiveTargetProgress = null;
  diagnosticsActiveTargetPercent = null;
  diagnosticReport = null;
  diagnosticUploadSnapshot = null;
  dom.diagnosticsStatusText.textContent = '';
  dom.diagnosticsSummary.textContent = '';
  dom.diagnosticsSuccessMessage.textContent = '';
  dom.diagnosticsHoldBanner.textContent = '';
  setDiagnosticsProgress(0, 'Not started');
  setDiagnosticsStageUi(diagnosticsStage);
}

function resetFirmwareUiState(): void {
  updateOutcome = null;
  pendingUpdateOutcome = null;
  resetFirmwareProgress('Waiting for update start');
  dom.statusText.textContent = '';
  dom.statusText.removeAttribute('data-level');
}

function resetFirmwareProgress(message: string): void {
  if (updateOutcome) {
    return;
  }

  dom.progressSection.hidden = false;
  dom.progressBar.style.width = '0%';
  dom.progressPercent.textContent = '0%';
  dom.progressSpeed.textContent = '0 kB/s';
  dom.progressSpeed.hidden = false;
  dom.progressDetail.textContent = message;
  dom.progressMeta.hidden = false;
  dom.progressBar.parentElement?.setAttribute('aria-valuenow', '0');
}

function showUpdateOutcome(outcome: 'success' | 'failed', message: string): void {
  updateOutcome = outcome;

  if (outcome === 'success') {
    trackUpdateResult('success');
  } else {
    trackUpdateResult('failed', updateFailureStage ?? 'ota');
  }

  renderStatus(dom, {
    level: outcome === 'success' ? 'success' : 'error',
    message,
  });
  dom.progressSpeed.hidden = true;
  resetOtaReconnectDebugState();
  hideReconnectPrompt();
  refreshFirmwareFlowSummary();
}

function finalizeUpdateOutcome(): void {
  if (!pendingUpdateOutcome) {
    return;
  }

  const { outcome, message } = pendingUpdateOutcome;
  pendingUpdateOutcome = null;
  showUpdateOutcome(outcome, message);
}

function triggerDevUpdateOutcome(outcome: 'success' | 'failed'): void {
  if (abortController && !abortController.signal.aborted) {
    abortController.abort();
  }

  cancelOtaReconnect();
  pendingUpdateOutcome = null;
  updateOutcome = null;
  activeOperation = 'none';
  abortController = null;

  const message =
    outcome === 'success'
      ? 'Your knife is now ready to use. You can close this window, or go back and choose another task.'
      : 'Firmware update failed. Reconnect and try again.';

  showUpdateOutcome(outcome, message);
  updateUiState();
}

function triggerDevRetuneOutcome(outcome: 'success' | 'failed'): void {
  if (abortController && !abortController.signal.aborted) {
    abortController.abort();
  }

  syncRetuneTargetSelectionState();
  activeOperation = 'none';
  abortController = null;
  retuneStage = 'idle';
  retuneComparisonState = null;
  clearRetuneUploadDraft();
  retuneAutoStartInFlight = false;
  retuneRequireButtonReleaseForAutoStart = false;
  setRetuneStageUi(retuneStage);
  renderRetuneComparisonState();
  dom.retuneHoldBanner.hidden = true;
  dom.retuneHoldBanner.textContent = '';

  if (outcome === 'success') {
    retuneLockedTarget = getRetuneSelectedTarget();
    const previousActiveState = captureRetunePreviousActiveState();
    const originalHz = previousActiveState.centerHz;
    const selectedSeedHz = retuneLockedTarget
      ? resolveRetuneTargetSeedHz(retuneLockedTarget)
      : Math.round(transport.getDeviceInfo()?.centerFrequency ?? 40000);
    const updatedHz = Math.max(20000, Math.min(60000, selectedSeedHz + 36));

    renderDevRetuneChart(originalHz, updatedHz);
    dom.devRetuneOriginalCenter.textContent = `${formatFrequencyHz(originalHz)} Hz`;
    dom.devRetuneCoarsePeak.textContent = `${formatFrequencyHz(updatedHz)} Hz`;
    dom.devRetuneFinePeak.textContent = `${formatFrequencyHz(updatedHz)} Hz`;
    dom.devRetuneDelta.textContent = `${updatedHz >= originalHz ? '+' : ''}${formatFrequencyHz(Math.abs(updatedHz - originalHz))} Hz`;

    retuneComparisonState = {
      originalTargetId: previousActiveState.targetId,
      originalHz,
      updatedHz,
      selected: 'updated',
      persisted: false,
      revisionId: 'dev',
      targetId: retuneLockedTarget?.target_id ?? 'dev-simulated',
      previousActiveState,
    };
    renderRetuneComparisonState();
    retuneStage = 'compare';
    setRetuneStageUi(retuneStage);
    setRetuneProgress(100, 'Compare frequencies');
    retuneChart.setSelectedMarker('updated');
    dom.retuneCompareMessage.textContent = buildRetuneCompareMessageText('updated');
    dom.retuneStatusText.textContent = '';
    dom.retuneHoldBanner.hidden = true;
    appendLog(dom, {
      level: 'info',
      message: 'Retune sweep complete (dev simulated). Compare original and updated frequencies, then save your choice.',
    });
  } else {
    retuneLockedTarget = null;
    retuneStage = 'idle';
    retuneComparisonState = null;
    setRetuneStageUi(retuneStage);
    renderRetuneComparisonState();
    retuneChart.reset();
    renderRetuneChartLegend(null);
    setRetuneProgress(0, 'Failed');
    dom.retuneStatusText.textContent = 'Retune failed: Simulated failure (dev mode).';
    dom.retuneHoldBanner.hidden = true;
    appendLog(dom, {
      level: 'error',
      message: 'Retune failed (dev simulated).',
    });
  }

  updateUiState();
}

function renderRetuneChartFromResult(result: RetuneResult, originalHz: number): void {
  retuneChart.reset();
  retuneChart.setInitialFrequency(originalHz);
  renderRetuneChartLegend(originalHz);

  result.coarseSweep.points.forEach((point) => {
    retuneChart.addPoint('coarse', point);
  });

  result.fineSweep.points.forEach((point) => {
    retuneChart.addPoint('fine', point);
  });

  retuneChart.setFinalFrequency(result.finePeakHz);
  const frequencies = result.coarseSweep.points
    .concat(result.fineSweep.points)
    .map((point) => point.frequencyHz)
    .concat([originalHz, result.finePeakHz])
    .filter((hz) => Number.isFinite(hz));
  if (frequencies.length > 0) {
    const minHz = Math.min(...frequencies);
    const maxHz = Math.max(...frequencies);
    retuneChart.setSweepRange(minHz - 500, maxHz + 500);
  }
}

function renderDevRetuneChart(originalHz: number, updatedHz: number): void {
  retuneChart.reset();
  retuneChart.setInitialFrequency(originalHz);
  renderRetuneChartLegend(originalHz);

  const coarsePoints = generateSyntheticRetunePoints({
    centerHz: updatedHz,
    rangeHz: 1400,
    stepHz: 40,
    baselinePower: 7.2,
    peakPower: 12.6,
  });

  const finePoints = generateSyntheticRetunePoints({
    centerHz: updatedHz,
    rangeHz: 220,
    stepHz: 8,
    baselinePower: 10.8,
    peakPower: 13.5,
  });

  coarsePoints.forEach((point) => {
    retuneChart.addPoint('coarse', point);
  });

  finePoints.forEach((point) => {
    retuneChart.addPoint('fine', point);
  });

  retuneChart.setFinalFrequency(updatedHz);
  const frequencies = coarsePoints
    .concat(finePoints)
    .map((point) => point.frequencyHz)
    .concat([originalHz, updatedHz]);
  retuneChart.setSweepRange(Math.min(...frequencies) - 500, Math.max(...frequencies) + 500);
}

function generateSyntheticRetunePoints(input: {
  centerHz: number;
  rangeHz: number;
  stepHz: number;
  baselinePower: number;
  peakPower: number;
}): SweepPoint[] {
  const points: SweepPoint[] = [];
  const { centerHz, rangeHz, stepHz, baselinePower, peakPower } = input;

  for (let frequencyHz = centerHz - rangeHz; frequencyHz <= centerHz + rangeHz; frequencyHz += stepHz) {
    const normalizedDistance = Math.abs(frequencyHz - centerHz) / rangeHz;
    const peakWeight = Math.max(0, 1 - normalizedDistance * normalizedDistance);
    const ripple = Math.sin(frequencyHz / 67) * 0.08;
    const powerW = baselinePower + (peakPower - baselinePower) * peakWeight + ripple;
    points.push({
      frequencyHz,
      powerW: Number(powerW.toFixed(3)),
    });
  }

  return points;
}

async function triggerDevDiagnosticsOutcome(outcome: 'success' | 'failed'): Promise<void> {
  if (abortController && !abortController.signal.aborted) {
    abortController.abort();
  }

  activeOperation = 'none';
  abortController = null;
  diagnosticsStage = 'armed';
  diagnosticsAutoStartInFlight = false;
  diagnosticsRequireButtonReleaseForAutoStart = false;
  diagnosticsActiveTargetProgress = null;
  diagnosticsActiveTargetPercent = null;
  diagnosticReport = null;
  diagnosticUploadSnapshot = null;
  dom.diagnosticsSuccessMessage.textContent = '';
  dom.diagnosticsSuccessMessage.hidden = true;
  dom.diagnosticsHoldBanner.hidden = true;
  dom.diagnosticsHoldBanner.textContent = '';
  setDiagnosticsStageUi(diagnosticsStage);

  if (outcome === 'success') {
    try {
      diagnosticReport = await buildDevDiagnosticReport();
      diagnosticsStage = 'complete';
      setDiagnosticsProgress(100, 'Diagnostics complete');
      setDiagnosticsStageUi(diagnosticsStage);
      dom.diagnosticsStatusText.textContent = '';
      dom.diagnosticsSummary.textContent =
        `Completed ${diagnosticReport.summary.completedTargets}/${diagnosticReport.summary.totalTargets} target sweeps, ` +
        `${diagnosticReport.summary.totalPointCount} points`;
      dom.diagnosticsSummary.hidden = false;
      renderDiagnosticsCompleteMessage();
      dom.downloadDiagnosticsButton.disabled = false;
      appendLog(dom, {
        level: 'success',
        message:
          `Diagnostics complete (dev simulated). Targets: ${diagnosticReport.summary.completedTargets}/` +
          `${diagnosticReport.summary.totalTargets} completed, ${diagnosticReport.summary.failedTargets} failed, ` +
          `${diagnosticReport.summary.skippedTargets} skipped. Points: ${diagnosticReport.summary.totalPointCount}.`,
      });
      dom.downloadDiagnosticsButton.focus();
    } catch (error) {
      diagnosticsStage = 'armed';
      setDiagnosticsStageUi(diagnosticsStage);
      setDiagnosticsProgress(0, 'Waiting for button hold');
      dom.diagnosticsStatusText.textContent = `Diagnostics failed: ${getErrorMessage(error)}`;
      dom.diagnosticsSummary.textContent = 'No report generated.';
      dom.diagnosticsSummary.hidden = false;
      appendLog(dom, {
        level: 'error',
        message: `Diagnostics failed (dev simulated): ${getErrorMessage(error)}`,
      });
    }
  } else {
    diagnosticsStage = 'armed';
    setDiagnosticsStageUi(diagnosticsStage);
    setDiagnosticsProgress(0, 'Waiting for button hold');
    dom.diagnosticsStatusText.textContent = 'Diagnostics failed: Simulated failure (dev mode).';
    dom.diagnosticsSummary.textContent = 'No report generated.';
    dom.diagnosticsSummary.hidden = false;
    appendLog(dom, {
      level: 'error',
      message: 'Diagnostics failed (dev simulated).',
    });
  }

  updateUiState();
}

async function buildDevDiagnosticReport(): Promise<DiagnosticReport> {
  const now = new Date();
  const timestamp = formatDevTimestamp(now);
  const fileName = `seaus-diagnostic-dev-${timestamp}.json`;
  const startedAtMs = now.getTime();
  const endedAtMs = startedAtMs;
  const generatedAtIso = now.toISOString();

  const metadata = {
    generated_at_iso: generatedAtIso,
    generated_at_local: now.toLocaleString(),
    source: 'dev-mode-simulated',
  };

  const targetSweeps = {
    DEV_TARGET: {
      targetId: 'DEV_TARGET',
      description: 'Dev simulated target',
      priority: 0,
      centerFrequencyHz: 40000,
      searchBandwidthHz: 500,
      status: 'completed' as const,
      error: null,
      sweep: {
        spec: {
          startHz: 39500,
          endHz: 40500,
          stepHz: 24,
          dwellMs: 10,
        },
        completionReason: 'sweepEnd' as const,
        startedAtMs,
        endedAtMs,
        pointCount: 0,
      },
      points: [],
    },
  };

  const summary = {
    totalTargets: 1,
    completedTargets: 1,
    failedTargets: 0,
    skippedTargets: 0,
    totalPointCount: 0,
  };

  const payload = buildDiagnosticReportPayload({
    metadata,
    targetSweeps,
    summary,
    fileName,
    generatedAtIso,
  });
  const download = await buildDiagnosticDownloadJson({
    report: payload,
    obfuscationSecret: getDiagnosticsObfuscationSecret(),
    obfuscationKeyId: DIAGNOSTIC_OBFUSCATION_KEY_ID,
  });

  return {
    matchedRevisionId: 'dev',
    factoryInfo: {},
    metadata,
    targetSweeps,
    summary,
    json: download.json,
    fileName,
  };
}

function showReconnectPrompt(): void {
  if (reconnectPromptActive) {
    return;
  }
  reconnectPromptActive = true;
  dom.reconnectPanel.hidden = false;
  dom.reconnectButton.textContent = 'Reconnect';
}

function hideReconnectPrompt(): void {
  if (!reconnectPromptActive) {
    return;
  }
  reconnectPromptActive = false;
  dom.reconnectPanel.hidden = true;
}

function clearOtaReconnectTimer(): void {
  if (otaReconnectTimer) {
    window.clearTimeout(otaReconnectTimer);
    otaReconnectTimer = null;
  }
}

function buildOtaReconnectWaitDetail(remainingMs: number): string {
  const remainingSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
  return `${OTA_RECONNECT_PROGRESS_MESSAGE} (${remainingSeconds}s)`;
}

function clearOtaReconnectFallbackCountdownTimer(): void {
  if (otaReconnectFallbackCountdownTimer) {
    window.clearInterval(otaReconnectFallbackCountdownTimer);
    otaReconnectFallbackCountdownTimer = null;
  }
}

function renderOtaReconnectFallbackCountdown(): void {
  if (otaReconnectFallbackScheduledAtMs === null) {
    return;
  }
  const elapsedMs = Math.max(0, Date.now() - otaReconnectFallbackScheduledAtMs);
  const remainingMs = OTA_RECONNECT_DISCONNECT_FALLBACK_DELAY_MS - elapsedMs;
  dom.progressDetail.textContent = buildOtaReconnectWaitDetail(remainingMs);
}

function clearOtaReconnectFallbackTimer(): void {
  if (otaReconnectFallbackTimer) {
    window.clearTimeout(otaReconnectFallbackTimer);
    otaReconnectFallbackTimer = null;
    otaReconnectInfo('Cleared disconnect fallback timer.');
  }
  clearOtaReconnectFallbackCountdownTimer();
  otaReconnectFallbackScheduledAtMs = null;
}

function scheduleOtaReconnectDisconnectFallback(): void {
  const guardState = {
    fallbackTimerAlreadyActive: otaReconnectFallbackTimer !== null,
    reconnectPending: otaReconnectPending,
    reconnectTimerAlreadyActive: otaReconnectTimer !== null,
    canReconnect: transport.canReconnect(),
  };
  if (
    guardState.fallbackTimerAlreadyActive ||
    !guardState.reconnectPending ||
    guardState.reconnectTimerAlreadyActive ||
    !guardState.canReconnect
  ) {
    otaReconnectInfo('Skipped scheduling disconnect fallback timer.', {
      reason: 'guard_not_met',
      ...guardState,
    });
    return;
  }

  otaReconnectFallbackScheduledAtMs = Date.now();
  otaReconnectInfo('Scheduled disconnect fallback timer.', {
    delayMs: OTA_RECONNECT_DISCONNECT_FALLBACK_DELAY_MS,
  });
  renderOtaReconnectFallbackCountdown();
  clearOtaReconnectFallbackCountdownTimer();
  otaReconnectFallbackCountdownTimer = window.setInterval(() => {
    if (!otaReconnectPending || otaReconnectFallbackScheduledAtMs === null) {
      clearOtaReconnectFallbackCountdownTimer();
      return;
    }
    renderOtaReconnectFallbackCountdown();
  }, 1000);

  otaReconnectFallbackTimer = window.setTimeout(() => {
    otaReconnectFallbackTimer = null;
    clearOtaReconnectFallbackCountdownTimer();
    otaReconnectInfo('Disconnect fallback timer fired.');

    const timerFireGuardState = {
      reconnectPending: otaReconnectPending,
      reconnectTimerAlreadyActive: otaReconnectTimer !== null,
      canReconnect: transport.canReconnect(),
    };
    if (
      !timerFireGuardState.reconnectPending ||
      timerFireGuardState.reconnectTimerAlreadyActive ||
      !timerFireGuardState.canReconnect
    ) {
      otaReconnectInfo('Fallback timer fired but reconnect trigger conditions were not met.', {
        reason: 'state_changed',
        ...timerFireGuardState,
      });
      return;
    }

    if (!otaReconnectPending || otaReconnectTimer) {
      otaReconnectInfo('Fallback exiting because reconnect was already started elsewhere.', {
        reconnectPending: otaReconnectPending,
        reconnectTimerAlreadyActive: otaReconnectTimer !== null,
      });
      return;
    }

    otaReconnectInfo('Disconnect event not observed before timeout; switching to manual reconnect.', {
      timeoutMs: OTA_RECONNECT_DISCONNECT_FALLBACK_DELAY_MS,
      transportConnected: transport.isConnected(),
    });
    updateHadReconnectIssue = true;
    otaReconnectPending = false;
    transport.setKeepDeviceOnDisconnect(false);
    appendLog(dom, {
      level: 'warning',
      message: 'Did not detect a disconnect after update. Click Reconnect once the knife has rebooted.',
    });
    renderStatus(dom, {
      level: 'warning',
      message: 'Update complete, but reconnect did not start automatically. Reconnect manually.',
    });
    dom.progressSpeed.textContent = '0 kB/s';
    dom.progressDetail.textContent = 'Reconnect required.';
    showReconnectPrompt();
    updateUiState();
  }, OTA_RECONNECT_DISCONNECT_FALLBACK_DELAY_MS);
}

function cancelOtaReconnect(): void {
  otaReconnectInfo('Canceling OTA reconnect state.');
  clearOtaReconnectTimer();
  clearOtaReconnectFallbackTimer();
  otaReconnectPending = false;
  isAutoReconnectAttempt = false;
  otaReconnectAttemptStartedAtMs = null;
  transport.setKeepDeviceOnDisconnect(false);
}

function scheduleOtaReconnect(stageIndex = 0): void {
  if (otaReconnectTimer) {
    otaReconnectInfo('Skipped scheduling reconnect attempt because a timer is already active.', {
      stageIndex,
    });
    return;
  }

  updateUsedAutoReconnect = true;
  const stage = OTA_RECONNECT_STAGES[stageIndex];

  if (!stage) {
    updateHadReconnectIssue = true;
    otaReconnectError('No reconnect stage available (timed out).', 'reconnect_timed_out', {
      stageIndex,
      totalAttempts: OTA_RECONNECT_STAGES.length,
    });
    appendLog(dom, {
      level: 'error',
      message: 'Auto-reconnect timed out. Remove and re-insert the battery, then reconnect.',
    });
    renderStatus(dom, {
      level: 'error',
      message: 'Auto-reconnect failed. Remove and re-insert the battery, then reconnect.',
    });
    dom.progressSpeed.textContent = '0 kB/s';
    dom.progressDetail.textContent = 'Reconnect required.';
    showReconnectPrompt();
    finalizeUpdateOutcome();
    return;
  }

  const attemptNumber = stageIndex + 1;
  const totalAttempts = OTA_RECONNECT_STAGES.length;
  otaReconnectInfo('Scheduled reconnect attempt.', {
    stageIndex,
    attemptNumber,
    totalAttempts,
    delayMs: stage.delayMs,
    timeoutMs: stage.timeoutMs,
  });
  appendLog(dom, {
    level: 'info',
    message: `Reconnect attempt ${attemptNumber}/${totalAttempts} in ${Math.round(stage.delayMs / 1000)} second(s)...`,
  });

  otaReconnectTimer = window.setTimeout(async () => {
    otaReconnectTimer = null;
    isAutoReconnectAttempt = true;
    transport.setKeepDeviceOnDisconnect(true);
    otaReconnectAttemptStartedAtMs = Date.now();
    otaReconnectInfo('Reconnect attempt started.', {
      stageIndex,
      attemptNumber,
      totalAttempts,
      timeoutMs: stage.timeoutMs,
    });
    dom.connectButton.textContent = 'Reconnecting...';
    dom.reconnectButton.textContent = 'Reconnecting...';
    updateUiState();

    renderStatus(dom, { level: 'info', message: OTA_RECONNECT_PROGRESS_MESSAGE });
    dom.progressSpeed.textContent = '0 kB/s';
    dom.progressDetail.textContent = OTA_RECONNECT_PROGRESS_MESSAGE;

    let attemptFailedMessage: string | null = null;

    try {
      const info = await transport.connect({
        reuseDevice: true,
        timeoutMs: stage.timeoutMs,
        maxRetries: 1,
      });
      otaReconnectInfo('Reconnect transport.connect() succeeded.', {
        attemptNumber,
        totalAttempts,
        connectDurationMs: elapsedSince(otaReconnectAttemptStartedAtMs),
        deviceName: info.name,
        commandCapability: transport.getCapabilities().command,
      });
      await runPostConnectCommandSetup('ota-auto');
      appendLog(dom, { level: 'success', message: `Reconnected to ${info.name}.` });
      renderStatus(dom, { level: 'success', message: 'Reconnected after firmware update.' });
      dom.progressSpeed.textContent = '0 kB/s';
      dom.progressDetail.textContent = 'Reconnected after update.';
      hideReconnectPrompt();
      updateManifestEvaluation();
    } catch (error) {
      updateHadReconnectIssue = true;
      attemptFailedMessage = getErrorMessage(error);
      otaReconnectError('Reconnect attempt failed.', error, {
        attemptNumber,
        totalAttempts,
        connectDurationMs: elapsedSince(otaReconnectAttemptStartedAtMs),
      });
      trackError('update_reconnect_error', error, {
        firmware_version: updateFirmwareVersion,
        target_firmware_version: updateFirmwareVersion,
        before_firmware_version: updateBeforeFirmwareVersion,
        attempt_number: attemptNumber,
        total_attempts: totalAttempts,
      });
    } finally {
      isAutoReconnectAttempt = false;

      if (transport.isConnected()) {
        transport.setKeepDeviceOnDisconnect(false);
        otaReconnectInfo('Reconnect flow succeeded.', {
          attemptNumber,
          totalAttempts,
        });
        dom.connectButton.textContent = 'Disconnect';
        dom.reconnectButton.textContent = 'Reconnect';
        updateUiState();
        finalizeUpdateOutcome();
        return;
      }

      const hasMoreAttempts = stageIndex + 1 < OTA_RECONNECT_STAGES.length;
      if (hasMoreAttempts) {
        updateHadReconnectIssue = true;
        otaReconnectInfo('Reconnect attempt failed; scheduling retry.', {
          attemptNumber,
          totalAttempts,
          message: attemptFailedMessage,
          nextStageIndex: stageIndex + 1,
        });
        appendLog(dom, {
          level: 'info',
          message: `Reconnect attempt ${attemptNumber}/${totalAttempts} failed: ${attemptFailedMessage}. Retrying...`,
        });
        dom.connectButton.textContent = 'Connect';
        dom.reconnectButton.textContent = 'Reconnect';
        updateUiState();
        scheduleOtaReconnect(stageIndex + 1);
        return;
      }

      transport.setKeepDeviceOnDisconnect(false);
      updateHadReconnectIssue = true;
      otaReconnectError('Reconnect attempts exhausted; switching to manual prompt.', attemptFailedMessage, {
        attemptNumber,
        totalAttempts,
      });
      appendLog(dom, { level: 'error', message: `Auto-reconnect failed: ${attemptFailedMessage}` });
      renderStatus(dom, {
        level: 'error',
        message: 'Auto-reconnect failed. Remove and re-insert the battery, then reconnect.',
      });
      dom.progressSpeed.textContent = '0 kB/s';
      dom.progressDetail.textContent = 'Reconnect required.';
      showReconnectPrompt();
      dom.connectButton.textContent = 'Connect';
      dom.reconnectButton.textContent = 'Reconnect';
      updateUiState();
      finalizeUpdateOutcome();
    }
  }, stage.delayMs);
}

function setDevModes(modes: {
  firmware: DevMode;
  retune: DevMode;
  diagnostics: DevMode;
  diagnosticsObfuscationEnabled: boolean;
}): void {
  devFirmwareMode = modes.firmware;
  devRetuneMode = modes.retune;
  devDiagnosticsMode = modes.diagnostics;
  devDiagnosticsObfuscationEnabled = modes.diagnosticsObfuscationEnabled;
}

function syncDevModesFromInputs(): void {
  setDevModes({
    firmware: resolveDevMode(dom.devModeSuccess, dom.devModeFail),
    retune: resolveDevMode(dom.devRetuneModeSuccess, dom.devRetuneModeFail),
    diagnostics: resolveDevMode(dom.devDiagnosticsModeSuccess, dom.devDiagnosticsModeFail),
    diagnosticsObfuscationEnabled:
      dom.devDiagnosticsObfuscationOn.checked || !dom.devDiagnosticsObfuscationOff.checked,
  });
  applyDevIdleDisconnectTimeoutFromInputs();
}

function resolveDevIdleDisconnectTimeoutMs(): number {
  return dom.devIdleTimeout15s.checked
    ? IDLE_DISCONNECT_TIMEOUT_DEV_TEST_MS
    : IDLE_DISCONNECT_TIMEOUT_MS;
}

function applyDevIdleDisconnectTimeoutFromInputs(): void {
  const timeoutMs = resolveDevIdleDisconnectTimeoutMs();
  idleDisconnectController.setTimeoutMs(timeoutMs);
  if (DEV_DIAGNOSTIC_CONSOLE_LOGS) {
    console.debug('[IdleDisconnect] timeout updated via dev control', {
      timeoutMs,
      selected: dom.devIdleTimeout15s.checked ? '15s' : '5m',
    });
  }
}

function resolveDevMode(successInput: HTMLInputElement, failInput: HTMLInputElement): DevMode {
  if (successInput.checked) {
    return 'success';
  }
  if (failInput.checked) {
    return 'fail';
  }
  return 'live';
}

function formatDevTimestamp(date: Date): string {
  const yyyy = String(date.getFullYear());
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  const ss = String(date.getSeconds()).padStart(2, '0');
  return `${yyyy}${mm}${dd}-${hh}${min}${ss}`;
}

function getDiagnosticsObfuscationSecret(): string {
  if (DEV_CONTROLS_ENABLED && !devDiagnosticsObfuscationEnabled) {
    return '';
  }
  return DIAGNOSTIC_OBFUSCATION_SECRET;
}

function renderDiagnosticsCompleteMessage(): void {
  dom.diagnosticsSuccessMessage.innerHTML = DIAGNOSTICS_COMPLETE_MESSAGE_HTML;
  dom.diagnosticsSuccessMessage.hidden = false;
}

function extractNamePrefix(data: Manifest | null): string | undefined {
  if (!data) {
    return undefined;
  }

  const prefixes = new Set<string>();
  data.updates.forEach((update) => {
    if (update.compat?.device_name_prefix) {
      prefixes.add(update.compat.device_name_prefix);
    }
  });

  if (prefixes.size === 1) {
    return Array.from(prefixes)[0];
  }

  return undefined;
}

function parseUpdateDate(value: string): number {
  const timestamp = Date.parse(value);
  return Number.isNaN(timestamp) ? -Infinity : timestamp;
}

function formatFrequencyHz(value: number): string {
  return Math.round(value).toLocaleString('en-US');
}

function formatFrequencyKhz(value: number): string {
  return `${(value / 1000).toLocaleString('en-US', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} kHz`;
}

function mapRetuneErrorMessage(error: unknown, hardwareRevision: string | null): string {
  if (error instanceof RetuneUnsupportedHardwareError) {
    return buildUnsupportedRevisionRetuneMessage(error.revision);
  }

  if (error instanceof RetuneConfigError || error instanceof HardwareConfigValidationError) {
    return buildRetuneConfigUnavailableMessage();
  }

  if (
    error instanceof Error &&
    error.message.toLowerCase().includes('failed to load hardware config')
  ) {
    return buildRetuneConfigUnavailableMessage();
  }

  if (
    error instanceof Error &&
    error.message.toLowerCase().includes('hardware config schema is invalid')
  ) {
    return buildRetuneConfigUnavailableMessage();
  }

  if (
    error instanceof Error &&
    error.message.toLowerCase().includes('retune unsupported for hardware revision')
  ) {
    return buildUnsupportedRevisionRetuneMessage(hardwareRevision);
  }

  return `Retune failed: ${getErrorMessage(error)}`;
}

function mapDiagnosticsErrorMessage(error: unknown, hardwareRevision: string | null): string {
  if (error instanceof DiagnosticUnsupportedHardwareError) {
    return buildUnsupportedRevisionDiagnosticsMessage(error.revision);
  }

  if (error instanceof DiagnosticConfigError || error instanceof HardwareConfigValidationError) {
    return buildDiagnosticsConfigUnavailableMessage();
  }

  if (
    error instanceof Error &&
    error.message.toLowerCase().includes('failed to load hardware config')
  ) {
    return buildDiagnosticsConfigUnavailableMessage();
  }

  if (
    error instanceof Error &&
    error.message.toLowerCase().includes('hardware config schema is invalid')
  ) {
    return buildDiagnosticsConfigUnavailableMessage();
  }

  if (
    error instanceof Error &&
    error.message.toLowerCase().includes('diagnostics unsupported for hardware revision')
  ) {
    return buildUnsupportedRevisionDiagnosticsMessage(hardwareRevision);
  }

  return `Diagnostics failed: ${getErrorMessage(error)}`;
}

function buildUnsupportedRevisionRetuneMessage(revision: string | null | undefined): string {
  const normalized = revision?.trim();
  const displayRevision = normalized && normalized.length > 0 ? normalized : 'unknown';
  return `Retune unavailable for hardware revision \"${displayRevision}\". Please contact Seattle Ultrasonics support at ${SUPPORT_EMAIL}.`;
}

function buildRetuneConfigUnavailableMessage(): string {
  return `Retune configuration is unavailable. Please contact Seattle Ultrasonics support at ${SUPPORT_EMAIL}.`;
}

function buildUnsupportedRevisionDiagnosticsMessage(revision: string | null | undefined): string {
  const normalized = revision?.trim();
  const displayRevision = normalized && normalized.length > 0 ? normalized : 'unknown';
  return `Diagnostics unavailable for hardware revision \"${displayRevision}\". Please contact Seattle Ultrasonics support at ${SUPPORT_EMAIL}.`;
}

function buildDiagnosticsConfigUnavailableMessage(): string {
  return `Diagnostics configuration is unavailable. Please contact Seattle Ultrasonics support at ${SUPPORT_EMAIL}.`;
}

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Unexpected error.';
}

function sweepDebug(task: 'Retune' | 'Diagnostics', message: string, details?: Record<string, unknown>): void {
  if (shouldPersistFlowDebugMessage(message)) {
    appendSupportLog(task.toLowerCase(), message, details, 'debug');
  }
  if (!DEV_DIAGNOSTIC_CONSOLE_LOGS) {
    return;
  }

  if (details) {
    console.debug(`[${task}] ${message}`, details);
  } else {
    console.debug(`[${task}] ${message}`);
  }
}

function diagnosticsDebug(message: string, details?: Record<string, unknown>): void {
  if (shouldPersistFlowDebugMessage(message)) {
    appendSupportLog('diagnostics', message, details, 'debug');
  }
  if (!DEV_DIAGNOSTIC_CONSOLE_LOGS) {
    return;
  }

  if (details) {
    console.debug(`[Diagnostics] ${message}`, details);
  } else {
    console.debug(`[Diagnostics] ${message}`);
  }
}

function diagnosticsError(
  message: string,
  error: unknown,
  details?: Record<string, unknown>
): void {
  appendSupportLog(
    'diagnostics',
    message,
    {
      ...(details ?? {}),
      error,
    },
    'error'
  );
  if (!DEV_DIAGNOSTIC_CONSOLE_LOGS) {
    return;
  }

  if (details) {
    console.error(`[Diagnostics] ${message}`, { error, ...details });
  } else {
    console.error(`[Diagnostics] ${message}`, error);
  }
}

function shouldPersistFlowDebugMessage(message: string): boolean {
  return message !== 'Sweep line received' && message !== 'Sweep point received';
}

function delay(timeoutMs: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, timeoutMs);
  });
}

async function setDriveMode(
  mode: 'hv' | 'autotune',
  options: { commandDelayMs?: number } = {}
): Promise<void> {
  const commandDelayMs = Math.max(0, options.commandDelayMs ?? 0);
  await transport.sendTextCommand('hven on');
  if (commandDelayMs > 0) {
    await delay(commandDelayMs);
  }
  if (mode === 'hv') {
    await transport.sendTextCommand('SetButtonEnabled false');
  } else {
    await transport.sendTextCommand('SetButtonEnabled true');
  }
}

async function enterSweepControlMode(task: 'retune' | 'diagnostics'): Promise<void> {
  try {
    await setDriveMode('hv');
    appendLog(dom, {
      level: 'info',
      message: `${task === 'retune' ? 'Retune' : 'Diagnostics'} mode enabled: HV on, autotune off.`,
    });
  } catch (error) {
    throw new Error(`Failed to enter sweep control mode: ${getErrorMessage(error)}`);
  }
}

async function restoreAutotuneButtonMode(task: 'retune' | 'diagnostics'): Promise<void> {
  try {
    await setDriveMode('autotune');
    appendLog(dom, {
      level: 'info',
      message: `${task === 'retune' ? 'Retune' : 'Diagnostics'} finished: restored autotune button mode.`,
    });
  } catch (error) {
    appendLog(dom, {
      level: 'error',
      message: `Failed to restore autotune mode after ${task}: ${getErrorMessage(error)}`,
    });
  }
}

async function runPostConnectCommandSetup(context: PostConnectSetupContext = 'normal'): Promise<void> {
  const shouldLogReconnectDebug = context !== 'normal';
  const setupStartedAtMs = Date.now();
  const commandCapability = transport.getCapabilities().command;

  if (shouldLogReconnectDebug) {
    otaReconnectInfo('Post-connect command setup started.', {
      context,
      commandCapability,
    });
  }

  if (!commandCapability) {
    if (shouldLogReconnectDebug) {
      otaReconnectInfo('Post-connect command setup skipped: command capability unavailable.', {
        context,
      });
    }
    return;
  }

  const unlockStartedAtMs = Date.now();
  try {
    await transport.unlockCommandChannel(COMMAND_UNLOCK_PASSPHRASE);
    if (shouldLogReconnectDebug) {
      otaReconnectInfo('Command channel unlock succeeded.', {
        context,
        stepDurationMs: elapsedSince(unlockStartedAtMs),
      });
    }
  } catch (error) {
    if (shouldLogReconnectDebug) {
      otaReconnectError('Command channel unlock failed.', error, {
        context,
        stepDurationMs: elapsedSince(unlockStartedAtMs),
      });
    }
    appendLog(dom, {
      level: 'error',
      message: `Command channel unlock failed: ${getErrorMessage(error)}`,
    });
    return;
  }
  await delay(POST_CONNECT_COMMAND_DELAY_MS);

  const driveModeStartedAtMs = Date.now();
  try {
    await setDriveMode('autotune', { commandDelayMs: POST_CONNECT_COMMAND_DELAY_MS });
    if (shouldLogReconnectDebug) {
      otaReconnectInfo('Set drive mode to autotune.', {
        context,
        stepDurationMs: elapsedSince(driveModeStartedAtMs),
      });
    }
    appendLog(dom, { level: 'info', message: 'Knife set to normal autotune button mode.' });
  } catch (error) {
    if (shouldLogReconnectDebug) {
      otaReconnectError('Failed to set drive mode to autotune.', error, {
        context,
        stepDurationMs: elapsedSince(driveModeStartedAtMs),
      });
    }
    appendLog(dom, {
      level: 'error',
      message: `Failed to set autotune button mode: ${getErrorMessage(error)}`,
    });
  }
  await delay(POST_CONNECT_COMMAND_DELAY_MS);

  const refreshStatusStartedAtMs = Date.now();
  try {
    await transport.refreshStatusFromCommand(2500);
    if (shouldLogReconnectDebug) {
      otaReconnectInfo('Status refresh command completed.', {
        context,
        stepDurationMs: elapsedSince(refreshStatusStartedAtMs),
      });
    }
  } catch (error) {
    if (shouldLogReconnectDebug) {
      otaReconnectError('Status refresh command did not return live metrics.', error, {
        context,
        stepDurationMs: elapsedSince(refreshStatusStartedAtMs),
      });
    }
    appendLog(dom, {
      level: 'info',
      message: 'Status command did not return live metrics.',
    });
  }
  await delay(POST_CONNECT_COMMAND_DELAY_MS);

  const factoryInfoStartedAtMs = Date.now();
  try {
    const factoryResponse = await transport.sendTextCommandWithResponse('GetFactoryInfo', 2500);
    const parsed = parseFactoryInfoLines(factoryResponse.split('\n'));
    transport.applyFactoryInfo(parsed);
    if (shouldLogReconnectDebug) {
      otaReconnectInfo('Factory info command completed and was applied.', {
        context,
        stepDurationMs: elapsedSince(factoryInfoStartedAtMs),
        parsedFieldCount: Object.keys(parsed).length,
      });
    }
  } catch (error) {
    if (shouldLogReconnectDebug) {
      otaReconnectError('Factory info command did not return a full response.', error, {
        context,
        stepDurationMs: elapsedSince(factoryInfoStartedAtMs),
      });
    }
    appendLog(dom, {
      level: 'info',
      message: 'Factory info command did not return a full response.',
    });
  }

  if (shouldLogReconnectDebug) {
    otaReconnectInfo('Post-connect command setup finished.', {
      context,
      totalDurationMs: elapsedSince(setupStartedAtMs),
    });
  }
}

function startUpdater(): void {
  if (updaterStarted) {
    return;
  }

  updaterStarted = true;
  void init();
}

function showBluefyGate(): void {
  dom.bluefyGate.hidden = false;
  dom.eulaGate.hidden = true;
  dom.updaterShell.hidden = true;
}

function bootstrap(): void {
  const compatibilityProbe = getCompatibilityProbe();
  if (compatibilityProbe.isSecureContext && requiresBluefyOnIos(compatibilityProbe)) {
    showBluefyGate();
    return;
  }

  const hasAcceptedEula = hasAcceptedCurrentEula();
  let acceptedInSession = false;

  setupEulaGate(
    {
      gate: dom.eulaGate,
      appShell: dom.updaterShell,
      content: dom.eulaContent,
      acceptCheckbox: dom.eulaAcceptCheckbox,
      acceptButton: dom.eulaAcceptButton,
      declineButton: dom.eulaDeclineButton,
    },
    {
      eulaHtml: eulaContentHtml,
      hasAccepted: () => hasAcceptedEula,
      onAccept: () => {
        acceptedInSession = true;
        trackEvent('eula_action', {
          action: 'accept',
          accepted_previously: false,
        });
        persistEulaAcceptance();
      },
      onAccepted: () => {
        if (hasAcceptedEula && !acceptedInSession) {
          trackEvent('eula_action', {
            action: 'auto_accepted',
            accepted_previously: true,
          });
        }
        startUpdater();
      },
      onDecline: () => {
        trackEvent('eula_action', {
          action: 'decline',
          accepted_previously: hasAcceptedEula,
        });
        window.location.assign(EULA_DECLINE_URL);
      },
    }
  );
}

window.addEventListener(
  'beforeunload',
  () => {
    stopIdleDisconnectDebugLogging();
    idleDisconnectController.destroy();
    retuneChart.destroy();
  },
  { once: true }
);

bootstrap();
